import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import "@/App.css";
import AgentPortal from "@/AgentPortal";
import TransactionDocuments from "@/TransactionDocuments";
import {
  AboutPage, ContactPage, TestimonialsPage, TestimonialsSection,
  WhyUsPage, FaqPage, MortgagePage, CityIndexPage, CityDetailPage,
  BlogIndexPage, BlogPostPage, PrivacyPage, TermsPage, FairHousingPage,
  IabsPage, ConsumerProtectionPage, ForAgentsPage, SiteFooter,
  SmsTermsPage, UnsubscribePage, SmsConsentCheckbox, SMS_CONSENT_TEXT,
} from "@/Pages";
import { CITIES } from "@/content/cities";
import { BLOG_POSTS } from "@/content/blog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// Dev-only error logger. Replaces raw `console.error` calls so production
// builds don't leak internal error details to the browser console.
// Swap to a real telemetry sink (Sentry, LogRocket) here when wired up.
const devError = (...args) => {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.error(...args);
  }
};

// URL routing. pathnames map to internal views. Landing pages are full URLs
// so Google Ads can target them and Google can index them individually.
const ROUTES = {
  "/": "home",
  "/home": "home",
  "/listings": "listings",
  "/mls-search": "mls-search",
  "/assistant": "assistant",
  "/dashboard": "dashboard",
  "/sell-fast-dfw": "lp-sell",
  "/find-home-dfw": "lp-buy",
  "/value-my-home": "contact",  // Home Value tool disabled until real comps wired
  "/signin": "signin",
  "/about": "about",
  "/contact": "contact",
  "/reviews": "testimonials",
  "/why-us": "why-us",
  "/faq": "faq",
  "/mortgage-calculator": "mortgage",
  "/cities": "cities",
  "/blog": "blog",
  "/privacy": "privacy",
  "/terms": "terms",
  "/sms-terms": "sms-terms",
  "/unsubscribe": "unsubscribe",
  "/fair-housing": "fair-housing",
  "/iabs": "iabs",
  "/consumer-protection": "consumer-protection",
  "/for-agents": "for-agents",
  "/agent-portal": "agent-portal",
  ...Object.fromEntries(CITIES.map((c) => [`/${c.slug}-homes-for-sale`, `city-${c.slug}`])),
  ...Object.fromEntries(BLOG_POSTS.map((p) => [`/blog/${p.slug}`, `post-${p.slug}`])),
};
const VIEW_TO_PATH = {
  home: "/",
  listings: "/listings",
  "mls-search": "/mls-search",
  assistant: "/assistant",
  dashboard: "/dashboard",
  "lp-sell": "/sell-fast-dfw",
  "lp-buy": "/find-home-dfw",
  "lp-value": "/value-my-home",
  signin: "/signin",
  about: "/about",
  contact: "/contact",
  testimonials: "/reviews",
  "why-us": "/why-us",
  faq: "/faq",
  mortgage: "/mortgage-calculator",
  cities: "/cities",
  blog: "/blog",
  privacy: "/privacy",
  terms: "/terms",
  "sms-terms": "/sms-terms",
  unsubscribe: "/unsubscribe",
  "fair-housing": "/fair-housing",
  iabs: "/iabs",
  "consumer-protection": "/consumer-protection",
  "for-agents": "/for-agents",
  "agent-portal": "/agent-portal",
  ...Object.fromEntries(CITIES.map((c) => [`city-${c.slug}`, `/${c.slug}-homes-for-sale`])),
  ...Object.fromEntries(BLOG_POSTS.map((p) => [`post-${p.slug}`, `/blog/${p.slug}`])),
};

// Per-view SEO. Keep titles ≤60 chars, descriptions ≤155.
const SEO = {
  home: {
    title: "Irving Real Estate Group | DFW Real Estate · Buy, Sell, Rent-to-Buy",
    description: "Buy, sell, or rent-to-buy in DFW with Jason, your real estate concierge. Live NTREIS listings, instant home values, contract-to-close tracking.",
  },
  listings: {
    title: "DFW Homes for Sale | Live NTREIS Listings | Irving Real Estate Group",
    description: "Browse live Fort Worth, Arlington, Dallas, and Irving homes for sale. Save favorites and ask Jason about any property.",
  },
  "mls-search": {
    title: "Full MLS Search | Live NTREIS Listings | Irving Real Estate Group",
    description: "Search the complete DFW MLS with live NTREIS data. Filter by city, price, beds, baths, and more.",
  },
  assistant: {
    title: "Ask Jason | Real Estate Concierge | Irving Real Estate Group",
    description: "Talk to Jason, the real estate concierge for DFW. Free, instant answers on buying, selling, lending, and your transaction.",
  },
  "lp-sell": { title: "Sell My House Fast in DFW | Free Home Value | Irving Real Estate", description: "Get a free home-value report and a no-pressure sell strategy. Most DFW homes close in 30-45 days." },
  "lp-buy": { title: "Find Your Home in DFW | Live MLS Listings | Irving Real Estate Group", description: "Search live NTREIS DFW listings, save favorites, and have Jason recommend homes that match your budget and timeline." },
  "lp-value": { title: "What's My Home Worth in DFW? | Free Instant Estimate", description: "Get a free, fast home-value estimate from Irving Real Estate Group plus a one-call pricing consultation." },
  signin: { title: "Sign In | Irving Real Estate Group", description: "Sign in to access your saved favorites and active transaction." },
  about: { title: "About Torey Irving | Fort Worth Realtor Since 2008", description: "Meet Torey Irving. Texas Realtor since 2008, Fort Worth resident, and founder of Irving Real Estate Group. 15+ years in mortgage and real estate." },
  contact: { title: "Contact Irving Real Estate Group | Fort Worth Realtor", description: "Call 817-308-0922 or visit our Fort Worth office at 600 W 6th Street, Suite 400. Torey responds within 1 business hour." },
  testimonials: { title: "Client Reviews | Irving Real Estate Group", description: "Five-star reviews from real DFW buyers and sellers. no paid placements." },
  "why-us": { title: "Why Choose Irving Real Estate Group | The Irving Experience", description: "Live MLS data, a real estate concierge, transaction tracking, and Fort Worth expertise. Six reasons to work with Irving Real Estate Group." },
  faq: { title: "Real Estate FAQ | DFW Buyer & Seller Questions Answered", description: "Honest answers to the real estate questions DFW buyers and sellers actually ask." },
  mortgage: { title: "DFW Mortgage Calculator | Irving Real Estate Group", description: "Estimate your monthly mortgage payment in DFW. Adjust price, down payment, rate, term, taxes, and insurance." },
  cities: { title: "DFW Neighborhoods | Irving Real Estate Group", description: "Explore Fort Worth, Arlington, Dallas, Irving, Frisco, Plano, Southlake, Mansfield, and Grand Prairie. written by a Fort Worth local." },
  blog: { title: "DFW Market Insights | Irving Real Estate Group Blog", description: "Honest, useful guides for DFW buyers and sellers. written by Torey Irving." },
  privacy: { title: "Privacy Policy | Irving Real Estate Group" },
  terms: { title: "Terms of Service | Irving Real Estate Group" },
  "fair-housing": { title: "Fair Housing & Equal Opportunity | Irving Real Estate Group" },
  iabs: { title: "TREC Information About Brokerage Services | Irving Real Estate Group" },
  "consumer-protection": { title: "TREC Consumer Protection Notice | Irving Real Estate Group" },
};

function applySeo(view) {
  const seo = SEO[view] || SEO.home;
  if (typeof document === "undefined") return;
  document.title = seo.title;
  const setMeta = (name, content, attr = "name") => {
    let el = document.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, name);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  };
  setMeta("description", seo.description);
  setMeta("og:title", seo.title, "property");
  setMeta("og:description", seo.description, "property");
  setMeta("twitter:title", seo.title);
  setMeta("twitter:description", seo.description);
}

// UTM capture. read once on first load, persist to localStorage, attach to
// every lead create call. Reflected in dashboard `source` field.
function captureUtm() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const utm = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid"].forEach((k) => {
    const v = params.get(k);
    if (v) utm[k] = v;
  });
  if (Object.keys(utm).length === 0) return JSON.parse(localStorage.getItem("ireg-utm") || "null");
  utm.landingPath = window.location.pathname;
  utm.firstSeenAt = new Date().toISOString();
  localStorage.setItem("ireg-utm", JSON.stringify(utm));
  return utm;
}

function utmSourceLabel(utm, fallbackType = "Buyer") {
  if (!utm || !utm.utm_source) return `${fallbackType} Flow`;
  const src = (utm.utm_source || "").toLowerCase();
  const med = (utm.utm_medium || "").toLowerCase();
  if (utm.gclid || src === "google") {
    if (med === "lsa" || (utm.utm_campaign || "").toLowerCase().includes("lsa")) return "Google LSA";
    if (med === "cpc" || med === "ppc") return "Google Ads · " + (utm.utm_campaign || "search");
    return "Google · " + (utm.utm_campaign || "organic");
  }
  if (utm.fbclid || src === "facebook" || src === "instagram") return "Meta Ads · " + (utm.utm_campaign || src);
  return `${src}${utm.utm_campaign ? " · " + utm.utm_campaign : ""}`;
}

const BRAND = {
  name: "Irving Real Estate Group",
  tagline: "The Irving Experience. It Will Move You!",
  assistant: "Jason",
  logo: "/logo-new.png",
};

// External MLS search (Placester-hosted IDX). Opens in a new tab because
// Placester blocks iframe embedding via Cloudflare bot verification.
const PLACESTER_MLS_URL = "https://toreyirving.myrealestateplatform.com/search-results/?paged=1&purchase_types=Buy&sort_field=beds&sort_direction=desc&search_num_results=12";

// Static landing-page form schemas. Hoisted to module scope so they keep stable
// identity across re-renders (prevents unnecessary LandingHeroForm re-renders).
const LP_VALUE_FIELDS = [
  { name: "address", placeholder: "Property address", required: true },
  { name: "name", placeholder: "Your name", required: true },
  { name: "phone", placeholder: "Phone", required: true, inputType: "tel" },
  { name: "email", placeholder: "Email", required: true, inputType: "email" },
  { name: "timeline", type: "select", placeholder: "When are you looking to sell?", options: ["0-90 days", "3-6 months", "6+ months", "Just curious"] },
];

const LP_BUY_FIELDS = [
  { name: "city", type: "select", placeholder: "Preferred city", options: ["Fort Worth", "Arlington", "Dallas", "Irving", "Frisco", "Plano", "Southlake", "Mansfield", "Grand Prairie", "Open to several"], required: true },
  { name: "budget", type: "select", placeholder: "Price range", options: ["Under $300K", "$300K-$400K", "$400K-$500K", "$500K-$700K", "$700K+"], required: true },
  { name: "timeline", type: "select", placeholder: "Buying timeline", options: ["0-90 days", "3-6 months", "6+ months", "Just exploring"], required: true },
  { name: "preapproval", type: "select", placeholder: "Preapproval status", options: ["Preapproved", "Talking to a lender", "Not started", "Not sure"] },
  { name: "name", placeholder: "Your name", required: true },
  { name: "phone", placeholder: "Phone", required: true, inputType: "tel" },
  { name: "email", placeholder: "Email", required: true, inputType: "email" },
];

const LP_HOME_VALUE_FIELDS = [
  { name: "address", placeholder: "Property address", required: true },
  { name: "condition", type: "select", placeholder: "Home condition", options: ["Excellent", "Good", "Needs minor updates", "Needs major updates", "Not sure"] },
  { name: "name", placeholder: "Your name", required: true },
  { name: "phone", placeholder: "Phone", required: true, inputType: "tel" },
  { name: "email", placeholder: "Email", required: true, inputType: "email" },
];

const blankForms = {
  Buyer: { name: "", phone: "", email: "", location: "", budget: "", timeline: "", preapprovalStatus: "", currentHousing: "", mustHaves: "", concern: "", consultationInterest: "", smsConsent: false },
  Seller: { name: "", phone: "", email: "", propertyAddress: "", timeline: "", homeCondition: "", reason: "", desiredPrice: "", sellingBuyingNext: "", consultationInterest: "", smsConsent: false },
  Renter: { name: "", phone: "", email: "", monthlyRent: "", leaseEndDate: "", location: "", creditConfidence: "", savingsRange: "", timeline: "", concern: "", smsConsent: false },
};

function timelineBucket(value) {
  const text = String(value || "").toLowerCase();
  if (text.includes("0-90") || text.includes("30") || text.includes("60") || text.includes("90") || text.includes("now") || text.includes("asap")) return "0-90 days";
  if (text.includes("3-6") || text.includes("3 to 6") || text.includes("six") || text.includes("6 months")) return "3-6 months";
  if (text.includes("6+") || text.includes("later") || text.includes("next year") || text.includes("12")) return "6+ months";
  return value || "Not provided";
}

function hasContact(form) {
  return form.name && form.phone && form.email;
}

function scoreLead(type, form) {
  if (!hasContact(form)) return "Incomplete Lead";
  const timeline = timelineBucket(form.timeline);
  const preapproval = String(form.preapprovalStatus || "").toLowerCase();
  const lease = String(form.leaseEndDate || "").toLowerCase();
  if (type === "Buyer" && preapproval.includes("preapproved") && timeline === "0-90 days") return "Hot Lead";
  if (type === "Seller" && timeline === "0-90 days") return "Hot Lead";
  if (type === "Renter" && (timeline === "0-90 days" || timeline === "3-6 months") && lease) return "Warm Lead";
  if (timeline === "3-6 months") return "Warm Lead";
  if (timeline === "6+ months") return "Nurture Lead";
  return "Education Lead";
}

function recommendedAction(type, score) {
  if (score === "Incomplete Lead") return "Get missing contact information before routing to follow-up.";
  if (type === "Buyer" && score === "Hot Lead") return "Schedule buyer consultation and confirm preapproval details.";
  if (type === "Seller" && score === "Hot Lead") return "Schedule home value consultation and prepare seller strategy call.";
  if (type === "Renter" && score === "Warm Lead") return "Invite to a buyer readiness call and explain next steps from renting to owning.";
  if (score === "Warm Lead") return "Send helpful next-step message and schedule a consultation if they are open.";
  if (score === "Nurture Lead") return "Add to nurture sequence with monthly education and light check-ins.";
  return "Send educational resources and ask one simple follow-up question.";
}

function followUpScript(lead) {
  const firstName = (lead.name || "there").split(" ")[0];
  if (lead.type === "Buyer") return `Hey ${firstName}, this is Torey with Irving Real Estate Group. Based on what you shared, the best next step is a quick buyer readiness conversation so we can see what makes the most sense for your timeline. Are you free today or tomorrow?`;
  if (lead.type === "Seller") return `Hey ${firstName}, this is Torey with Irving Real Estate Group. I saw you're thinking about selling, and the smartest first move is getting a clear look at value, timing, and strategy before you make any decisions. Are you open to a quick home value conversation this week?`;
  return `Hey ${firstName}, this is Torey with Irving Real Estate Group. Based on what you shared, you may be closer to buying than you think. The best move is a quick renter-to-buyer readiness call so we can look at timing, rent, savings, and next steps. Are you free today or tomorrow?`;
}

function money(n) {
  if (n == null) return "-";
  return `$${Number(n).toLocaleString()}`;
}

// "Last seen" pill: compute from createdAt or lastSeenAt ISO string.
function lastSeenLabel(iso) {
  if (!iso) return null;
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return null;
  const diffMin = Math.floor((Date.now() - ts) / 60000);
  if (diffMin < 0) return { text: "just now", tone: "live" };
  if (diffMin < 2) return { text: "live now", tone: "live" };
  if (diffMin < 60) return { text: `${diffMin}m ago`, tone: "live" };
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return { text: `${diffH}h ago`, tone: "hot" };
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return { text: `${diffD}d ago`, tone: "warm" };
  if (diffD < 30) return { text: `${diffD}d ago`, tone: "cool" };
  if (diffD < 365) return { text: `${Math.floor(diffD / 30)}mo ago`, tone: "cold" };
  return { text: `${Math.floor(diffD / 365)}y ago`, tone: "cold" };
}

const LAST_SEEN_TONE = {
  live: "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300",
  hot: "bg-amber-100 text-amber-800 ring-1 ring-amber-300",
  warm: "bg-sky-100 text-sky-800 ring-1 ring-sky-200",
  cool: "bg-slate-100 text-slate-700",
  cold: "bg-slate-50 text-slate-500",
};

export default function App() {
  const initialView = (typeof window !== "undefined" && ROUTES[window.location.pathname]) || "home";
  const [view, setViewRaw] = useState(initialView);
  const [utm] = useState(() => captureUtm());

  // Admin auth state. only allowlisted Google account can access dashboard.
  // null = checking, object = authenticated, false = not authenticated.
  const [admin, setAdmin] = useState(null);
  // Agent (team member) auth state — separate cookie (agent_session JWT).
  const [agent, setAgent] = useState(null);
  const [authError, setAuthError] = useState("");
  const [authProcessing, setAuthProcessing] = useState(false);
  // Mobile hamburger menu open state.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Wrap setView so URL stays in sync with state for shareable / Google-indexable pages.
  const setView = useCallback((v) => {
    setViewRaw(v);
    const path = VIEW_TO_PATH[v];
    if (path && typeof window !== "undefined" && window.location.pathname !== path) {
      window.history.pushState({}, "", path);
    }
  }, []);

  // Back/forward button support.
  useEffect(() => {
    function onPop() {
      const next = ROUTES[window.location.pathname] || "home";
      setViewRaw(next);
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Apply SEO meta tags on every view change.
  useEffect(() => { applySeo(view); }, [view]);

  // ----- Admin auth bootstrap -----
  // Handles two paths:
  //   1) If we just returned from Emergent Google OAuth, the URL has #session_id=...
  //      → exchange it for a httpOnly cookie via POST /api/auth/session
  //   2) Otherwise, check existing cookie via GET /api/auth/me
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      if (typeof window === "undefined") return;
      const hash = window.location.hash || "";
      if (hash.includes("session_id=")) {
        setAuthProcessing(true);
        const sessionId = hash.split("session_id=")[1].split("&")[0];
        // Clean the URL immediately so a refresh doesn't re-process.
        window.history.replaceState({}, "", window.location.pathname);
        try {
          const res = await axios.post(`${API}/auth/session`, { session_id: sessionId });
          if (cancelled) return;
          setAdmin(res.data);
          setAuthError("");
        } catch (err) {
          if (cancelled) return;
          const msg = err?.response?.data?.detail || "Sign-in failed. Please try again.";
          setAuthError(msg);
          setAdmin(false);
        } finally {
          if (!cancelled) setAuthProcessing(false);
        }
        return;
      }
      try {
        const res = await axios.get(`${API}/auth/me`);
        if (!cancelled) setAdmin(res.data);
      } catch (err) {
        if (!cancelled) setAdmin(false);
      }
    }
    bootstrap();
    return () => { cancelled = true; };
  }, []);

  const signInWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const signOutAdmin = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      devError("Admin logout request failed", err);
    }
    setAdmin(false);
    setView("home");
  }, [setView]);

  // Detect agent session (independent of admin session).
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/agent/me`)
      .then((res) => { if (!cancelled) setAgent(res.data); })
      .catch(() => { if (!cancelled) setAgent(false); });
    return () => { cancelled = true; };
  }, []);

  const signOutAgent = useCallback(async () => {
    try { await axios.post(`${API}/agent/logout`); }
    catch (err) { devError("Agent logout failed", err); }
    setAgent(false);
    setView("home");
  }, [setView]);

  const [leadType, setLeadType] = useState("Buyer");
  const [form, setForm] = useState(blankForms.Buyer);
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [filter, setFilter] = useState("All");

  const [chatSessionId, setChatSessionId] = useState(() => localStorage.getItem("ireg-chat-session") || "");
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { role: "assistant", text: `Hey, I'm ${BRAND.assistant} with Irving Real Estate Group. Are you looking to buy, sell, rent-to-buy, invest, or just explore your options?` },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState("");

  // Buyer identity (lightweight)
  const [buyer, setBuyer] = useState(() => {
    const raw = localStorage.getItem("ireg-buyer");
    return raw ? JSON.parse(raw) : null;
  });
  const [pendingListingsView, setPendingListingsView] = useState(false);
  const [pendingListingKey, setPendingListingKey] = useState(null);

  // Listings state
  const [listings, setListings] = useState([]);
  const [listingFilters, setListingFilters] = useState({ q: "", city: "", min_price: "", max_price: "", min_beds: "", min_baths: "" });
  const [cities, setCities] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [topListings, setTopListings] = useState([]);

  // Transactions
  const [transactions, setTransactions] = useState([]); // agent-side all
  const [myTransactions, setMyTransactions] = useState([]); // buyer-side
  const [activeTransaction, setActiveTransaction] = useState(null);
  const [atRiskTxns, setAtRiskTxns] = useState([]);
  const [stagesMeta, setStagesMeta] = useState([]);

  const showToast = useCallback((message) => {
    setToast(message);
    setTimeout(() => setToast(""), 3500);
  }, []);

  const fetchLeads = useCallback(async () => {
    if (!admin) return;
    try {
      const res = await axios.get(`${API}/leads`);
      setLeads(res.data || []);
    } catch (err) {
      devError("Failed to fetch leads", err);
    }
  }, [admin]);

  const fetchListings = useCallback(async () => {
    try {
      const params = {};
      Object.entries(listingFilters).forEach(([k, v]) => { if (v !== "" && v != null) params[k] = v; });
      const res = await axios.get(`${API}/listings`, { params });
      setListings(res.data?.results || []);
    } catch (err) {
      devError("Listings fetch failed", err);
    }
  }, [listingFilters]);

  const fetchFavorites = useCallback(async () => {
    if (!buyer) return;
    try {
      const res = await axios.get(`${API}/buyers/${buyer.id}/favorites`);
      setFavorites((res.data?.favorites || []).map((f) => f.listingKey));
    } catch (err) {
      devError("Favorites fetch failed", err);
    }
  }, [buyer]);

  const fetchCities = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/listings/cities`);
      setCities(res.data?.cities || []);
    } catch (err) {
      devError("Cities fetch failed", err);
    }
  }, []);

  const fetchTopListings = useCallback(async () => {
    if (!admin) return;
    try {
      const res = await axios.get(`${API}/analytics/top-listings?limit=5`);
      setTopListings(res.data || []);
    } catch (err) {
      devError("Top listings fetch failed", err);
    }
  }, [admin]);

  const fetchTransactions = useCallback(async () => {
    if (!admin) return;
    try {
      const [all, atRisk, meta] = await Promise.all([
        axios.get(`${API}/transactions`),
        axios.get(`${API}/transactions/at-risk`),
        axios.get(`${API}/transactions/meta/stages`),
      ]);
      setTransactions(all.data || []);
      setAtRiskTxns(atRisk.data || []);
      setStagesMeta(meta.data?.stages || []);
    } catch (err) { devError("Transactions fetch failed", err); }
  }, [admin]);

  const fetchMyTransactions = useCallback(async () => {
    if (!buyer) { setMyTransactions([]); return; }
    try {
      const res = await axios.get(`${API}/transactions?buyerId=${buyer.id}`);
      setMyTransactions(res.data || []);
    } catch (err) { devError("My transactions fetch failed", err); }
  }, [buyer]);

  const refreshActiveTransaction = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API}/transactions/${id}`);
      setActiveTransaction(res.data);
    } catch (err) { devError(err); }
  }, []);

  useEffect(() => { if (admin) fetchLeads(); fetchCities(); }, [admin, fetchLeads, fetchCities]);
  useEffect(() => { if (view === "listings") fetchListings(); }, [view, fetchListings]);
  useEffect(() => { fetchFavorites(); }, [fetchFavorites]);
  useEffect(() => { if (view === "dashboard") { fetchTopListings(); fetchTransactions(); } }, [view, fetchTopListings, fetchTransactions]);
  useEffect(() => { fetchMyTransactions(); }, [fetchMyTransactions]);

  useEffect(() => { if (chatSessionId) localStorage.setItem("ireg-chat-session", chatSessionId); }, [chatSessionId]);
  useEffect(() => { if (buyer) localStorage.setItem("ireg-buyer", JSON.stringify(buyer)); }, [buyer]);

  function startFlow(type) { setLeadType(type); setForm(blankForms[type]); setView("flow"); }
  function updateField(field, value) { setForm((prev) => ({ ...prev, [field]: value })); }

  async function submitLead() {
    setSubmitting(true);
    try {
      const payload = { type: leadType, ...form };
      payload.smsConsentText = form.smsConsent ? SMS_CONSENT_TEXT : "";
      payload.smsConsentSource = "intake_flow";
      if (utm) {
        payload.source = utmSourceLabel(utm, leadType);
        payload.utm = utm;
      }
      const res = await axios.post(`${API}/leads`, payload);
      setSelectedLead(res.data);
      await fetchLeads();
      setView("result");
    } catch (err) {
      devError("Lead create failed", err);
      showToast("Could not save lead. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function updateLeadNotes(id, notes) {
    setSelectedLead((lead) => (lead && lead.id === id ? { ...lead, notes } : lead));
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, notes } : l)));
    try { await axios.patch(`${API}/leads/${id}/notes`, { notes }); } catch (err) { devError(err); }
  }

  async function deleteLead(id) {
    try {
      await axios.delete(`${API}/leads/${id}`);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      setSelectedLead(null);
    } catch (err) {
      devError(err);
      window.alert("Could not delete lead: " + (err?.response?.data?.detail || err.message));
    }
  }

  function exportJSON() {
    const envelope = {
      brand: {
        name: BRAND.name,
        tagline: BRAND.tagline,
        logoUrl: `${window.location.origin}${BRAND.logo}`,
      },
      generatedAt: new Date().toISOString(),
      totalLeads: leads.length,
      leads,
    };
    const blob = new Blob([JSON.stringify(envelope, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "irving-real-estate-leads.json"; a.click();
    URL.revokeObjectURL(a.href);
  }

  function exportCSV() {
    const headers = ["type", "name", "phone", "email", "timeline", "score", "source", "pipelineStage", "recommendedNextAction", "createdAt"];
    const rows = leads.map((lead) => headers.map((h) => `"${String(lead[h] || "").replaceAll('"', '""')}"`).join(","));
    const banner = [
      `"${BRAND.name}"`,
      `"${BRAND.tagline}"`,
      `"Generated ${new Date().toLocaleString()}. ${leads.length} lead${leads.length === 1 ? "" : "s"}"`,
      "",
    ];
    const csv = [...banner, headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "irving-real-estate-leads.csv"; a.click();
    URL.revokeObjectURL(a.href);
  }

  async function sendChat() {
    if (!chatInput.trim() || chatBusy) return;
    const userText = chatInput.trim();
    setChatMessages((prev) => [...prev, { role: "user", text: userText }]);
    setChatInput("");
    setChatBusy(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: chatSessionId || null,
        message: userText,
        buyer_id: buyer?.id || null,
        transaction_id: activeTransaction?.id || null,
      });
      if (res.data?.session_id) setChatSessionId(res.data.session_id);
      setChatMessages((prev) => [...prev, { role: "assistant", text: res.data.reply }]);
    } catch (err) {
      devError(err);
      setChatMessages((prev) => [...prev, { role: "assistant", text: "I'm having trouble connecting right now. Please try again in a moment." }]);
    } finally {
      setChatBusy(false);
    }
  }

  // ---- Transactions ----
  async function createLead(payload) {
    // Admin-side manual lead creation. Uses the public /api/leads endpoint
    // (rate-limited + honeypot-protected). We pass hp="" as an admin.
    try {
      const res = await axios.post(`${API}/leads`, { ...payload, hp: "" });
      await fetchLeads();
      showToast("Lead created.");
      return res.data;
    } catch (err) {
      devError(err);
      showToast("Could not create lead.");
      throw err;
    }
  }

  async function createTransaction(lead, data) {
    try {
      const res = await axios.post(`${API}/transactions`, {
        leadId: lead.id,
        buyerId: lead.buyerId || "",
        propertyAddress: data.propertyAddress,
        listingKey: data.listingKey || "",
        listPrice: Number(data.listPrice) || 0,
        contractDate: data.contractDate,
        targetCloseDate: data.targetCloseDate || "",
        optionPeriodEndDate: data.optionPeriodEndDate || "",
        earnestMoney: Number(data.earnestMoney) || 0,
        lenderName: data.lenderName || "",
        titleCompany: data.titleCompany || "",
        hoaName: data.hoaName || "",
        buyerAgentName: data.buyerAgentName || "",
        buyerAgentEmail: data.buyerAgentEmail || "",
        notes: data.notes || "",
      });
      showToast(lead.type === "Seller"
        ? "Transaction created. Concierge email queued to " + (lead.email || "seller") + "."
        : "Transaction created.");
      await fetchLeads();
      await fetchTransactions();
      setSelectedLead(null);
      setActiveTransaction(res.data);
      setView("agent-transaction");
    } catch (err) { devError(err); showToast("Could not create transaction."); }
  }

  async function advanceTransaction(id) {
    try {
      const res = await axios.post(`${API}/transactions/${id}/advance`);
      setActiveTransaction(res.data);
      await fetchTransactions();
      await fetchMyTransactions();
      const isSeller = String(res.data.clientType || "").toLowerCase() === "seller";
      showToast(isSeller
        ? `Advanced to: ${res.data.stageName}. Concierge email queued.`
        : `Advanced to: ${res.data.stageName}`);
    } catch (err) { devError(err); showToast("Advance failed."); }
  }

  async function toggleCheck(id, itemKey, done) {
    try {
      const res = await axios.post(`${API}/transactions/${id}/check`, { itemKey, done });
      setActiveTransaction(res.data);
    } catch (err) { devError(err); }
  }

  async function openTransaction(id, role) {
    try {
      const res = await axios.get(`${API}/transactions/${id}`);
      setActiveTransaction(res.data);
      setView(role === "agent" ? "agent-transaction" : "my-transaction");
    } catch (err) { devError(err); showToast("Could not load transaction."); }
  }

  // ---- Buyer auth (magic link / SMS code) ----
  async function startAuth(emailOrPhone, channel) {
    try {
      const res = await axios.post(`${API}/auth/start`, { emailOrPhone, channel });
      return res.data;
    } catch (err) {
      showToast(err.response?.data?.detail || "Could not start sign-in");
      return null;
    }
  }

  async function verifyAuth(emailOrPhone, codeOrToken) {
    try {
      const res = await axios.post(`${API}/auth/verify`, { emailOrPhone, codeOrToken });
      setBuyer(res.data);
      await fetchLeads();
      showToast(`Welcome back, ${res.data.name?.split(" ")[0] || "Buyer"}!`);
      return true;
    } catch (err) {
      showToast(err.response?.data?.detail || "Invalid code or link");
      return false;
    }
  }

  async function bookConsultation(lead, { preferredDate, preferredTime, notes }) {
    try {
      const res = await axios.post(`${API}/consultations`, { leadId: lead.id, leadName: lead.name, leadEmail: lead.email, leadPhone: lead.phone, type: lead.type, preferredDate, preferredTime, notes });
      showToast(res.data?.message || "Consultation booked. Confirmation sent.");
    } catch (err) { devError(err); showToast("Booking failed. Please try again."); }
  }

  async function sendEmailFollowUp(lead, subject, body) {
    try {
      const res = await axios.post(`${API}/followups/email`, { leadId: lead.id, to: lead.email, subject, body });
      showToast(res.data?.mocked ? "Email queued (SendGrid not configured)." : "Email sent.");
    } catch (err) { devError(err); showToast("Email failed."); }
  }

  async function sendSmsFollowUp(lead, body) {
    try {
      const res = await axios.post(`${API}/followups/sms`, { leadId: lead.id, to: lead.phone, body });
      showToast(res.data?.mocked ? "SMS queued (Twilio not configured)." : "SMS sent.");
    } catch (err) {
      devError(err);
      showToast(err?.response?.data?.detail || "SMS failed.");
    }
  }

  // ----- buyer identification -----
  async function identifyBuyer(name, email) {
    try {
      const res = await axios.post(`${API}/buyers/identify`, { name, email });
      setBuyer(res.data);
      await fetchLeads();
      if (pendingListingKey) {
        const key = pendingListingKey;
        setPendingListingKey(null);
        await openListing(key, res.data.id);
        setView("listing-detail");
      } else if (pendingListingsView) {
        setPendingListingsView(false);
        setView("listings");
      }
    } catch (err) {
      devError(err);
      showToast("Could not identify. Try again.");
    }
  }

  function openListings() {
    if (!buyer) { setPendingListingsView(true); setView("identify"); return; }
    setView("listings");
  }

  async function openListing(listingKey, buyerOverrideId = null) {
    const bid = buyerOverrideId || buyer?.id;
    if (!bid) { setPendingListingKey(listingKey); setView("identify"); return; }
    try {
      const res = await axios.get(`${API}/listings/${listingKey}`);
      setSelectedListing(res.data);
      setView("listing-detail");
      // track view
      axios.post(`${API}/listings/${listingKey}/view`, { buyerId: bid }).catch((e) => devError(e));
    } catch (err) {
      devError(err);
      showToast("Could not open listing.");
    }
  }

  async function toggleFavorite(listingKey) {
    if (!buyer) { setPendingListingKey(listingKey); setView("identify"); return; }
    try {
      const res = await axios.post(`${API}/listings/${listingKey}/favorite`, { buyerId: buyer.id });
      setFavorites((prev) => res.data.favorited ? [...new Set([...prev, listingKey])] : prev.filter((k) => k !== listingKey));
      showToast(res.data.favorited ? "Added to favorites." : "Removed from favorites.");
    } catch (err) { devError(err); }
  }

  function askJasonAbout(listing) {
    const msg = `Tell me more about ${listing.UnparsedAddress}. is it a good fit?`;
    setChatInput(msg);
    setView("assistant");
  }

  function signOutBuyer() {
    setBuyer(null);
    setFavorites([]);
    localStorage.removeItem("ireg-buyer");
    showToast("Signed out.");
  }

  const filteredLeads = useMemo(() => {
    if (filter === "All") return leads;
    if (["Buyer", "Seller", "Renter"].includes(filter)) return leads.filter((lead) => lead.type === filter);
    return leads.filter((lead) => lead.score === filter);
  }, [filter, leads]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] font-body text-[#E0D2A8]" data-testid="app-root">
      <header className="sticky top-0 z-20 border-b border-[#d9c7a3] bg-[#1E1E1E]/95 text-white backdrop-blur">
        <div className="relative mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 md:px-8">
          <button onClick={() => { setView("home"); setMobileNavOpen(false); }} className="flex items-center text-left" data-testid="brand-home-btn">
            <img src={BRAND.logo} alt={BRAND.name} className="h-10 w-auto object-contain md:h-12" />
          </button>
          {/* Mobile hamburger. visible below `lg`, hidden on desktop. */}
          <button
            data-testid="mobile-nav-toggle"
            aria-label="Toggle navigation menu"
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#C4A56B]/40 text-[#C4A56B] lg:hidden"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              {mobileNavOpen ? (<><line x1="4" y1="4" x2="16" y2="16" /><line x1="4" y1="16" x2="16" y2="4" /></>) : (<><line x1="3" y1="6" x2="17" y2="6" /><line x1="3" y1="10" x2="17" y2="10" /><line x1="3" y1="14" x2="17" y2="14" /></>)}
            </svg>
          </button>
          <nav
            data-testid="site-nav"
            className={`${mobileNavOpen ? "flex" : "hidden"} absolute left-0 right-0 top-full z-30 flex-col gap-2 border-b border-[#d9c7a3] bg-[#1E1E1E] px-4 pb-4 pt-2 text-sm shadow-xl lg:static lg:flex lg:flex-row lg:flex-wrap lg:items-center lg:gap-2 lg:border-none lg:bg-transparent lg:p-0 lg:shadow-none`}
          >
            <NavButton testId="nav-home" active={view === "home"} onClick={() => { setView("home"); setMobileNavOpen(false); }}>Home</NavButton>
            <NavButton testId="nav-about" active={view === "about"} onClick={() => { setView("about"); setMobileNavOpen(false); }}>About</NavButton>
            <NavDropdown
              label="Buy"
              testId="nav-buy"
              items={[
                { label: "Start Buyer Intake", testId: "nav-buy-intake", onClick: () => { startFlow("Buyer"); setMobileNavOpen(false); } },
                { label: "Search Homes", testId: "nav-buy-search", href: PLACESTER_MLS_URL, external: true },
                { label: "Mortgage Calculator", testId: "nav-buy-mortgage", onClick: () => { setView("mortgage"); setMobileNavOpen(false); } },
              ]}
            />
            <NavButton testId="nav-sell" onClick={() => { startFlow("Seller"); setMobileNavOpen(false); }}>Sell</NavButton>
            <NavButton testId="nav-assistant" active={view === "assistant"} onClick={() => { setActiveTransaction(null); setView("assistant"); setMobileNavOpen(false); }}>Ask {BRAND.assistant}</NavButton>
            <NavButton testId="nav-for-agents" active={view === "for-agents"} onClick={() => { setView("for-agents"); setMobileNavOpen(false); }}>For Agents</NavButton>
            <NavButton testId="nav-contact" active={view === "contact"} onClick={() => { setView("contact"); setMobileNavOpen(false); }}>Contact</NavButton>
            {buyer && myTransactions.length > 0 && (
              <NavButton testId="nav-my-transaction" active={view === "my-transaction"} onClick={() => { openTransaction(myTransactions[0].id, "buyer"); setMobileNavOpen(false); }}>My Transaction</NavButton>
            )}
            {admin && (
              <NavButton testId="nav-dashboard" active={view === "dashboard" || view === "agent-transaction"} onClick={() => { setView("dashboard"); setMobileNavOpen(false); }}>Dashboard</NavButton>
            )}
            {admin && (
              <button data-testid="admin-signout-btn" onClick={() => { signOutAdmin(); setMobileNavOpen(false); }} className="rounded-full bg-[#C4A56B]/20 px-3 py-1 text-xs text-[#C4A56B] hover:bg-[#C4A56B]/30">
                {admin.name?.split(" ")[0] || "Agent"} · Sign out
              </button>
            )}
            {agent && !admin && (
              <button
                data-testid="agent-portal-nav-btn"
                onClick={() => { setView("agent-portal"); setMobileNavOpen(false); }}
                className="rounded-full bg-[#C4A56B] px-3 py-1 text-xs font-bold text-[#1E1E1E] hover:bg-[#d4b876]"
              >
                Hello, {agent.name?.split(" ")[0] || "Agent"}
              </button>
            )}
            {agent && !admin && (
              <button
                data-testid="agent-signout-nav-btn"
                onClick={() => { signOutAgent(); setMobileNavOpen(false); }}
                className="rounded-full bg-white/10 px-3 py-1 text-xs text-white hover:bg-white/20"
              >
                Sign out
              </button>
            )}
            {!buyer && !admin && !agent && (
              <NavButton testId="nav-signin" active={view === "signin"} onClick={() => { setView("signin"); setMobileNavOpen(false); }}>Sign in</NavButton>
            )}
            {buyer && (
              <button data-testid="buyer-signout-btn" onClick={() => { signOutBuyer(); setMobileNavOpen(false); }} className="rounded-full bg-white/10 px-3 py-1 text-xs hover:bg-white/20">
                {buyer.name?.split(" ")[0] || "Signed in"} · Sign out
              </button>
            )}
          </nav>
        </div>
      </header>

      {view === "home" && <Home startFlow={startFlow} setView={setView} openListings={openListings} />}
      {view === "lp-sell" && <LandingSell startFlow={startFlow} setView={setView} />}
      {view === "lp-buy" && <LandingBuy startFlow={startFlow} setView={setView} openListings={openListings} />}
      {view === "lp-value" && <ContactPage navigate={setView} /> /* Home Value tool disabled — redirect to Contact */}
      {view === "about" && <AboutPage setView={setView} startFlow={startFlow} />}
      {view === "contact" && <ContactPage navigate={setView} />}
      {view === "testimonials" && <TestimonialsPage />}
      {view === "why-us" && <WhyUsPage setView={setView} />}
      {view === "faq" && <FaqPage />}
      {view === "mortgage" && <MortgagePage />}
      {view === "cities" && <CityIndexPage setView={setView} navigateCity={(slug) => setView(`city-${slug}`)} />}
      {view.startsWith("city-") && <CityDetailPage slug={view.slice(5)} openListings={openListings} startFlow={startFlow} />}
      {view === "blog" && <BlogIndexPage navigatePost={(slug) => setView(`post-${slug}`)} />}
      {view.startsWith("post-") && <BlogPostPage slug={view.slice(5)} navigateBlog={() => setView("blog")} startFlow={startFlow} />}
      {view === "privacy" && <PrivacyPage />}
      {view === "terms" && <TermsPage />}
      {view === "sms-terms" && <SmsTermsPage />}
      {view === "unsubscribe" && <UnsubscribePage />}
      {view === "fair-housing" && <FairHousingPage />}
      {view === "iabs" && <IabsPage />}
      {view === "consumer-protection" && <ConsumerProtectionPage />}
      {view === "for-agents" && <ForAgentsPage navigate={setView} />}
      {view === "agent-portal" && <AgentPortal />}
      {view === "assistant" && <Assistant chatMessages={chatMessages} chatInput={chatInput} setChatInput={setChatInput} sendChat={sendChat} startFlow={startFlow} chatBusy={chatBusy} buyer={buyer} activeTransaction={activeTransaction} />}
      {view === "flow" && <LeadFlow type={leadType} form={form} updateField={updateField} submitLead={submitLead} submitting={submitting} />}
      {view === "result" && selectedLead && <LeadResult lead={selectedLead} setView={setView} />}
      {view === "identify" && <IdentifyGate identifyBuyer={identifyBuyer} />}
      {view === "signin" && <SignInScreen signInWithGoogle={signInWithGoogle} admin={admin} setView={setView} />}
      {view === "my-transaction" && activeTransaction && (
        <MyTransaction
          txn={activeTransaction}
          toggleCheck={(key, done) => toggleCheck(activeTransaction.id, key, done)}
          askJason={() => setView("assistant")}
          refresh={() => refreshActiveTransaction(activeTransaction.id)}
        />
      )}
      {view === "agent-transaction" && admin && activeTransaction && (
        <AgentTransaction
          txn={activeTransaction}
          advance={() => advanceTransaction(activeTransaction.id)}
          toggleCheck={(key, done) => toggleCheck(activeTransaction.id, key, done)}
          onDelete={async () => {
            const addr = activeTransaction.propertyAddress || "this transaction";
            if (!window.confirm(`Delete ${addr}? This cannot be undone. The concierge email log will also be wiped.`)) return;
            try {
              await axios.delete(`${API}/transactions/${activeTransaction.id}`);
              await fetchTransactions();
              await fetchMyTransactions();
              await fetchLeads();
              setActiveTransaction(null);
              setView("dashboard");
              showToast(`Deleted ${addr}.`);
            } catch (err) { devError(err); showToast("Delete failed."); }
          }}
          back={() => setView("dashboard")}
        />
      )}
      {view === "agent-transaction" && !admin && (
        <AdminGate admin={admin} signInWithGoogle={signInWithGoogle} authError={authError} authProcessing={authProcessing} />
      )}
      {view === "listings" && (
        <Listings
          listings={listings}
          filters={listingFilters}
          setFilters={setListingFilters}
          cities={cities}
          favorites={favorites}
          openListing={openListing}
          toggleFavorite={toggleFavorite}
          buyer={buyer}
        />
      )}
      {view === "mls-search" && <MlsSearch />}
      {view === "listing-detail" && selectedListing && (
        <ListingDetail
          listing={selectedListing}
          isFav={favorites.includes(selectedListing.ListingKey)}
          toggleFavorite={toggleFavorite}
          askJasonAbout={askJasonAbout}
          back={() => setView("listings")}
        />
      )}
      {view === "dashboard" && admin && (
        <Dashboard
          leads={filteredLeads}
          allLeads={leads}
          filter={filter}
          setFilter={setFilter}
          setSelectedLead={setSelectedLead}
          exportJSON={exportJSON}
          exportCSV={exportCSV}
          topListings={topListings}
          transactions={transactions}
          atRiskTxns={atRiskTxns}
          stagesMeta={stagesMeta}
          openTransaction={(id) => openTransaction(id, "agent")}
          createTransaction={createTransaction}
          createLead={createLead}
        />
      )}
      {view === "dashboard" && !admin && (
        <AdminGate admin={admin} signInWithGoogle={signInWithGoogle} authError={authError} authProcessing={authProcessing} />
      )}
      {selectedLead && view === "dashboard" && (
        <LeadModal lead={selectedLead} onClose={() => setSelectedLead(null)} updateLeadNotes={updateLeadNotes} bookConsultation={bookConsultation} sendEmailFollowUp={sendEmailFollowUp} sendSmsFollowUp={sendSmsFollowUp} createTransaction={createTransaction} openTransaction={(id) => openTransaction(id, "agent")} deleteLead={deleteLead} />
      )}
      {admin && <HotLeadAlerts />}
      {toast && <div data-testid="toast" className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-full bg-[#1E1E1E] px-6 py-3 text-sm font-semibold text-white shadow-2xl">{toast}</div>}
      {!["dashboard", "agent-transaction", "my-transaction", "flow", "result", "signin", "identify", "lp-sell", "lp-buy", "lp-value"].includes(view) && <SiteFooter navigate={setView} />}
    </div>
  );
}

function Home({ startFlow, setView, openListings }) {
  // Sunset-lit DFW-style neighborhood. sets the "real estate site" tone.
  const heroImage = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2400&q=80";
  return (
    <main data-testid="home-view">
      <section className="relative overflow-hidden text-white">
        {/* Full-bleed hero photo with layered dark/gold gradient overlay. */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${heroImage}")` }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(120deg, rgba(0,0,0,0.94) 0%, rgba(10,10,10,0.85) 45%, rgba(30,30,30,0.65) 100%)",
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-4 py-20 md:px-8 lg:py-28">
          <div className="max-w-3xl">
            <div className="mb-8 inline-block">
              <img
                src="/logo-new.png"
                alt="Irving Real Estate Group"
                className="h-24 w-auto object-contain drop-shadow-lg md:h-32"
              />
            </div>
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#C4A56B]/50 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#C4A56B] backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-[#C4A56B]" />
              Fort Worth · Dallas · The Metroplex
            </div>
            <h1 className="font-serif text-4xl uppercase leading-tight tracking-wide md:text-6xl">
              <span className="text-[#E0D2A8]">The hands-on way to </span>
              <span className="text-[#C4A56B]">find home</span>
              <span className="text-[#E0D2A8]"> in DFW.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-[#E0D2A8]/80">
              Live listings, a real estate concierge that answers day or night, and Torey Irving handling
              every offer, inspection, and closing personally. No team hand-offs, no ghosting.
            </p>
            <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <HeroButton testId="hero-buy" onClick={() => startFlow("Buyer")}>I Want to Buy</HeroButton>
              <HeroButton testId="hero-sell" onClick={() => startFlow("Seller")}>I Want to Sell</HeroButton>
              <HeroButton testId="hero-assistant" onClick={() => setView("assistant")}>Ask Jason</HeroButton>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-300">
              <span>⭐ 5.0 · Local reviews</span>
              <span className="opacity-40">|</span>
              <span>Licensed Texas broker</span>
              <span className="opacity-40">|</span>
              <span>MLS-connected · NTREIS</span>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 md:px-8">
        <div className="mb-10 text-center">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C4A56B]">The Irving Difference</div>
          <h2 className="mt-2 font-serif text-3xl uppercase tracking-wide text-[#E0D2A8] md:text-4xl">Why buyers &amp; sellers choose us</h2>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          <Feature title="Live DFW Listings" text="Search live homes across the Metroplex. save favorites, compare properties, and get personal recommendations." />
          <Feature title="Concierge Service" text="Jason answers questions any time of day. Torey personally handles every offer, inspection, and closing." />
          <Feature title="Nothing Slips" text="Every browse, favorite, and conversation flows back to Torey. you'll never feel forgotten." />
        </div>
      </section>

      <section className="bg-[#141414] py-12">
        <div className="mx-auto max-w-6xl px-4 md:px-8">
          <div className="mb-6 text-center">
            <div className="text-sm font-semibold uppercase tracking-[0.18em] text-[#C4A56B]">What clients say</div>
            <h2 className="mt-2 font-serif text-3xl uppercase tracking-wide text-[#E0D2A8]">Real reviews from real DFW buyers.</h2>
          </div>
          <TestimonialsSection compact max={3} />
        </div>
      </section>

      <section className="bg-[#1E1E1E] py-16 text-white">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 md:px-8 lg:grid-cols-[0.8fr_1.2fr]">
          <img src="/torey.jpg" alt="Torey Irving" className="h-80 w-full rounded-[2rem] object-cover object-top ring-1 ring-[#C4A56B]/30 md:h-full" />
          <div>
            <div className="text-sm font-semibold uppercase tracking-[0.18em] text-[#C4A56B]">Meet your agent</div>
            <h2 className="mt-2 text-4xl font-bold">Torey Irving · Texas Realtor since 2008</h2>
            <p className="mt-4 text-lg leading-8 text-slate-200">Nearly 15 years in mortgage finance, a deep DFW network, and a relentless work ethic. Torey lives in Fort Worth with his four children and knows every corner of the metroplex.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button data-testid="home-meet-torey-btn" onClick={() => setView("about")} className="rounded-2xl bg-[#C4A56B] px-5 py-3 font-bold text-[#1E1E1E]">Read Torey's story</button>
              <button data-testid="home-contact-btn" onClick={() => setView("contact")} className="rounded-2xl border border-[#C4A56B] px-5 py-3 font-bold text-[#C4A56B]">Get in touch</button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function IdentifyGate({ identifyBuyer }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <main className="mx-auto max-w-xl px-4 py-12 md:px-8" data-testid="identify-view">
      <div className="rounded-[2rem] bg-white p-8 shadow-sm">
        <div className="text-sm font-semibold text-[#1E1E1E]">One quick step</div>
        <h1 className="mt-1 text-3xl font-bold">Let Jason show you the right homes.</h1>
        <p className="mt-2 text-slate-600">Drop your name and email so we can save your favorites and tailor recommendations. No spam. Torey reaches out only if you're ready.</p>
        <div className="mt-6 grid gap-3">
          <input data-testid="identify-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-[#C4A56B]" />
          <input data-testid="identify-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" type="email" className="rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-[#C4A56B]" />
          <button
            data-testid="identify-submit-btn"
            disabled={busy || !name.trim() || !email.includes("@")}
            onClick={async () => { setBusy(true); await identifyBuyer(name.trim(), email.trim()); setBusy(false); }}
            className="rounded-2xl bg-[#1E1E1E] px-6 py-3 font-bold text-white disabled:opacity-60"
          >
            {busy ? "Saving…" : "Continue to Listings"}
          </button>
        </div>
      </div>
    </main>
  );
}

function MlsSearch() {
  useEffect(() => {
    // Placester blocks iframe embedding; open in a new tab and route back home.
    window.open(PLACESTER_MLS_URL, "_blank", "noopener,noreferrer");
    const t = setTimeout(() => { window.location.href = "/listings"; }, 300);
    return () => clearTimeout(t);
  }, []);
  return (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center" data-testid="mls-search-view">
      <div className="text-xs font-semibold uppercase tracking-wider text-[#1E1E1E]">Full MLS</div>
      <h1 className="mt-2 text-2xl font-bold">Opening MLS search in a new tab…</h1>
      <p className="mt-3 text-sm text-slate-600">
        If it didn't open,{" "}
        <a href={PLACESTER_MLS_URL} target="_blank" rel="noopener noreferrer" className="font-bold text-[#7a5a00] underline">click here to open the MLS search</a>.
      </p>
    </main>
  );
}

function Listings({ listings, filters, setFilters, cities, favorites, openListing, toggleFavorite, buyer }) {
  function set(field, value) { setFilters((prev) => ({ ...prev, [field]: value })); }
  return (
    <main className="mx-auto max-w-7xl px-4 py-8 md:px-8" data-testid="listings-view">
      <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-sm font-semibold text-[#1E1E1E]">DFW Listings · powered by NTREIS</div>
          <h1 className="text-3xl font-bold">Browse and favorite homes.</h1>
          {buyer && <p className="mt-1 text-sm text-slate-600">Welcome back, {buyer.name.split(" ")[0]}. {favorites.length} favorite{favorites.length === 1 ? "" : "s"} saved.</p>}
        </div>
        <a
          href={PLACESTER_MLS_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="listings-full-mls-cta"
          className="inline-flex items-center gap-2 self-start rounded-2xl bg-[#C4A56B] px-5 py-3 text-sm font-bold text-[#1E1E1E] shadow-sm hover:bg-[#A68B51]"
        >
          🏘️ Search Homes ↗
        </a>
      </div>

      <div className="mb-6 rounded-3xl bg-white p-4 shadow-sm md:p-6">
        <div className="grid gap-3 md:grid-cols-6">
          <input data-testid="listings-q" value={filters.q} onChange={(e) => set("q", e.target.value)} placeholder="Search address, ZIP, area..." className="md:col-span-2 rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-[#C4A56B]" />
          <select data-testid="listings-city" value={filters.city} onChange={(e) => set("city", e.target.value)} className="rounded-2xl border border-slate-300 bg-white px-4 py-3 outline-none focus:border-[#C4A56B]">
            <option value="">Any city</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input data-testid="listings-min-price" value={filters.min_price} onChange={(e) => set("min_price", e.target.value)} placeholder="Min price" type="number" className="rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-[#C4A56B]" />
          <input data-testid="listings-max-price" value={filters.max_price} onChange={(e) => set("max_price", e.target.value)} placeholder="Max price" type="number" className="rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-[#C4A56B]" />
          <div className="flex gap-2">
            <input data-testid="listings-min-beds" value={filters.min_beds} onChange={(e) => set("min_beds", e.target.value)} placeholder="Beds" type="number" className="w-full rounded-2xl border border-slate-300 px-3 py-3 outline-none focus:border-[#C4A56B]" />
            <input data-testid="listings-min-baths" value={filters.min_baths} onChange={(e) => set("min_baths", e.target.value)} placeholder="Baths" type="number" className="w-full rounded-2xl border border-slate-300 px-3 py-3 outline-none focus:border-[#C4A56B]" />
          </div>
        </div>
      </div>

      {listings.length === 0 && <div className="rounded-3xl bg-white p-10 text-center text-slate-500" data-testid="listings-empty">No listings match those filters yet.</div>}

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {listings.map((l) => {
          const isFav = favorites.includes(l.ListingKey);
          return (
            <div key={l.ListingKey} data-testid={`listing-card-${l.ListingKey}`} className="overflow-hidden rounded-[1.5rem] bg-white shadow-sm transition hover:shadow-lg">
              <button onClick={() => openListing(l.ListingKey)} className="block w-full text-left">
                <img src={l.Media?.[0]?.MediaURL} alt={l.UnparsedAddress} className="h-52 w-full object-cover" loading="lazy" />
              </button>
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <button onClick={() => openListing(l.ListingKey)} className="text-left">
                    <div className="text-xl font-bold">{money(l.ListPrice)}</div>
                    <div className="text-sm text-slate-600">{l.UnparsedAddress}</div>
                  </button>
                  <button
                    data-testid={`favorite-btn-${l.ListingKey}`}
                    onClick={() => toggleFavorite(l.ListingKey)}
                    className={`rounded-full px-3 py-1 text-xs font-bold ${isFav ? "bg-[#C4A56B] text-[#1E1E1E]" : "bg-slate-100 text-slate-700"}`}
                  >
                    {isFav ? "★ Saved" : "☆ Save"}
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-700">
                  <Pill>{l.BedroomsTotal} bd</Pill>
                  <Pill>{l.BathroomsTotalInteger} ba</Pill>
                  <Pill>{Number(l.LivingArea).toLocaleString()} sqft</Pill>
                  <Pill>{l.PropertySubType}</Pill>
                </div>
                <div className="mt-3 text-xs text-slate-500">{l.SubdivisionName} · {l.DaysOnMarket} days on market</div>
              </div>
            </div>
          );
        })}
      </div>
    </main>
  );
}

function ListingDetail({ listing, isFav, toggleFavorite, askJasonAbout, back }) {
  return (
    <main className="mx-auto max-w-6xl px-4 py-8 md:px-8" data-testid="listing-detail-view">
      <button data-testid="listing-detail-back-btn" onClick={back} className="mb-4 rounded-full bg-white px-4 py-2 text-sm font-semibold shadow-sm">← Back to listings</button>
      <div className="overflow-hidden rounded-[2rem] bg-white shadow-sm">
        <div className="grid gap-1 md:grid-cols-3">
          {listing.Media?.slice(0, 3).map((m, i) => (
            <img key={i} src={m.MediaURL} alt="" className={`${i === 0 ? "md:col-span-2 md:row-span-2 h-96" : "h-48"} w-full object-cover`} />
          ))}
        </div>
        <div className="p-6 md:p-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="text-3xl font-bold">{money(listing.ListPrice)}</div>
              <div className="mt-1 text-slate-600">{listing.UnparsedAddress}</div>
              <div className="mt-3 flex flex-wrap gap-2 text-sm">
                <Pill>{listing.BedroomsTotal} bedrooms</Pill>
                <Pill>{listing.BathroomsTotalInteger} bathrooms</Pill>
                <Pill>{Number(listing.LivingArea).toLocaleString()} sqft</Pill>
                <Pill>Built {listing.YearBuilt}</Pill>
                <Pill>{listing.GarageSpaces}-car garage</Pill>
                <Pill>{listing.PropertySubType}</Pill>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="detail-favorite-btn"
                onClick={() => toggleFavorite(listing.ListingKey)}
                className={`rounded-2xl px-4 py-3 font-bold ${isFav ? "bg-[#C4A56B] text-[#1E1E1E]" : "bg-slate-100 text-slate-800"}`}
              >
                {isFav ? "★ Favorited" : "☆ Save"}
              </button>
              <button data-testid="detail-ask-jason-btn" onClick={() => askJasonAbout(listing)} className="rounded-2xl bg-[#1E1E1E] px-5 py-3 font-bold text-white">Ask Jason</button>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <Info label="Subdivision" value={listing.SubdivisionName} />
            <Info label="Lot Size" value={`${listing.LotSizeAcres} acres`} />
            <Info label="Days on Market" value={listing.DaysOnMarket} />
            <Info label="Elementary" value={listing.ElementarySchool} />
            <Info label="Middle" value={listing.MiddleOrJuniorSchool} />
            <Info label="High" value={listing.HighSchool} />
          </div>

          <div className="mt-6 rounded-3xl bg-[#f6f2ea] p-5">
            <div className="text-sm font-semibold text-[#1E1E1E]">About this home</div>
            <p className="mt-2 leading-7 text-slate-700">{listing.PublicRemarks}</p>
          </div>

          <div className="mt-4 text-xs text-slate-500">MLS #{listing.ListingId} · Listed by {listing.ListAgentFullName}, {listing.ListOfficeName}</div>
        </div>
      </div>
    </main>
  );
}

function Assistant({ chatMessages, chatInput, setChatInput, sendChat, startFlow, chatBusy, buyer, activeTransaction }) {
  return (
    <main className="mx-auto grid max-w-7xl gap-6 px-4 py-8 md:px-8 lg:grid-cols-[1.2fr_0.8fr]" data-testid="assistant-view">
      <section className="rounded-[2rem] bg-white p-6 shadow-sm">
        <div className="mb-6">
          <div className="text-sm font-semibold text-black">Jason · The Irving Assistant</div>
          <h1 className="text-3xl font-bold text-black">Talk to {BRAND.assistant} about buying, selling, lenders, or your transaction.</h1>
          <p className="mt-2 text-slate-600">
            {(() => {
              if (activeTransaction) {
                return <>Jason has your <strong>active transaction context</strong>. Ask anything about your deal.</>;
              }
              if (buyer) {
                return "Jason can see your favorites and viewing history.";
              }
              return "Sign in via Listings so Jason can personalize.";
            })()}
          </p>
          {activeTransaction && (
            <div data-testid="assistant-txn-badge" className="mt-3 inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-bold text-black">
              <span className="rounded-full bg-black px-2 py-0.5 text-[10px] text-white">Stage {activeTransaction.stage}</span>
              {activeTransaction.propertyAddress}
            </div>
          )}
        </div>
        <div data-testid="chat-window" className="h-[460px] overflow-auto rounded-3xl border border-slate-200 bg-slate-50 p-4">
          {chatMessages.map((msg, index) => (
            <div key={`${msg.role}-${index}-${(msg.text || "").slice(0, 16)}`} className={`mb-4 flex items-end gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              {msg.role === "assistant" && (
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-black ring-1 ring-[#C4A56B]/40">
                  <img src="/logo-new.png" alt="Jason" className="h-full w-full object-contain" />
                </div>
              )}
              <div data-testid={`chat-msg-${msg.role}`} className={`max-w-[78%] rounded-2xl px-4 py-3 text-sm leading-6 ${msg.role === "user" ? "bg-[#1E1E1E] text-white" : "bg-white text-slate-800 shadow-sm"}`}>{msg.text}</div>
            </div>
          ))}
          {chatBusy && (
            <div className="mb-4 flex items-end gap-2 justify-start">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-black ring-1 ring-[#C4A56B]/40">
                <img src="/logo-new.png" alt="Jason" className="h-full w-full object-contain" />
              </div>
              <div className="rounded-2xl bg-white px-4 py-3 text-sm text-slate-500 shadow-sm">Jason is typing…</div>
            </div>
          )}
        </div>
        <div className="mt-4 flex gap-3">
          <input data-testid="chat-input" value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendChat()} placeholder="Ask about buying, selling, a specific home, rates, or next steps..." className="flex-1 rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-[#C4A56B]" />
          <button data-testid="chat-send-btn" disabled={chatBusy} onClick={sendChat} className="rounded-2xl bg-[#1E1E1E] px-5 py-3 font-semibold text-white disabled:opacity-60">Send</button>
        </div>
      </section>
      <aside className="space-y-4">
        <div className="rounded-[2rem] bg-white p-6 shadow-sm">
          <h2 className="text-xl font-bold text-black">Start a guided path</h2>
          <div className="mt-4 grid gap-3">
            <button data-testid="aside-buy-btn" onClick={() => startFlow("Buyer")} className="rounded-2xl bg-[#f6f2ea] p-4 text-left font-semibold text-black hover:bg-[#eadfca]">I want to buy</button>
            <button data-testid="aside-sell-btn" onClick={() => startFlow("Seller")} className="rounded-2xl bg-[#f6f2ea] p-4 text-left font-semibold text-black hover:bg-[#eadfca]">I want to sell</button>
            <button data-testid="aside-rent-btn" onClick={() => startFlow("Renter")} className="rounded-2xl bg-[#f6f2ea] p-4 text-left font-semibold text-black hover:bg-[#eadfca]">I'm renting but want to buy</button>
          </div>
        </div>
        <ComplianceBox />
      </aside>
    </main>
  );
}

function LeadFlow({ type, form, updateField, submitLead, submitting }) {
  const fields = flowFields(type);
  const score = scoreLead(type, form);
  return (
    <main className="mx-auto max-w-5xl px-4 py-8 md:px-8" data-testid="flow-view">
      <div className="rounded-[2rem] bg-white p-6 shadow-sm md:p-8">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="text-sm font-semibold text-[#1E1E1E]">{type} Intake Flow</div>
            <h1 className="mt-1 text-3xl font-bold">Let's get your next move organized.</h1>
            <p className="mt-2 max-w-2xl text-slate-600">Answer a few questions so Jason can recommend the right next step and route your information to the brokerage dashboard.</p>
          </div>
          <div data-testid="live-score-badge" className={`rounded-full px-4 py-2 text-sm font-bold ${scoreColor(score)}`}>{score}</div>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {fields.map((field) => {
            const testId = `flow-field-${field.name}`;
            const value = form[field.name] || "";
            const onChange = (e) => updateField(field.name, e.target.value);
            let control;
            if (field.type === "select") {
              control = (
                <select
                  data-testid={testId}
                  value={value}
                  onChange={onChange}
                  className="mt-2 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 outline-none focus:border-[#C4A56B]"
                >
                  <option value="">Select one</option>
                  {field.options.map((option) => <option key={option}>{option}</option>)}
                </select>
              );
            } else if (field.type === "textarea") {
              control = (
                <textarea
                  data-testid={testId}
                  value={value}
                  onChange={onChange}
                  className="mt-2 min-h-28 w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-[#C4A56B]"
                />
              );
            } else {
              control = (
                <input
                  data-testid={testId}
                  value={value}
                  onChange={onChange}
                  placeholder={field.placeholder || ""}
                  className="mt-2 w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-[#C4A56B]"
                />
              );
            }
            return (
              <div key={field.name} className={field.large ? "md:col-span-2" : ""}>
                <label className="text-sm font-semibold text-slate-700">{field.label}</label>
                {control}
              </div>
            );
          })}
        </div>
        <div className="mt-8 rounded-2xl bg-[#f6f2ea] p-5">
          <div className="font-bold">Current readiness category: {score}</div>
          <p className="mt-2 text-sm text-slate-700">Recommended next action: {recommendedAction(type, score)}</p>
        </div>
        <SmsConsentCheckbox checked={form.smsConsent} onChange={(v) => updateField("smsConsent", v)} />
        <button data-testid="flow-submit-btn" disabled={submitting} onClick={submitLead} className="mt-6 w-full rounded-2xl bg-[#1E1E1E] px-6 py-4 font-bold text-white hover:bg-[#1f3354] disabled:opacity-60">
          {submitting ? "Saving…" : "Create Lead + Show Next Step"}
        </button>
      </div>
    </main>
  );
}

function LeadResult({ lead, setView }) {
  return (
    <main className="mx-auto max-w-4xl px-4 py-8 md:px-8" data-testid="result-view">
      <div className="rounded-[2rem] bg-white p-6 shadow-sm md:p-8">
        <div className={`mb-5 inline-flex rounded-full px-4 py-2 text-sm font-bold ${scoreColor(lead.score)}`} data-testid="result-score-badge">{lead.score}</div>
        <h1 className="text-3xl font-bold">Lead captured. Next move is clear.</h1>
        <p className="mt-3 text-slate-600">{lead.conversationSummary}</p>
        <div className="mt-6 rounded-3xl bg-[#f6f2ea] p-6">
          <div className="text-sm font-semibold text-[#1E1E1E]">Recommended Next Step</div>
          <div className="mt-2 text-xl font-bold">{lead.recommendedNextAction}</div>
        </div>
        <div className="mt-6 rounded-3xl border border-slate-200 p-6">
          <div className="text-sm font-semibold text-[#1E1E1E]">Follow-Up Script</div>
          <p className="mt-2 leading-7 text-slate-700">{followUpScript(lead)}</p>
        </div>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <button data-testid="result-view-dashboard-btn" onClick={() => setView("dashboard")} className="rounded-2xl bg-[#1E1E1E] px-6 py-3 font-bold text-white">View in Dashboard</button>
          <button data-testid="result-back-home-btn" onClick={() => setView("home")} className="rounded-2xl border border-slate-300 px-6 py-3 font-bold">Back Home</button>
        </div>
      </div>
    </main>
  );
}

function AdminGate({ admin, signInWithGoogle, authError, authProcessing }) {
  // admin === null while we're still checking the cookie; show a skeleton.
  const isChecking = admin === null || authProcessing;
  return (
    <main className="min-h-[70vh] bg-[#f6f2ea] px-4 py-16 md:py-24" data-testid="admin-gate-view">
      <div className="mx-auto max-w-md rounded-[2rem] bg-white p-8 shadow-xl ring-1 ring-slate-200">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl bg-black p-1 ring-1 ring-[#C4A56B]/30">
            <img src="/logo-new.png" alt="Irving Real Estate Group" className="h-full w-full object-contain" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#C4A56B]">Agent Console</div>
            <div className="text-lg font-bold text-[#1E1E1E]">Irving Real Estate Group</div>
          </div>
        </div>
        <h1 className="mt-8 text-2xl font-bold text-[#1E1E1E]">Sign in to your dashboard</h1>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Lead data, transactions, and the past-client nurture engine are private to authorized agents only. Sign in with your Google account to continue.
        </p>
        {authError && (
          <div data-testid="admin-auth-error" className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {authError}
          </div>
        )}
        <button
          data-testid="admin-google-signin-btn"
          onClick={signInWithGoogle}
          disabled={isChecking}
          className="mt-6 flex w-full items-center justify-center gap-3 rounded-xl bg-[#1E1E1E] px-5 py-3 font-bold text-white transition hover:bg-[#0c1828] disabled:opacity-60"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.616z" fill="#4285F4"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.859-3.048.859-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
            <path d="M3.964 10.705A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.705V4.963H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.037l3.007-2.332z" fill="#FBBC05"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.963L3.964 7.295C4.672 5.168 6.656 3.58 9 3.58z" fill="#EA4334"/>
          </svg>
          {isChecking ? "Checking session…" : "Sign in with Google"}
        </button>
        <p className="mt-4 text-xs leading-5 text-slate-500">
          Only emails on the brokerage allowlist can access this dashboard. If you're not Torey or an authorized team member, this view is not for you.
        </p>
      </div>
    </main>
  );
}

function Dashboard({ leads, allLeads, filter, setFilter, setSelectedLead, exportJSON, exportCSV, topListings, transactions, atRiskTxns, stagesMeta, openTransaction, createTransaction, createLead }) {
  const filters = ["All", "Buyer", "Seller", "Renter", "Hot Lead", "Warm Lead", "Nurture Lead"];
  const [sortBy, setSortBy] = useState("activity"); // "activity" | "signup"
  const [showNewTxnModal, setShowNewTxnModal] = useState(false);
  const [showHelpDrawer, setShowHelpDrawer] = useState(false);
  const [showNewLeadModal, setShowNewLeadModal] = useState(false);
  const [showSellerEmailsPreview, setShowSellerEmailsPreview] = useState(false);
  const sortedLeads = useMemo(() => {
    const copy = [...leads];
    if (sortBy === "activity") {
      copy.sort((a, b) => String(b.lastSeenAt || b.createdAt || "").localeCompare(String(a.lastSeenAt || a.createdAt || "")));
    } else {
      copy.sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
    }
    return copy;
  }, [leads, sortBy]);
  const [tab, setTab] = useState("today");
  const [today, setToday] = useState(null);
  const [todayLoading, setTodayLoading] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [reports, setReports] = useState([]);
  const [anniversaries, setAnniversaries] = useState([]);
  const [cities, setCities] = useState([]);
  const [reportCity, setReportCity] = useState("");
  const [snapshot, setSnapshot] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [nurtureMsg, setNurtureMsg] = useState("");
  const [upcoming, setUpcoming] = useState({ dueNow: [], comingSoon: [] });
  const [runningScheduler, setRunningScheduler] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);

  const loadNurture = useCallback(async () => {
    try {
      const [subs, reps, annv, cts, upc] = await Promise.all([
        axios.get(`${API}/subscribers?includeUnsubscribed=false`),
        axios.get(`${API}/nurture/reports?limit=20`),
        axios.get(`${API}/nurture/anniversaries?window_days=60`),
        axios.get(`${API}/market/cities`),
        axios.get(`${API}/nurture/upcoming?days=14`),
      ]);
      setSubscribers(subs.data || []);
      setReports(reps.data || []);
      setAnniversaries(annv.data || []);
      setCities(cts.data?.cities || []);
      setUpcoming(upc.data || { dueNow: [], comingSoon: [] });
    } catch (err) {
      devError("Nurture tab data fetch failed", err);
    }
  }, []);

  const loadToday = useCallback(async () => {
    setTodayLoading(true);
    try {
      const r = await axios.get(`${API}/dashboard/today`);
      setToday(r.data);
    } catch (err) {
      devError("Today fetch failed", err);
    } finally {
      setTodayLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "today") loadToday();
  }, [tab, loadToday]);

  useEffect(() => {
    if (tab === "nurture") loadNurture();
  }, [tab, loadNurture]);

  useEffect(() => {
    if (tab !== "nurture") return;
    const url = reportCity ? `${API}/market/snapshot?city=${encodeURIComponent(reportCity)}` : `${API}/market/snapshot`;
    axios.get(url).then((r) => setSnapshot(r.data)).catch(() => setSnapshot(null));
  }, [tab, reportCity]);

  const generateReport = async (send) => {
    if (!reportCity) {
      setNurtureMsg("Pick a city first.");
      return;
    }
    setGenerating(true);
    setNurtureMsg("");
    try {
      const res = await axios.post(`${API}/nurture/reports`, {
        city: reportCity,
        sendToSubscribers: send,
      });
      setNurtureMsg(send
        ? `Report generated and queued to ${res.data.deliveryCount} subscriber${res.data.deliveryCount === 1 ? "" : "s"} (MOCK email).`
        : "Report generated. Preview below.");
      await loadNurture();
    } catch (err) {
      setNurtureMsg(err?.response?.data?.detail || "Couldn't generate report.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <main className="mx-auto max-w-7xl px-4 py-8 md:px-8" data-testid="dashboard-view">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-sm font-semibold text-[#1E1E1E]">Brokerage Dashboard</div>
          <h1 className="text-3xl font-bold">Lead Command Center</h1>
          <p className="mt-2 text-slate-600">Track Jason's conversations, intake results, lead scores, listing engagement, transactions, and follow-up scripts.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="export-csv-btn" onClick={exportCSV} className="rounded-2xl border border-slate-300 bg-white px-4 py-3 font-semibold text-[#1E1E1E]">Export CSV</button>
          <button data-testid="export-json-btn" onClick={exportJSON} className="rounded-2xl bg-[#1E1E1E] px-4 py-3 font-semibold text-white">Export JSON</button>
          <button
            data-testid="cleanup-test-data-btn"
            onClick={async () => {
              if (!window.confirm("Delete all test/mock leads, subscribers, and alerts? This can't be undone.")) return;
              try {
                const r = await axios.post(`${API}/admin/cleanup-test-data`);
                window.alert(`Deleted:\n${Object.entries(r.data.deleted).map(([k, v]) => `  ${k}: ${v}`).join("\n")}`);
                window.location.reload();
              } catch (err) {
                window.alert("Cleanup failed: " + (err?.response?.data?.detail || err.message));
              }
            }}
            className="rounded-2xl border border-red-300 bg-red-50 px-4 py-3 font-semibold text-red-800 hover:bg-red-100"
          >
            🧹 Clean Test Data
          </button>
          <button
            data-testid="wipe-all-leads-btn"
            onClick={async () => {
              const first = window.confirm(
                "⚠️ WIPE EVERY LEAD, TRANSACTION, SUBSCRIBER, AND ATTACHED RECORD IN THE CRM?\n\n" +
                "This deletes every lead, transaction, past-client, consultation, favorite, chat, " +
                "follow-up, value report, and nurture report. Nothing is recoverable.\n\n" +
                "Use this to reset the CRM to zero."
              );
              if (!first) return;
              const typed = window.prompt("Type WIPE (all caps) to confirm.");
              if (typed !== "WIPE") { window.alert("Cancelled."); return; }
              try {
                const r = await axios.post(`${API}/admin/wipe-all-leads?confirm=YES`);
                window.alert(`Deleted:\n${Object.entries(r.data.deleted).map(([k, v]) => `  ${k}: ${v}`).join("\n")}`);
                window.location.reload();
              } catch (err) {
                window.alert("Wipe failed: " + (err?.response?.data?.detail || err.message));
              }
            }}
            className="rounded-2xl border border-red-500 bg-red-600 px-4 py-3 font-semibold text-white hover:bg-red-700"
          >
            💣 Wipe All Data
          </button>
        </div>
      </div>

      {atRiskTxns?.length > 0 && (
        <div data-testid="dashboard-risk-banner" className="mb-6 rounded-2xl border border-red-300 bg-red-50 p-4">
          <div className="font-bold text-red-900">⚠ {atRiskTxns.length} transaction{atRiskTxns.length === 1 ? "" : "s"} at risk</div>
          <ul className="mt-2 space-y-1 text-sm text-red-800">
            {atRiskTxns.slice(0, 5).map((t) => (
              <li key={t.id}>
                <button onClick={() => openTransaction(t.id)} className="underline">{t.propertyAddress}</button>. {t.atRiskReason}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        <button data-testid="dash-tab-today" onClick={() => setTab("today")} className={`rounded-full px-4 py-2 text-sm font-bold ${tab === "today" ? "bg-[#1E1E1E] text-white" : "bg-white text-slate-700"}`}>
          Today {today?.counts ? `(${(today.counts.newLeads || 0) + (today.counts.hotAlerts || 0) + (today.counts.overdue || 0)})` : ""}
        </button>
        <button data-testid="dash-tab-leads" onClick={() => setTab("leads")} className={`rounded-full px-4 py-2 text-sm font-bold ${tab === "leads" ? "bg-[#1E1E1E] text-white" : "bg-white text-slate-700"}`}>Leads</button>
        <button data-testid="dash-tab-pipeline" onClick={() => setTab("pipeline")} className={`rounded-full px-4 py-2 text-sm font-bold ${tab === "pipeline" ? "bg-[#1E1E1E] text-white" : "bg-white text-slate-700"}`}>Pipeline</button>
        <button data-testid="dash-tab-transactions" onClick={() => setTab("transactions")} className={`rounded-full px-4 py-2 text-sm font-bold ${tab === "transactions" ? "bg-[#1E1E1E] text-white" : "bg-white text-slate-700"}`}>
          Transactions {transactions?.length ? `(${transactions.length})` : ""}
        </button>
        <button data-testid="dash-tab-nurture" onClick={() => setTab("nurture")} className={`rounded-full px-4 py-2 text-sm font-bold ${tab === "nurture" ? "bg-[#1E1E1E] text-white" : "bg-white text-slate-700"}`}>
          Past Clients & Nurture
        </button>
        <button data-testid="dash-tab-content" onClick={() => setTab("content")} className={`rounded-full px-4 py-2 text-sm font-bold ${tab === "content" ? "bg-[#1E1E1E] text-white" : "bg-white text-slate-700"}`}>
          Content Studio
        </button>
        <button data-testid="dash-tab-team" onClick={() => setTab("team")} className={`rounded-full px-4 py-2 text-sm font-bold ${tab === "team" ? "bg-[#1E1E1E] text-white" : "bg-white text-slate-700"}`}>
          Team
        </button>
      </div>

      {tab === "team" && <TeamTab />}

      {tab === "today" && (
        <TodayPanel
          today={today}
          loading={todayLoading}
          refresh={loadToday}
          openLead={(id) => {
            const lead = allLeads.find((l) => l.id === id);
            if (lead) setSelectedLead(lead);
          }}
        />
      )}

      {tab === "pipeline" && (
        <PipelinePanel
          openLead={(id) => {
            const lead = allLeads.find((l) => l.id === id);
            if (lead) setSelectedLead(lead);
          }}
        />
      )}
      {tab === "leads" && (
        <>
          <div className="mb-6 grid gap-4 md:grid-cols-4">
            <Stat label="All Leads" value={allLeads.length} testId="dash-stat-all" />
            <Stat label="Hot" value={allLeads.filter((l) => l.score === "Hot Lead").length} testId="dash-stat-hot" />
            <Stat label="Warm" value={allLeads.filter((l) => l.score === "Warm Lead").length} testId="dash-stat-warm" />
            <Stat label="Nurture" value={allLeads.filter((l) => l.score === "Nurture Lead").length} testId="dash-stat-nurture" />
          </div>

          {topListings?.length > 0 && (
            <div className="mb-6 rounded-[2rem] bg-white p-5 shadow-sm" data-testid="top-listings-widget">
              <div className="text-sm font-semibold text-[#1E1E1E]">Most-viewed listings this week</div>
              <div className="mt-3 grid gap-3 md:grid-cols-5">
                {topListings.map((t) => (
                  <div key={t.listingKey} className="rounded-2xl bg-[#f6f2ea] p-3">
                    <div className="text-xs text-slate-500">{t.city}</div>
                    <div className="mt-1 text-sm font-bold">{money(t.listPrice)}</div>
                    <div className="mt-1 truncate text-xs text-slate-600" title={t.address}>{t.address}</div>
                    <div className="mt-2 text-xs font-bold text-[#1E1E1E]">{t.views} view{t.views === 1 ? "" : "s"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mb-6 flex flex-wrap items-center gap-2">
            {filters.map((f) => (
              <button key={f} data-testid={`filter-${f.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setFilter(f)} className={`rounded-full px-4 py-2 text-sm font-bold ${filter === f ? "bg-[#1E1E1E] text-white" : "bg-white text-slate-700"}`}>{f}</button>
            ))}
            <button
              data-testid="new-lead-btn"
              onClick={() => setShowNewLeadModal(true)}
              className="rounded-full bg-[#C4A56B] px-4 py-2 text-sm font-bold text-[#1E1E1E] hover:bg-[#d4b876]"
            >
              + New Lead
            </button>
            <div className="ml-auto flex items-center gap-2 rounded-full bg-white p-1 text-xs font-bold shadow-sm">
              <span className="px-2 text-slate-500">Sort:</span>
              <button
                data-testid="sort-activity"
                onClick={() => setSortBy("activity")}
                className={`rounded-full px-3 py-1.5 transition ${sortBy === "activity" ? "bg-[#1E1E1E] text-white" : "text-slate-600 hover:text-[#1E1E1E]"}`}
              >
                Last activity
              </button>
              <button
                data-testid="sort-signup"
                onClick={() => setSortBy("signup")}
                className={`rounded-full px-3 py-1.5 transition ${sortBy === "signup" ? "bg-[#1E1E1E] text-white" : "text-slate-600 hover:text-[#1E1E1E]"}`}
              >
                Newest signup
              </button>
            </div>
          </div>

          <div className="overflow-hidden rounded-[2rem] bg-white shadow-sm">
            <div className="grid grid-cols-12 gap-3 border-b border-slate-200 bg-[#1E1E1E] px-5 py-4 text-xs font-bold uppercase tracking-wide text-white">
              <div className="col-span-2">Lead</div>
              <div className="col-span-2">Contact</div>
              <div className="col-span-2">Timeline</div>
              <div className="col-span-2">Score</div>
              <div className="col-span-2">Stage</div>
              <div className="col-span-2">Action</div>
            </div>
            {sortedLeads.length === 0 && <div className="px-5 py-10 text-center text-slate-500" data-testid="dashboard-empty">No leads match this filter yet.</div>}
            {sortedLeads.map((lead) => {
              const seen = lastSeenLabel(lead.lastSeenAt || lead.createdAt);
              return (
              <button key={lead.id} data-testid={`lead-row-${lead.id}`} onClick={() => setSelectedLead(lead)} className="grid w-full grid-cols-12 gap-3 border-b border-slate-100 px-5 py-4 text-left text-sm hover:bg-[#f6f2ea]">
                <div className="col-span-2">
                  <div className="flex items-center gap-2">
                    <div className="font-bold">{lead.name || "Missing Name"}</div>
                    {seen && (
                      <span data-testid={`lead-lastseen-${lead.id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${LAST_SEEN_TONE[seen.tone]}`}>
                        {seen.tone === "live" && <span className="mr-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />}
                        {seen.text}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-1">
                    {lead.type}
                    {String(lead.source || "").toLowerCase().includes("placester") && (
                      <span data-testid={`lead-source-placester-${lead.id}`} className="rounded-full bg-[#1E1E1E] px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#C4A56B]">Placester</span>
                    )}
                  </div>
                </div>
                <div className="col-span-2"><div>{lead.phone || "No phone"}</div><div className="text-xs text-slate-500">{lead.email || "No email"}</div></div>
                <div className="col-span-2">{lead.timeline || "Not provided"}</div>
                <div className="col-span-2"><span className={`rounded-full px-3 py-1 text-xs font-bold ${scoreColor(lead.score)}`}>{lead.score}</span></div>
                <div className="col-span-2">{lead.pipelineStage}</div>
                <div className="col-span-2 text-xs text-slate-600">{lead.recommendedNextAction}</div>
              </button>
              );
            })}
          </div>
        </>
      )}

      {tab === "transactions" && (
        <div data-testid="transactions-kanban">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[#141414] p-5 text-[#E0D2A8]">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C4A56B]">Transactions · Concierge tracker</div>
              <div className="mt-1 text-lg font-bold">{transactions.length} active · drag through 8 closing stages</div>
              <div className="mt-1 text-xs text-[#E0D2A8]/70">Add a transaction for any existing lead in your CRM, then work it through option period, appraisal, and close.</div>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="diagnostics-btn"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/admin/diagnostics`);
                    const d = res.data;
                    const uptime = Math.floor((d.backend_uptime_seconds || 0) / 60);
                    const msg =
                      `Backend uptime: ${uptime} min (started ${d.backend_uptime_start})\n` +
                      `DB: ${d.db_name}\n\n` +
                      `Record counts:\n` +
                      Object.entries(d.counts).map(([k, v]) => `  ${k}: ${v}`).join("\n") +
                      `\n\nMost recent transactions:\n` +
                      (d.recent_transactions?.length
                        ? d.recent_transactions.map(t => `  ${t.createdAt?.slice(0,16)} · ${t.propertyAddress} · ${t.clientName || "?"}`).join("\n")
                        : "  (none)") +
                      `\n\nSendGrid: ${d.integrations.sendgrid_configured ? "✅ configured" : "❌ NOT configured"}\n` +
                      `Emergent LLM: ${d.integrations.emergent_llm_configured ? "✅ configured" : "❌ NOT configured"}`;
                    window.alert(msg);
                  } catch (err) {
                    window.alert(`Diagnostics error: ${err?.response?.data?.detail || err?.message}`);
                  }
                }}
                className="rounded-full border border-[#C4A56B]/60 px-4 py-2 text-sm font-bold text-[#C4A56B] hover:bg-[#C4A56B]/10"
              >
                🩺 Diagnostics
              </button>
              <button
                data-testid="preview-seller-emails-btn"
                onClick={() => setShowSellerEmailsPreview(true)}
                className="rounded-full border border-[#C4A56B]/60 px-4 py-2 text-sm font-bold text-[#C4A56B] hover:bg-[#C4A56B]/10"
              >
                ✉ Preview Seller Emails
              </button>
              <button
                data-testid="training-drawer-btn"
                onClick={() => setShowHelpDrawer(true)}
                className="rounded-full border border-[#C4A56B]/60 px-4 py-2 text-sm font-bold text-[#C4A56B] hover:bg-[#C4A56B]/10"
              >
                ? Training
              </button>
              <button
                data-testid="new-transaction-btn"
                onClick={() => setShowNewTxnModal(true)}
                className="rounded-full bg-[#C4A56B] px-5 py-2 text-sm font-bold text-[#1E1E1E] hover:bg-[#d4b876]"
              >
                + New Transaction
              </button>
            </div>
          </div>
          {transactions.length === 0 && <div className="rounded-3xl bg-white p-10 text-center text-slate-500">No active transactions yet. Click <strong>+ New Transaction</strong> above, or open any lead and click "Create Transaction".</div>}
          {transactions.length > 0 && (
            <div className="overflow-x-auto">
              <div className="flex min-w-max gap-4">
                {(stagesMeta || []).map((stage) => {
                  const stageTxns = transactions.filter((t) => t.stage === stage.num);
                  return (
                    <div key={stage.num} className="w-72 flex-shrink-0">
                      <div className="rounded-t-2xl bg-[#1E1E1E] px-4 py-3 text-xs font-bold uppercase text-white">
                        {stage.num}. {stage.name} <span className="ml-1 rounded-full bg-white/15 px-2 py-0.5">{stageTxns.length}</span>
                      </div>
                      <div className="space-y-3 rounded-b-2xl bg-slate-50 p-3 min-h-[200px]">
                        {stageTxns.map((t) => (
                          <button key={t.id} data-testid={`txn-card-${t.id}`} onClick={() => openTransaction(t.id)} className={`w-full rounded-xl bg-white p-3 text-left shadow-sm hover:shadow-md ${t.atRisk ? "border-2 border-red-300" : "border border-slate-200"}`}>
                            <div className="text-sm font-bold truncate">{t.propertyAddress}</div>
                            <div className="mt-1 text-xs text-slate-500">{money(t.listPrice)} · close {t.targetCloseDate}</div>
                            {t.atRisk && <div className="mt-2 rounded bg-red-50 px-2 py-1 text-[10px] font-bold text-red-800">⚠ At risk</div>}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "nurture" && (
        <div data-testid="nurture-tab" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-4">
            <Stat label="Total Contacts" value={subscribers.length} testId="nurture-stat-subs" />
            <Stat label="Past Clients" value={subscribers.filter((s) => s.contactType === "Past Client" || s.closingDate).length} testId="nurture-stat-past" />
            <Stat label="Sphere" value={subscribers.filter((s) => s.contactType === "Sphere").length} testId="nurture-stat-sphere" />
            <Stat label="Due Now" value={upcoming.dueNow.length} testId="nurture-stat-due" />
          </div>

          <div className="rounded-[2rem] bg-white p-6 shadow-sm" data-testid="nurture-scheduler-card">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-[#1E1E1E]">Auto-Nurture Scheduler</div>
                <h2 className="text-xl font-bold">Today's outreach queue</h2>
                <p className="mt-1 text-sm text-slate-600">
                  The system runs every hour and auto-fires cadences (weekly market for Past Clients & Prospects, monthly snapshot + quarterly referral ask for Sphere, plus closing-anniversary check-ins).
                  {" "}<span className="font-semibold">Emails currently queue as MOCKED</span>. connect SendGrid to go live.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid="nurture-add-contact-btn"
                  onClick={() => setShowAddContact(true)}
                  className="rounded-xl bg-[#1E1E1E] px-4 py-2.5 text-sm font-bold text-white"
                >
                  + Add Contact
                </button>
                <button
                  data-testid="nurture-csv-import-btn"
                  onClick={() => setShowCsvImport(true)}
                  className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-700"
                >
                  Import CSV
                </button>
                <button
                  data-testid="nurture-run-scheduler-btn"
                  onClick={async () => {
                    setRunningScheduler(true);
                    try {
                      const r = await axios.post(`${API}/nurture/scheduler/run`);
                      setNurtureMsg(`Scheduler queued ${r.data.queued} message${r.data.queued === 1 ? "" : "s"} (MOCKED).`);
                      await loadNurture();
                    } catch (err) {
                      setNurtureMsg(err?.response?.data?.detail || "Scheduler failed.");
                    } finally {
                      setRunningScheduler(false);
                    }
                  }}
                  disabled={runningScheduler || upcoming.dueNow.length === 0}
                  className="rounded-xl bg-[#C4A56B] px-4 py-2.5 text-sm font-bold text-[#1E1E1E] disabled:opacity-60"
                >
                  {runningScheduler ? "Sending…" : `Run Now (${upcoming.dueNow.length})`}
                </button>
              </div>
            </div>

            {upcoming.dueNow.length === 0 && upcoming.comingSoon.length === 0 && (
              <div className="mt-5 rounded-2xl bg-[#f6f2ea] p-6 text-center text-sm text-slate-500">
                Nothing in the queue. Add contacts and the scheduler will start firing within 60 minutes.
              </div>
            )}

            {upcoming.dueNow.length > 0 && (
              <div className="mt-5">
                <div className="text-xs font-bold uppercase tracking-wide text-emerald-700">Due now ({upcoming.dueNow.length})</div>
                <ul className="mt-2 divide-y divide-slate-100 rounded-2xl bg-emerald-50 ring-1 ring-emerald-200">
                  {upcoming.dueNow.slice(0, 8).map((u) => (
                    <li key={u.id} data-testid={`due-now-${u.id}`} className="grid grid-cols-12 gap-3 px-4 py-2.5 text-sm">
                      <div className="col-span-4 truncate font-bold text-[#1E1E1E]">{u.name || u.email}</div>
                      <div className="col-span-3 truncate text-slate-600">{u.contactType || "Prospect"}</div>
                      <div className="col-span-3 truncate text-slate-600">{u.cadence}</div>
                      <div className="col-span-2 truncate text-xs text-slate-500">{u.reason}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {upcoming.comingSoon.length > 0 && (
              <div className="mt-4">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Coming up (next 14 days)</div>
                <ul className="mt-2 divide-y divide-slate-100">
                  {upcoming.comingSoon.slice(0, 6).map((u) => (
                    <li key={u.id} className="grid grid-cols-12 gap-3 px-1 py-2 text-sm">
                      <div className="col-span-4 truncate font-bold">{u.name || u.email}</div>
                      <div className="col-span-3 truncate text-slate-600">{u.contactType}</div>
                      <div className="col-span-3 truncate text-slate-600">{u.cadence}</div>
                      <div className="col-span-2 text-xs text-slate-500">in {u.daysUntil}d</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {showAddContact && (
            <AddContactModal
              cities={cities}
              onClose={() => setShowAddContact(false)}
              onSaved={() => { setShowAddContact(false); loadNurture(); }}
            />
          )}
          {showCsvImport && (
            <CsvImportModal
              onClose={() => setShowCsvImport(false)}
              onImported={() => { setShowCsvImport(false); loadNurture(); }}
            />
          )}

          <div className="rounded-[2rem] bg-white p-6 shadow-sm" data-testid="nurture-report-generator">
            <div className="text-sm font-semibold text-[#1E1E1E]">Weekly Market Report Generator</div>
            <h2 className="text-xl font-bold">Generate this week's update</h2>
            <p className="mt-1 text-sm text-slate-600">Pulls live MLS stats, writes a professional 3-paragraph update, and (optionally) queues a branded email to every subscriber in that city.</p>
            <div className="mt-4 grid gap-3 md:grid-cols-[2fr_auto_auto]">
              <select
                data-testid="nurture-city-select"
                value={reportCity}
                onChange={(e) => setReportCity(e.target.value)}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm"
              >
                <option value="">Select a city…</option>
                {cities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <button
                data-testid="nurture-generate-preview"
                onClick={() => generateReport(false)}
                disabled={generating}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 disabled:opacity-60"
              >
                {generating ? "Generating…" : "Preview Report"}
              </button>
              <button
                data-testid="nurture-generate-send"
                onClick={() => generateReport(true)}
                disabled={generating}
                className="rounded-xl bg-[#1E1E1E] px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60"
              >
                {generating ? "Sending…" : "Generate & Send"}
              </button>
            </div>
            {nurtureMsg && <div data-testid="nurture-message" className="mt-3 text-sm font-semibold text-[#1E1E1E]">{nurtureMsg}</div>}

            {snapshot && (
              <div data-testid="nurture-snapshot" className="mt-5 grid gap-3 rounded-2xl bg-[#f6f2ea] p-4 md:grid-cols-5">
                <Snap label="Active" value={snapshot.activeCount} />
                <Snap label="Median Price" value={money(snapshot.medianPrice)} />
                <Snap label="$/sqft" value={`$${snapshot.medianPricePerSqft}`} />
                <Snap label="Median Beds/Baths" value={`${snapshot.medianBeds}/${snapshot.medianBaths}`} />
                <Snap label="Avg DOM" value={snapshot.averageDaysOnMarket} />
              </div>
            )}
          </div>

          {reports.length > 0 && (
            <div className="rounded-[2rem] bg-white p-6 shadow-sm" data-testid="nurture-reports-list">
              <div className="text-sm font-semibold text-[#1E1E1E]">Recent Reports</div>
              <h2 className="text-xl font-bold">Past weekly updates</h2>
              <div className="mt-4 space-y-3">
                {reports.slice(0, 5).map((r) => (
                  <details key={r.id} className="rounded-2xl bg-[#f6f2ea] p-4">
                    <summary className="cursor-pointer text-sm font-bold text-[#1E1E1E]">
                      {r.subject}
                      {r.deliveryCount > 0 && <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800">Sent to {r.deliveryCount}</span>}
                    </summary>
                    <div className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-700">{r.body}</div>
                  </details>
                ))}
              </div>
            </div>
          )}

          {anniversaries.length > 0 && (
            <div className="rounded-[2rem] bg-white p-6 shadow-sm" data-testid="nurture-anniversaries">
              <div className="text-sm font-semibold text-[#1E1E1E]">Closing Anniversary Reminders</div>
              <h2 className="text-xl font-bold">Reach out before these dates</h2>
              <ul className="mt-4 divide-y divide-slate-100">
                {anniversaries.map((a) => (
                  <li key={a.id} className="flex items-center justify-between py-3 text-sm">
                    <div>
                      <div className="font-bold text-[#1E1E1E]">{a.name || a.email}</div>
                      <div className="text-xs text-slate-500">{a.propertyAddress || "Property address not on file"}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-[#1E1E1E]">{a.daysUntil === 0 ? "Today" : `${a.daysUntil} days`}</div>
                      <div className="text-xs text-slate-500">{a.yearsClosed}-yr anniversary · {a.anniversaryDate}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="rounded-[2rem] bg-white p-6 shadow-sm" data-testid="nurture-subscribers-list">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-[#1E1E1E]">Subscriber List</div>
                <h2 className="text-xl font-bold">All active subscribers</h2>
              </div>
            </div>
            {subscribers.length === 0 && <div className="mt-6 rounded-2xl bg-[#f6f2ea] p-6 text-center text-sm text-slate-500">No subscribers yet. The footer signup will populate this list.</div>}
            {subscribers.length > 0 && (
              <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
                <div className="grid grid-cols-12 gap-3 bg-[#1E1E1E] px-5 py-3 text-xs font-bold uppercase tracking-wide text-white">
                  <div className="col-span-3">Name</div>
                  <div className="col-span-3">Email</div>
                  <div className="col-span-2">City</div>
                  <div className="col-span-2">Type</div>
                  <div className="col-span-2">Joined</div>
                </div>
                {subscribers.slice(0, 50).map((s) => (
                  <div key={s.id} data-testid={`subscriber-row-${s.id}`} className="grid grid-cols-12 gap-3 border-t border-slate-100 px-5 py-3 text-sm">
                    <div className="col-span-3 font-bold">{s.name || "-"}</div>
                    <div className="col-span-3 truncate text-slate-600">{s.email}</div>
                    <div className="col-span-2">{s.city || "DFW"}</div>
                    <div className="col-span-2">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${s.closingDate ? "bg-[#C4A56B]/20 text-[#7a5a00]" : "bg-slate-100 text-slate-700"}`}>
                        {s.closingDate ? "Past Client" : "Prospect"}
                      </span>
                    </div>
                    <div className="col-span-2 text-xs text-slate-500">{(s.createdAt || "").slice(0, 10)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "content" && <ContentStudioTab />}

      <ComplianceBox />

      {showNewTxnModal && (
        <NewTransactionModal
          allLeads={allLeads}
          onClose={() => setShowNewTxnModal(false)}
          createTransaction={createTransaction}
          createLead={createLead}
        />
      )}
      {showHelpDrawer && <TrainingDrawer onClose={() => setShowHelpDrawer(false)} />}
      {showSellerEmailsPreview && <SellerEmailsPreviewModal onClose={() => setShowSellerEmailsPreview(false)} />}
      {showNewLeadModal && (
        <NewLeadModal
          onClose={() => setShowNewLeadModal(false)}
          createLead={createLead}
        />
      )}
    </main>
  );
}

function Snap({ label, value }) {
  return (
    <div className="rounded-xl bg-white p-3">
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-bold text-[#1E1E1E]">{value}</div>
    </div>
  );
}

function NewTransactionModal({ allLeads, onClose, createTransaction, createLead }) {
  const [mode, setMode] = useState("existing"); // "existing" | "adhoc"
  const [leadId, setLeadId] = useState("");
  const [search, setSearch] = useState("");
  const [adhoc, setAdhoc] = useState({
    type: "Buyer",
    name: "",
    email: "",
    phone: "",
  });
  const contractDefault = new Date().toLocaleDateString("en-CA");
  // Default option-period end = contract + 10 days; target close = contract + 30
  const addDaysISO = (base, days) => {
    const d = new Date(base + "T00:00:00");
    d.setDate(d.getDate() + days);
    return d.toLocaleDateString("en-CA");
  };
  const [form, setForm] = useState({
    propertyAddress: "",
    listPrice: "",
    contractDate: contractDefault,
    optionPeriodEndDate: addDaysISO(contractDefault, 10),
    targetCloseDate: addDaysISO(contractDefault, 30),
    earnestMoney: "",
    lenderName: "",
    titleCompany: "",
    hoaName: "",
    buyerAgentName: "",
    buyerAgentEmail: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Auto-recalculate default option-end + target-close whenever contract date
  // changes, but only if the user hasn't manually overridden them.
  const [datesTouched, setDatesTouched] = useState({ option: false, close: false });
  useEffect(() => {
    if (!form.contractDate) return;
    setForm((f) => ({
      ...f,
      optionPeriodEndDate: datesTouched.option ? f.optionPeriodEndDate : addDaysISO(form.contractDate, 10),
      targetCloseDate: datesTouched.close ? f.targetCloseDate : addDaysISO(form.contractDate, 30),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.contractDate]);

  const eligibleLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (allLeads || [])
      .filter((l) => !l.transactionId)
      .filter((l) => {
        if (!q) return true;
        return (
          (l.name || "").toLowerCase().includes(q) ||
          (l.email || "").toLowerCase().includes(q) ||
          (l.phone || "").toLowerCase().includes(q)
        );
      })
      .slice(0, 50);
  }, [allLeads, search]);

  const selectedLead = (allLeads || []).find((l) => l.id === leadId);

  const adhocReady = adhoc.name.trim() && (adhoc.email.trim() || adhoc.phone.trim());
  const contractReady = form.propertyAddress && form.contractDate;
  const canSubmit =
    contractReady && (mode === "existing" ? !!selectedLead : adhocReady);

  async function handleSubmit() {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      let leadForTxn = selectedLead;
      if (mode === "adhoc") {
        // Auto-create the lead first so the client is tracked in the CRM too.
        leadForTxn = await createLead({
          type: adhoc.type,
          name: adhoc.name.trim(),
          email: adhoc.email.trim(),
          phone: adhoc.phone.trim(),
          timeline: "Under Contract",
          source: "Manual (referral / walk-in)",
        });
      }
      if (leadForTxn) {
        await createTransaction(leadForTxn, form);
      }
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="new-txn-modal">
      <div className="w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between bg-[#1E1E1E] px-6 py-4 text-white">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C4A56B]">New Transaction</div>
            <div className="text-lg font-bold">Start a concierge file</div>
          </div>
          <button data-testid="new-txn-close" onClick={onClose} className="text-2xl leading-none text-white/80 hover:text-white">×</button>
        </div>

        <div className="max-h-[75vh] space-y-5 overflow-y-auto p-6 text-[#1E1E1E]">
          {/* Mode toggle */}
          <div className="flex rounded-full bg-slate-100 p-1 text-xs font-bold">
            <button
              data-testid="new-txn-mode-existing"
              type="button"
              onClick={() => setMode("existing")}
              className={`flex-1 rounded-full px-4 py-2 transition ${mode === "existing" ? "bg-[#1E1E1E] text-white" : "text-slate-600"}`}
            >
              Existing CRM lead
            </button>
            <button
              data-testid="new-txn-mode-adhoc"
              type="button"
              onClick={() => setMode("adhoc")}
              className={`flex-1 rounded-full px-4 py-2 transition ${mode === "adhoc" ? "bg-[#1E1E1E] text-white" : "text-slate-600"}`}
            >
              New client (referral / walk-in)
            </button>
          </div>

          {/* Step 1 */}
          {mode === "existing" && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-slate-500">1 · Select CRM lead</div>
              {!selectedLead && (
                <>
                  <input
                    data-testid="new-txn-lead-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search leads by name, email, or phone…"
                    className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
                  />
                  <div className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-slate-200">
                    {eligibleLeads.length === 0 && (
                      <div className="p-4 text-center text-sm text-slate-500">No available leads. Every lead already has a transaction, or none match your search. Switch to "New client" tab above for walk-ins.</div>
                    )}
                    {eligibleLeads.map((l) => (
                      <button
                        key={l.id}
                        data-testid={`new-txn-pick-${l.id}`}
                        onClick={() => setLeadId(l.id)}
                        className="flex w-full items-center justify-between border-b border-slate-100 px-4 py-2 text-left text-sm hover:bg-slate-50"
                      >
                        <div>
                          <div className="font-semibold">{l.name || "(no name)"} · <span className="text-slate-500">{l.type}</span></div>
                          <div className="text-xs text-slate-500">{l.email || "—"} · {l.phone || "—"}</div>
                        </div>
                        <span className="text-xs font-bold text-[#C4A56B]">Select →</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
              {selectedLead && (
                <div className="mt-2 flex items-center justify-between rounded-xl border border-[#C4A56B] bg-[#FBF5E7] px-4 py-2">
                  <div className="text-sm">
                    <div className="font-bold">{selectedLead.name} · {selectedLead.type}</div>
                    <div className="text-xs text-slate-600">{selectedLead.email || "—"} · {selectedLead.phone || "—"}</div>
                  </div>
                  <button data-testid="new-txn-change-lead" onClick={() => setLeadId("")} className="text-xs font-bold text-[#1E1E1E] underline">Change</button>
                </div>
              )}
            </div>
          )}

          {mode === "adhoc" && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-slate-500">1 · New client details</div>
              <p className="mt-1 text-[11px] text-slate-500">We'll auto-create a matching lead record in your CRM so this client also shows up on the Kanban and reports.</p>
              <div className="mt-2 grid gap-3 md:grid-cols-2">
                <label className="text-xs">
                  <span className="mb-1 block font-semibold">Client Type *</span>
                  <select
                    data-testid="new-txn-adhoc-type"
                    value={adhoc.type}
                    onChange={(e) => setAdhoc({ ...adhoc, type: e.target.value })}
                    className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm"
                  >
                    <option value="Buyer">Buyer</option>
                    <option value="Seller">Seller</option>
                    <option value="Renter">Renter</option>
                  </select>
                </label>
                <label className="text-xs">
                  <span className="mb-1 block font-semibold">Full Name *</span>
                  <input
                    data-testid="new-txn-adhoc-name"
                    value={adhoc.name}
                    onChange={(e) => setAdhoc({ ...adhoc, name: e.target.value })}
                    className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
                  />
                </label>
                <label className="text-xs">
                  <span className="mb-1 block font-semibold">Email</span>
                  <input
                    data-testid="new-txn-adhoc-email"
                    type="email"
                    value={adhoc.email}
                    onChange={(e) => setAdhoc({ ...adhoc, email: e.target.value })}
                    className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
                  />
                </label>
                <label className="text-xs">
                  <span className="mb-1 block font-semibold">Phone</span>
                  <input
                    data-testid="new-txn-adhoc-phone"
                    value={adhoc.phone}
                    onChange={(e) => setAdhoc({ ...adhoc, phone: e.target.value })}
                    className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
                  />
                </label>
              </div>
              <div className="mt-1 text-[11px] text-slate-500">Email or phone is required (one of the two).</div>
            </div>
          )}

          {/* Step 2 */}
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500">2 · Contract details</div>
            <div className="mt-2 grid gap-3 md:grid-cols-2">
              <label className="md:col-span-2 text-xs">
                <span className="mb-1 block font-semibold">Property Address *</span>
                <input
                  data-testid="new-txn-address"
                  value={form.propertyAddress}
                  onChange={(e) => setForm({ ...form, propertyAddress: e.target.value })}
                  placeholder="1234 Cedar Elm Ln, Dallas, TX 75201"
                  className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
                />
              </label>
              <label className="text-xs">
                <span className="mb-1 block font-semibold">Contract Date *</span>
                <input
                  data-testid="new-txn-contract-date"
                  type="date"
                  value={form.contractDate}
                  onChange={(e) => setForm({ ...form, contractDate: e.target.value })}
                  className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
                />
              </label>
              <label className="text-xs">
                <span className="mb-1 block font-semibold">Option Period End Date</span>
                <input
                  data-testid="new-txn-option-end"
                  type="date"
                  value={form.optionPeriodEndDate}
                  onChange={(e) => { setDatesTouched((d) => ({ ...d, option: true })); setForm({ ...form, optionPeriodEndDate: e.target.value }); }}
                  className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
                />
              </label>
              <label className="text-xs">
                <span className="mb-1 block font-semibold">Target Close Date</span>
                <input
                  data-testid="new-txn-close-date"
                  type="date"
                  value={form.targetCloseDate}
                  onChange={(e) => { setDatesTouched((d) => ({ ...d, close: true })); setForm({ ...form, targetCloseDate: e.target.value }); }}
                  className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
                />
              </label>
              <label className="text-xs">
                <span className="mb-1 block font-semibold">Contract Price</span>
                <input
                  data-testid="new-txn-price"
                  type="number"
                  value={form.listPrice}
                  onChange={(e) => setForm({ ...form, listPrice: e.target.value })}
                  placeholder="525000"
                  className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
                />
              </label>
              <label className="text-xs">
                <span className="mb-1 block font-semibold">Earnest Money ($)</span>
                <input
                  data-testid="new-txn-earnest"
                  type="number"
                  value={form.earnestMoney}
                  onChange={(e) => setForm({ ...form, earnestMoney: e.target.value })}
                  placeholder="5000"
                  className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
                />
              </label>
              <label className="text-xs">
                <span className="mb-1 block font-semibold">Title Company</span>
                <input
                  data-testid="new-txn-title"
                  value={form.titleCompany}
                  onChange={(e) => setForm({ ...form, titleCompany: e.target.value })}
                  placeholder="e.g. North Texas Title"
                  className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
                />
              </label>
              <label className="text-xs">
                <span className="mb-1 block font-semibold">Lender</span>
                <input
                  data-testid="new-txn-lender"
                  value={form.lenderName}
                  onChange={(e) => setForm({ ...form, lenderName: e.target.value })}
                  placeholder="e.g. First Texas Bank"
                  className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
                />
              </label>
              <label className="text-xs">
                <span className="mb-1 block font-semibold">HOA Name</span>
                <input
                  data-testid="new-txn-hoa"
                  value={form.hoaName}
                  onChange={(e) => setForm({ ...form, hoaName: e.target.value })}
                  placeholder="Optional — leave blank if none"
                  className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
                />
              </label>
              <label className="text-xs">
                <span className="mb-1 block font-semibold">Buyer's Agent Name</span>
                <input
                  data-testid="new-txn-buyer-agent-name"
                  value={form.buyerAgentName}
                  onChange={(e) => setForm({ ...form, buyerAgentName: e.target.value })}
                  placeholder="e.g. Rachel Kim (Compass)"
                  className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
                />
              </label>
              <label className="text-xs">
                <span className="mb-1 block font-semibold">Buyer's Agent Email</span>
                <input
                  data-testid="new-txn-buyer-agent-email"
                  type="email"
                  value={form.buyerAgentEmail}
                  onChange={(e) => setForm({ ...form, buyerAgentEmail: e.target.value })}
                  placeholder="Optional — for disclosure delivery"
                  className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
                />
              </label>
              <label className="md:col-span-2 text-xs">
                <span className="mb-1 block font-semibold">Deal Notes</span>
                <textarea
                  data-testid="new-txn-notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  placeholder="Anything the concierge nurture should know — repairs, motivations, contingencies, etc."
                  className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
                />
              </label>
            </div>
            <div className="mt-2 text-[11px] text-slate-500">Option period end + target close default to contract+10 and contract+30 days. Edit above if your contract specifies different dates.</div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button data-testid="new-txn-cancel" onClick={onClose} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-bold text-[#1E1E1E]">Cancel</button>
            <button
              data-testid="new-txn-submit"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="rounded-xl bg-[#1E1E1E] px-6 py-2 text-sm font-bold text-white disabled:opacity-50"
            >
              {submitting ? "Creating…" : "Create Transaction"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function NewLeadModal({ onClose, createLead }) {
  const [form, setForm] = useState({
    type: "Buyer",
    name: "",
    email: "",
    phone: "",
    timeline: "",
    location: "",
    budget: "",
    source: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = form.type && form.name.trim() && (form.email.trim() || form.phone.trim());

  async function handleSubmit() {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await createLead({
        type: form.type,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        timeline: form.timeline,
        location: form.location,
        budget: form.budget,
        source: form.source || "Manual (agent-added)",
        concern: form.notes,
      });
      onClose();
    } catch (err) {
      setError("Could not save. Check the details and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="new-lead-modal">
      <div className="w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between bg-[#1E1E1E] px-6 py-4 text-white">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C4A56B]">New Lead</div>
            <div className="text-lg font-bold">Add a lead manually to the pipeline</div>
          </div>
          <button data-testid="new-lead-close" onClick={onClose} className="text-2xl leading-none text-white/80 hover:text-white">×</button>
        </div>
        <div className="max-h-[75vh] space-y-4 overflow-y-auto p-6 text-[#1E1E1E]">
          <p className="text-xs text-slate-600">Use this for referrals, open-house sign-ins, phone-ins, or anyone who didn't submit through the website. Jason will still score and slot them into the Kanban.</p>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-xs">
              <span className="mb-1 block font-semibold">Lead Type *</span>
              <select
                data-testid="new-lead-type"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm"
              >
                <option value="Buyer">Buyer</option>
                <option value="Seller">Seller</option>
                <option value="Renter">Renter</option>
              </select>
            </label>
            <label className="text-xs">
              <span className="mb-1 block font-semibold">Full Name *</span>
              <input
                data-testid="new-lead-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
              />
            </label>
            <label className="text-xs">
              <span className="mb-1 block font-semibold">Email</span>
              <input
                data-testid="new-lead-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
              />
            </label>
            <label className="text-xs">
              <span className="mb-1 block font-semibold">Phone</span>
              <input
                data-testid="new-lead-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
              />
            </label>
            <label className="text-xs">
              <span className="mb-1 block font-semibold">Timeline</span>
              <select
                data-testid="new-lead-timeline"
                value={form.timeline}
                onChange={(e) => setForm({ ...form, timeline: e.target.value })}
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm"
              >
                <option value="">Select timeline</option>
                <option value="0-3 months">0-3 months</option>
                <option value="3-6 months">3-6 months</option>
                <option value="6-12 months">6-12 months</option>
                <option value="12+ months">12+ months</option>
                <option value="Just browsing">Just browsing</option>
              </select>
            </label>
            <label className="text-xs">
              <span className="mb-1 block font-semibold">Neighborhood / City</span>
              <input
                data-testid="new-lead-location"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Preston Hollow, Frisco, Southlake…"
                className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
              />
            </label>
            <label className="text-xs">
              <span className="mb-1 block font-semibold">Budget / Price Range</span>
              <input
                data-testid="new-lead-budget"
                value={form.budget}
                onChange={(e) => setForm({ ...form, budget: e.target.value })}
                placeholder="$450k–$550k"
                className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
              />
            </label>
            <label className="text-xs">
              <span className="mb-1 block font-semibold">Source</span>
              <input
                data-testid="new-lead-source"
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                placeholder="Referral from Sarah / Open house / Sign call"
                className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
              />
            </label>
            <label className="md:col-span-2 text-xs">
              <span className="mb-1 block font-semibold">Notes</span>
              <textarea
                data-testid="new-lead-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                placeholder="Anything Jason should know — motivations, concerns, must-haves…"
                className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm"
              />
            </label>
          </div>
          <div className="text-[11px] text-slate-500">Email or phone is required. Jason will auto-score the lead and place it on the Kanban.</div>
          {error && <div data-testid="new-lead-error" className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button data-testid="new-lead-cancel" onClick={onClose} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-bold text-[#1E1E1E]">Cancel</button>
            <button
              data-testid="new-lead-submit"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="rounded-xl bg-[#1E1E1E] px-6 py-2 text-sm font-bold text-white disabled:opacity-50"
            >
              {submitting ? "Saving…" : "Create Lead"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TrainingDrawer({ onClose }) {
  const sections = [
    {
      key: "pipeline",
      title: "The 17-Stage Lead Pipeline",
      body: [
        "Every lead lives on a Kanban board in the Leads tab. The stages take a prospect from first touch (New) all the way to Referral Given after closing.",
        "How to work it: open a lead card, review Jason's score + recommended action, then drag the card to the next stage after each meaningful touch (Contacted → Consult Booked → Consult Held → Buyer/Seller Consult Prep, etc.).",
        "Example workflow: A new buyer submits the Home Search form. Card appears in 'New'. You call within 5 minutes → drag to 'Contacted'. Book Saturday tour → 'Consult Booked'. After showing → 'Showing Complete'. Wrote offer → move to Transactions tab (see below).",
      ],
    },
    {
      key: "today",
      title: "Today's Follow-Ups Dashboard",
      body: [
        "The 'Today' tab is your morning run-sheet. It groups tasks by urgency: Overdue (red), Due Today (gold), Due Tomorrow, and Hot Leads with no touch in 24hrs.",
        "How to work it: open at 8am with coffee. Clear the red row first, then the gold row. Every item is one click to the lead. Log a note or send an email/SMS right from the lead modal — the follow-up auto-clears.",
        "Example: 'Overdue: Sarah Chen — hasn't been contacted since Feb 12'. Click card → send an SMS from the modal → the item disappears from Today.",
      ],
    },
    {
      key: "leads",
      title: "Leads Tab — Filters, Search & Bulk Actions",
      body: [
        "Filters at the top let you slice by lead type (Buyer/Seller/Renter) and by score (Hot/Warm/Nurture). Use these before your morning follow-ups.",
        "Click any lead card to open the profile modal. Inside you can: log a call, send an email (uses SendGrid), send a mock SMS, book a consult, add private notes, or promote to a transaction.",
        "Example: Filter to 'Hot Lead' → sort by activity → work down the list top-to-bottom, targeting one 5-minute call per lead. Log the outcome in Notes before closing the modal.",
      ],
    },
    {
      key: "transactions",
      title: "Transactions Tab — The 8-Stage Concierge Tracker",
      body: [
        "Once a lead signs a contract, they move here. Each transaction has an 8-stage checklist: Under Contract → Option Period → Inspection → Appraisal → Financing → Title/Survey → Final Walk → Closed.",
        "To create one: click '+ New Transaction' at the top of this tab, pick the lead, enter the property address + contract date. The system auto-calculates target close (30 days out) and option period end (7 days).",
        "Once inside the transaction, tick items on the checklist as you complete them. Click 'Advance Stage' to move to the next milestone. The card turns red on the Kanban if it's 'at-risk' (a milestone slipped).",
        "Example: You just papered a deal on Feb 20. Create transaction → option ends Feb 27, close targets Mar 22. Tick 'Executed contract uploaded' today, 'Inspection scheduled' tomorrow, etc.",
      ],
    },
    {
      key: "hotlead",
      title: "AI Hot Lead Alerts (Jason)",
      body: [
        "The bell icon in the top nav shows real-time hot lead alerts. Claude Sonnet 4.5 scans every new lead and every activity change, and pings you when someone crosses the 'hot' threshold (e.g. mortgage-ready, tight timeline, high engagement).",
        "How to work it: when the bell lights up, click it → read Jason's 2-sentence call script → hit 'Call Now'. Aim to react within 5 minutes; conversion drops sharply after 15.",
        "Example: 'Michael Brown just viewed 4 luxury listings in Preston Hollow AND filled the Home Value form for his current condo. Likely move-up buyer. Script: mention he can leverage equity for the upgrade — offer a private tour Saturday.'",
      ],
    },
    {
      key: "email",
      title: "Emails, SMS & Home Value PDFs",
      body: [
        "Emails: Every outbound email from the lead modal ships via SendGrid on your domain. Templates are pre-loaded (New Buyer Welcome, Home Value Delivery, Consult Confirmation, etc.).",
        "SMS: Live via Twilio on your approved toll-free number. Clicks send real texts. TCPA opt-out is enforced automatically — opted-out numbers are blocked at the API before Twilio ever sees them.",
        "Home Value PDFs: When a seller submits the Home Value form on the site, the system auto-generates a branded PDF report and emails it to them within seconds. You'll see the send status on the lead card.",
        "Example: Seller submits at 2pm → PDF hits their inbox at 2:00:15pm → lead lands on Kanban at 'New (Seller)' with 'Delivered CMA' checkbox already ticked → you follow up at day 2 with a call.",
      ],
    },
  ];
  const [open, setOpen] = useState(sections[0].key);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" data-testid="training-drawer">
      <div className="h-full w-full max-w-xl overflow-y-auto bg-white text-[#1E1E1E] shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between bg-[#1E1E1E] px-6 py-4 text-white">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C4A56B]">Back-Office Training</div>
            <div className="text-lg font-bold">How to run the CRM</div>
          </div>
          <button data-testid="training-close-btn" onClick={onClose} className="text-2xl leading-none text-white/80 hover:text-white">×</button>
        </div>
        <div className="space-y-3 p-6">
          <p className="text-sm text-slate-600">Tap any section to expand. Each includes a real-world workflow example.</p>
          {sections.map((s) => (
            <div key={s.key} className="rounded-2xl border border-slate-200 bg-white">
              <button
                data-testid={`training-section-${s.key}`}
                onClick={() => setOpen(open === s.key ? "" : s.key)}
                className="flex w-full items-center justify-between px-4 py-3 text-left"
              >
                <span className="text-sm font-bold text-[#1E1E1E]">{s.title}</span>
                <span className="text-xs font-bold text-[#C4A56B]">{open === s.key ? "▲" : "▼"}</span>
              </button>
              {open === s.key && (
                <div className="space-y-3 border-t border-slate-100 px-4 py-3 text-sm text-slate-700">
                  {s.body.map((p, idx) => (
                    <p key={idx}>{p}</p>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="mt-4 rounded-2xl bg-[#FBF5E7] p-4 text-xs text-slate-700">
            <div className="font-bold text-[#1E1E1E]">Daily rhythm</div>
            <ol className="mt-2 list-decimal space-y-1 pl-5">
              <li>8am — clear Today tab (Overdue → Due Today → Hot Leads).</li>
              <li>10am — work Transactions tab; tick checklist items, advance any stage that's ready.</li>
              <li>2pm — react to any Hot Lead alerts within 5 minutes.</li>
              <li>5pm — spend 15 min in Leads tab, refresh notes and drag stale cards forward.</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}


function TeamTab() {
  const [agents, setAgents] = useState([]);
  const [overview, setOverview] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", tempPassword: "", defaultSplitPct: 70 });
  const [error, setError] = useState("");

  const reload = async () => {
    try {
      const [a, o] = await Promise.all([
        axios.get(`${API}/admin/agents`),
        axios.get(`${API}/admin/agents/team-overview`),
      ]);
      setAgents(a.data || []);
      setOverview(o.data || []);
    } catch (err) { console.error(err); }
  };
  useEffect(() => { reload(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await axios.post(`${API}/admin/agents`, form);
      setShowAdd(false);
      setForm({ name: "", email: "", phone: "", tempPassword: "", defaultSplitPct: 70 });
      await reload();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this agent? They won't be able to log in.")) return;
    await axios.delete(`${API}/admin/agents/${id}`);
    await reload();
  };

  const resetPassword = async (id, name) => {
    const newPw = window.prompt(`Set a new temporary password for ${name}. They'll be emailed the new password and forced to change it on next login. Minimum 8 characters.`);
    if (!newPw) return;
    if (newPw.trim().length < 8) { alert("Password must be at least 8 characters."); return; }
    try {
      await axios.post(`${API}/admin/agents/${id}/reset-password`, { newPassword: newPw.trim() });
      alert(`Password reset. ${name} has been emailed the new temp password.`);
    } catch (err) {
      alert(err?.response?.data?.detail || "Reset failed.");
    }
  };

  return (
    <div data-testid="team-tab" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[#141414] p-5 text-[#E0D2A8]">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#C4A56B]">Team · Agents on Irving REG</div>
          <div className="mt-1 text-lg font-bold">{agents.filter(a => a.active).length} active agent{agents.filter(a => a.active).length === 1 ? "" : "s"}</div>
        </div>
        <button data-testid="add-agent-btn" onClick={() => setShowAdd(true)} className="rounded-full bg-[#C4A56B] px-5 py-2 text-sm font-bold text-[#1E1E1E]">+ Add Agent</button>
      </div>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {overview.map((o) => (
          <div key={o.agent.id} data-testid={`agent-card-${o.agent.id}`} className={`rounded-2xl border p-5 ${o.agent.active ? "border-[#C4A56B]/30 bg-white" : "border-slate-200 bg-slate-50 opacity-60"}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-[#1E1E1E]">{o.agent.name}</div>
                <div className="text-xs text-slate-500">{o.agent.email}</div>
                {o.agent.phone && <div className="text-xs text-slate-500">{o.agent.phone}</div>}
              </div>
              <div className="flex items-center gap-3">
                <button data-testid={`reset-pw-agent-${o.agent.id}`} onClick={() => resetPassword(o.agent.id, o.agent.name)} className="text-xs font-bold text-[#1E1E1E] hover:underline">Reset password</button>
                <button data-testid={`deactivate-agent-${o.agent.id}`} onClick={() => deactivate(o.agent.id)} className="text-xs font-bold text-red-700 hover:underline">Deactivate</button>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
              <div className="rounded-lg bg-slate-100 p-2">
                <div className="font-serif text-xl text-[#1E1E1E]">{o.openLeads}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Open Leads</div>
              </div>
              <div className="rounded-lg bg-slate-100 p-2">
                <div className="font-serif text-xl text-[#1E1E1E]">{o.openTransactions}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">In Pipeline</div>
              </div>
              <div className="rounded-lg bg-[#FBF5E7] p-2">
                <div className="font-serif text-xl text-[#1E1E1E]">${Math.round(o.gciYtd).toLocaleString()}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">GCI YTD</div>
              </div>
            </div>
            <div className="mt-3 text-[11px] text-slate-500">Default split: {o.agent.defaultSplitPct}% · Closed deals: {o.closedTransactions}</div>
          </div>
        ))}
        {overview.length === 0 && (
          <div className="col-span-full rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            No agents yet. Click "+ Add Agent" above to invite your first team member.
          </div>
        )}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="add-agent-modal">
          <form onSubmit={submit} className="w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between bg-[#1E1E1E] px-6 py-4 text-white">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C4A56B]">New Agent</div>
                <div className="text-lg font-bold">Add someone to your team</div>
              </div>
              <button type="button" onClick={() => setShowAdd(false)} className="text-2xl leading-none text-white/70">×</button>
            </div>
            <div className="space-y-3 p-6 text-[#1E1E1E]">
              <label className="block text-xs">
                <span className="mb-1 block font-bold">Full Name *</span>
                <input data-testid="agent-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm" />
              </label>
              <label className="block text-xs">
                <span className="mb-1 block font-bold">Email *</span>
                <input data-testid="agent-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm" />
              </label>
              <label className="block text-xs">
                <span className="mb-1 block font-bold">Phone</span>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm" />
              </label>
              <label className="block text-xs">
                <span className="mb-1 block font-bold">Temporary Password *</span>
                <input data-testid="agent-password-input" required minLength={8} value={form.tempPassword} onChange={(e) => setForm({ ...form, tempPassword: e.target.value })} placeholder="Min 8 characters. They'll change it on first login." className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm" />
              </label>
              <label className="block text-xs">
                <span className="mb-1 block font-bold">Default Commission Split % (agent's cut)</span>
                <input type="number" min="0" max="100" step="1" value={form.defaultSplitPct} onChange={(e) => setForm({ ...form, defaultSplitPct: Number(e.target.value) })} className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm" />
              </label>
              {error && <div className="rounded-lg bg-red-50 p-2 text-xs text-red-700">{error}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowAdd(false)} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-bold">Cancel</button>
                <button data-testid="agent-submit-btn" type="submit" className="rounded-xl bg-[#1E1E1E] px-6 py-2 text-sm font-bold text-white">Create Agent</button>
              </div>
              <p className="text-[10px] text-slate-500">The agent signs in at <strong>/agent-portal</strong> with this email and temp password. They'll be prompted to change it on first login.</p>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}


function SellerEmailsPreviewModal({ onClose }) {
  const [previews, setPreviews] = useState([]);
  const [sampleContext, setSampleContext] = useState({});
  const [loading, setLoading] = useState(true);
  const [openStage, setOpenStage] = useState(1);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/transactions/meta/seller-emails`)
      .then((res) => {
        setPreviews(res.data?.previews || []);
        setSampleContext(res.data?.sampleContext || {});
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || err?.message || "Could not load previews.");
        setLoading(false);
      });
  }, []);

  const openPreview = previews.find((p) => p.stage === openStage);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="seller-emails-preview-modal">
      <div className="flex h-[85vh] w-full max-w-5xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="w-64 shrink-0 overflow-y-auto bg-[#1E1E1E] p-4 text-[#E0D2A8]">
          <div className="mb-3 text-[10px] font-bold uppercase tracking-[0.18em] text-[#C4A56B]">Seller Concierge · 8 emails</div>
          <div className="mb-4 text-xs text-[#E0D2A8]/70">These fire automatically for every seller transaction. Sample data shown for preview.</div>
          {loading && <div className="text-sm text-[#E0D2A8]/60">Loading…</div>}
          {error && <div className="rounded-lg bg-red-950/50 px-3 py-2 text-xs text-red-200">{error}</div>}
          <div className="space-y-1">
            {previews.map((p) => (
              <button
                key={p.stage}
                data-testid={`preview-stage-${p.stage}`}
                onClick={() => setOpenStage(p.stage)}
                className={`w-full rounded-lg px-3 py-2 text-left text-xs transition ${openStage === p.stage ? "bg-[#C4A56B] text-[#1E1E1E]" : "text-[#E0D2A8]/85 hover:bg-white/5"}`}
              >
                <div className="font-bold">Stage {p.stage}</div>
                <div className={`mt-0.5 text-[11px] ${openStage === p.stage ? "text-[#1E1E1E]/80" : "text-[#E0D2A8]/60"}`}>{p.stageName}</div>
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-1 flex-col">
          <div className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C4A56B]">Email preview</div>
              <div className="text-lg font-bold text-[#1E1E1E]">Stage {openStage} · {openPreview?.stageName || "—"}</div>
            </div>
            <button data-testid="seller-emails-close" onClick={onClose} className="text-2xl leading-none text-slate-400 hover:text-slate-700">×</button>
          </div>
          <div className="flex-1 overflow-y-auto bg-slate-50 p-6">
            {openPreview && (
              <>
                <div className="rounded-2xl border border-[#C4A56B]/30 bg-[#FBF5E7] px-4 py-3 text-xs text-[#1E1E1E]">
                  <strong>When it sends:</strong> {openPreview.trigger}
                </div>
                <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="border-b border-slate-100 bg-slate-50 px-6 py-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">From</div>
                    <div className="text-sm text-[#1E1E1E]">Torey Irving &lt;torey@irvingrealestategroup.com&gt;</div>
                    <div className="mt-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">To</div>
                    <div className="text-sm text-[#1E1E1E]">{sampleContext.first_name} &lt;seller@example.com&gt;</div>
                    <div className="mt-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">Subject</div>
                    <div className="text-sm font-bold text-[#1E1E1E]">{openPreview.subject}</div>
                  </div>
                  <pre className="whitespace-pre-wrap px-6 py-5 font-serif text-sm leading-relaxed text-[#1E1E1E]">{openPreview.body}</pre>
                </div>
                <div className="mt-4 rounded-xl bg-slate-100 p-3 text-[11px] text-slate-600">
                  <strong>Sample data used:</strong> Address = "{sampleContext.address}" · Target close = {sampleContext.target_close} · Option end = {sampleContext.option_end} · Title = {sampleContext.title_company} · Lender = {sampleContext.lender}. Real emails use each seller's actual contract data.
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


const CONTACT_TYPE_OPTIONS = ["Past Client", "Sphere", "Prospect"];

function AddContactModal({ cities, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", city: "", contactType: "Sphere",
    closingDate: "", propertyAddress: "", relationship: "", personalNote: "", birthday: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.email.includes("@")) {
      setError("Email is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API}/subscribers`, form);
      onSaved?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't add contact.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 md:items-center" data-testid="add-contact-modal">
      <form onSubmit={submit} className="w-full max-w-2xl rounded-[2rem] bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-[#1E1E1E]">Add a contact</h2>
          <button type="button" data-testid="add-contact-close" onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <p className="mt-1 text-sm text-slate-500">Past client, sphere contact (friend/neighbor/pro), or prospect.</p>
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <label className="block">
            <div className="text-xs font-bold text-slate-600">Contact type</div>
            <select data-testid="add-contact-type" value={form.contactType} onChange={(e) => update("contactType", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm">
              {CONTACT_TYPE_OPTIONS.map((t) => <option key={t}>{t}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-xs font-bold text-slate-600">Name</div>
            <input data-testid="add-contact-name" value={form.name} onChange={(e) => update("name", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" placeholder="Full name" />
          </label>
          <label className="block">
            <div className="text-xs font-bold text-slate-600">Email *</div>
            <input data-testid="add-contact-email" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" />
          </label>
          <label className="block">
            <div className="text-xs font-bold text-slate-600">Phone</div>
            <input data-testid="add-contact-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" placeholder="(817) 555-0100" />
          </label>
          <label className="block">
            <div className="text-xs font-bold text-slate-600">City</div>
            <select value={form.city} onChange={(e) => update("city", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm">
              <option value="">DFW (any)</option>
              {cities.map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-xs font-bold text-slate-600">Birthday (optional)</div>
            <input data-testid="add-contact-birthday" type="date" value={form.birthday} onChange={(e) => update("birthday", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" />
          </label>
          {form.contactType === "Past Client" && (
            <>
              <label className="block">
                <div className="text-xs font-bold text-slate-600">Closing date</div>
                <input data-testid="add-contact-closingdate" type="date" value={form.closingDate} onChange={(e) => update("closingDate", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" />
              </label>
              <label className="block">
                <div className="text-xs font-bold text-slate-600">Property address</div>
                <input value={form.propertyAddress} onChange={(e) => update("propertyAddress", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" placeholder="123 Main St, Fort Worth TX" />
              </label>
            </>
          )}
          {form.contactType === "Sphere" && (
            <label className="block md:col-span-2">
              <div className="text-xs font-bold text-slate-600">Relationship</div>
              <input data-testid="add-contact-relationship" value={form.relationship} onChange={(e) => update("relationship", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" placeholder="College friend / Title rep at Stewart / Neighbor / CPA" />
            </label>
          )}
          <label className="block md:col-span-2">
            <div className="text-xs font-bold text-slate-600">Personal note (just for you)</div>
            <input data-testid="add-contact-note" value={form.personalNote} onChange={(e) => update("personalNote", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" placeholder="Met at Becky's wedding 2023" />
          </label>
        </div>
        {error && <div data-testid="add-contact-error" className="mt-3 text-sm text-red-600">{error}</div>}
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-700">Cancel</button>
          <button type="submit" data-testid="add-contact-save" disabled={saving} className="rounded-xl bg-[#1E1E1E] px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60">
            {saving ? "Saving…" : "Save contact"}
          </button>
        </div>
      </form>
    </div>
  );
}

function CsvImportModal({ onClose, onImported }) {
  const [text, setText] = useState("name,email,phone,city,contactType,closingDate,propertyAddress,relationship,personalNote\nJane Doe,jane@x.com,8175550100,Fort Worth,Past Client,2023-06-15,123 Main St Fort Worth TX,,\nJohn Friend,john@x.com,,Dallas,Sphere,,,College friend,Met at UT");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const parseCsv = (raw) => {
    const lines = raw.split(/\r?\n/).filter((line) => line.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].split(",").map((h) => h.trim());
    return lines.slice(1).map((line) => {
      const cells = line.split(",").map((c) => c.trim());
      const row = {};
      headers.forEach((h, i) => { row[h] = cells[i] || ""; });
      return row;
    }).filter((r) => r.email);
  };

  const submit = async () => {
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const rows = parseCsv(text);
      if (!rows.length) {
        setError("No rows parsed. Make sure your first line is headers and 'email' is one of them.");
        setBusy(false);
        return;
      }
      const r = await axios.post(`${API}/subscribers/import-csv`, { rows });
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 md:items-center" data-testid="csv-import-modal">
      <div className="w-full max-w-3xl rounded-[2rem] bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-[#1E1E1E]">Bulk import contacts</h2>
          <button onClick={onClose} data-testid="csv-import-close" className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <p className="mt-1 text-sm text-slate-500">Paste CSV with these headers (any order): <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">name, email, phone, city, contactType, closingDate, propertyAddress, relationship, personalNote, birthday</code>. Only <strong>email</strong> is required. Duplicates are skipped automatically.</p>
        <textarea
          data-testid="csv-import-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={10}
          className="mt-4 w-full rounded-xl border border-slate-300 px-3 py-2.5 font-mono text-xs"
        />
        {error && <div data-testid="csv-import-error" className="mt-3 text-sm text-red-600">{error}</div>}
        {result && (
          <div data-testid="csv-import-result" className="mt-4 rounded-2xl bg-emerald-50 p-4 text-sm text-emerald-900 ring-1 ring-emerald-200">
            ✓ Added {result.added}, skipped {result.skipped}{result.errors?.length > 0 ? ` (${result.errors.length} errors. see console)` : ""}.
          </div>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-700">Close</button>
          <button onClick={result ? onImported : submit} disabled={busy} data-testid="csv-import-submit" className="rounded-xl bg-[#1E1E1E] px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60">
            {busy ? "Importing…" : result ? "Done" : "Import"}
          </button>
        </div>
      </div>
    </div>
  );
}

function HotLeadAlerts() {
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/lead-alerts?unacknowledged=true&limit=10`);
        if (!cancelled) setAlerts(r.data || []);
      } catch (err) {
        // Auth not loaded yet or backend error. silent.
      }
    };
    poll();
    const t = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const ack = async (id) => {
    try {
      await axios.post(`${API}/lead-alerts/${id}/ack`);
      setAlerts((cur) => cur.filter((a) => a.id !== id));
    } catch (err) {
      devError("Failed to ack alert", err);
    }
  };

  if (alerts.length === 0) return null;
  return (
    <div data-testid="hot-lead-alerts" className="fixed right-4 top-20 z-40 w-80 space-y-2">
      {alerts.slice(0, 3).map((a) => (
        <div key={a.id} data-testid={`hot-alert-${a.id}`} className="rounded-2xl bg-white p-4 shadow-2xl ring-2 ring-red-400">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="inline-block h-2.5 w-2.5 animate-pulse rounded-full bg-red-500" />
              <span className="text-[10px] font-bold uppercase tracking-wider text-red-600">{a.score} · {a.type}</span>
            </div>
            <button onClick={() => ack(a.id)} data-testid={`hot-alert-ack-${a.id}`} className="text-xs text-slate-400 hover:text-slate-700">Dismiss</button>
          </div>
          <div className="mt-2 text-base font-bold text-[#1E1E1E]">{a.name || "(no name)"}</div>
          <div className="mt-0.5 text-xs text-slate-500">
            {a.phone || "no phone"} · {a.location || "DFW"} · {a.timeline}
          </div>
          <div className="mt-2 text-xs leading-5 text-slate-700">{a.recommendedNextAction}</div>

          <div className="mt-3 rounded-xl bg-[#fff8e7] p-3 ring-1 ring-[#C4A56B]/40" data-testid={`hot-alert-script-${a.id}`}>
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#7a5a00]">
              <img src="/logo-new.png" alt="" className="h-3.5 w-3.5 rounded object-contain" />
              Jason · suggested opener
            </div>
            {a.callScriptStatus === "generating" || !a.callScript ? (
              <div className="mt-1.5 flex items-center gap-2 text-xs italic text-slate-500">
                <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-[#C4A56B]" />
                Writing your opener…
              </div>
            ) : (
              <div className="mt-1.5 text-xs leading-5 text-slate-800">{a.callScript}</div>
            )}
          </div>

          {a.phone && (
            <a href={`tel:${a.phone}`} className="mt-3 block w-full rounded-xl bg-[#C4A56B] px-3 py-2 text-center text-sm font-bold text-[#1E1E1E] hover:bg-[#A68B51]">
              Call now: {a.phone}
            </a>
          )}
        </div>
      ))}
    </div>
  );
}

function LeadModal({ lead, onClose, updateLeadNotes, bookConsultation, sendEmailFollowUp, sendSmsFollowUp, createTransaction, openTransaction, deleteLead }) {
  const [bookingDate, setBookingDate] = useState("");
  const [bookingTime, setBookingTime] = useState("");
  const [bookingNotes, setBookingNotes] = useState("");
  const [showContractForm, setShowContractForm] = useState(false);
  const [contractData, setContractData] = useState({
    property_address: "",
    price: "",
    contract_date: "",
    lender: "",
  });
  const defaultScript = followUpScript(lead);
  const [emailSubject, setEmailSubject] = useState(`${lead.type === "Seller" ? "Home value" : "Next step"} from Irving Real Estate Group`);
  const [emailBody, setEmailBody] = useState(defaultScript);
  const [smsBody, setSmsBody] = useState(defaultScript);
  const [favs, setFavs] = useState([]);
  const [views, setViews] = useState([]);
  const [placesterActivity, setPlacesterActivity] = useState([]);
  const [agents, setAgents] = useState([]);
  const [assignedAgentId, setAssignedAgentId] = useState(lead.assignedAgentId || "");
  const [assignStatus, setAssignStatus] = useState(""); // "", "saving", "saved", "error"
  useEffect(() => { axios.get(`${API}/admin/agents`).then((r) => setAgents(r.data || [])).catch(() => {}); }, []);
  const dirty = assignedAgentId !== (lead.assignedAgentId || "");
  const saveAssign = async () => {
    setAssignStatus("saving");
    try {
      await axios.patch(`${API}/admin/leads/${lead.id}/assign`, { agentId: assignedAgentId });
      setAssignStatus("saved");
      lead.assignedAgentId = assignedAgentId; // keep local ref fresh
      setTimeout(() => setAssignStatus(""), 2000);
    } catch (err) { console.error(err); setAssignStatus("error"); }
  };

  useEffect(() => {
    if (!lead?.buyerId) return;
    axios.get(`${API}/buyers/${lead.buyerId}/favorites`).then((res) => setFavs(res.data?.favorites || [])).catch(() => {});
    axios.get(`${API}/buyers/${lead.buyerId}/views?limit=10`).then((res) => setViews(res.data?.views || [])).catch(() => {});
  }, [lead?.buyerId]);

  useEffect(() => {
    if (!lead?.id) return;
    axios.get(`${API}/leads/${lead.id}/placester-activity`)
      .then((res) => setPlacesterActivity(res.data || []))
      .catch(() => {});
  }, [lead?.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="lead-modal">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-auto rounded-[2rem] bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className={`mb-3 inline-flex rounded-full px-4 py-2 text-sm font-bold ${scoreColor(lead.score)}`}>{lead.score}</div>
            <h2 className="text-3xl font-bold">{lead.name || "Lead Detail"}</h2>
            <p className="mt-2 text-slate-600">{lead.conversationSummary}</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <button data-testid="lead-modal-close-btn" onClick={onClose} className="rounded-full bg-slate-100 px-4 py-2 font-bold">Close</button>
            <button
              data-testid="lead-modal-delete-btn"
              onClick={() => {
                if (window.confirm(`Permanently delete this lead (${lead.name || lead.email || "Anonymous"})?\n\nThis also removes their alerts, follow-ups, and value reports.`)) {
                  deleteLead(lead.id);
                }
              }}
              className="rounded-full bg-red-100 px-4 py-2 text-sm font-bold text-red-800 hover:bg-red-200"
            >
              Delete lead
            </button>
          </div>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <Info label="Type" value={lead.type} />
          <Info label="Phone" value={lead.phone} />
          <Info label="Email" value={lead.email} />
          <Info label="Timeline" value={lead.timeline} />
          <Info label="Pipeline Stage" value={lead.pipelineStage} />
          <Info label="Source" value={lead.source} />
        </div>

        <div className="mt-4 rounded-2xl border border-[#C4A56B]/30 bg-[#FBF5E7] p-3">
          <div className="mb-1 text-xs font-bold uppercase tracking-wide text-[#1E1E1E]">Assigned to</div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              data-testid="lead-assign-select"
              value={assignedAgentId}
              onChange={(e) => setAssignedAgentId(e.target.value)}
              className="flex-1 min-w-[220px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-[#1E1E1E]"
            >
              <option value="">Unassigned (broker only)</option>
              {agents.filter(a => a.active).map((a) => (
                <option key={a.id} value={a.id}>{a.name} · {a.email}</option>
              ))}
            </select>
            <button
              data-testid="lead-assign-save"
              onClick={saveAssign}
              disabled={!dirty || assignStatus === "saving"}
              className="rounded-lg bg-[#1E1E1E] px-4 py-2 text-xs font-bold text-white disabled:opacity-40"
            >
              {assignStatus === "saving" ? "Saving…" : assignStatus === "saved" ? "Saved ✓" : "Save"}
            </button>
          </div>
          {assignStatus === "saved" && <div className="mt-1 text-[11px] text-green-700">Agent notified by email.</div>}
          {assignStatus === "error" && <div className="mt-1 text-[11px] text-red-700">Save failed. Try again.</div>}
        </div>

        {(favs.length > 0 || views.length > 0) && (
          <div className="mt-6 rounded-3xl border border-[#C4A56B]/40 bg-[#fff8e7] p-5">
            <div className="font-bold">Buyer's listing activity</div>
            {favs.length > 0 && (
              <div className="mt-3">
                <div className="text-xs font-bold uppercase text-slate-500">Favorited ({favs.length})</div>
                <ul className="mt-1 space-y-1 text-sm">{favs.map((f) => <li key={f.listingKey}>★ {f.address}. {money(f.listPrice)}</li>)}</ul>
              </div>
            )}
            {views.length > 0 && (
              <div className="mt-3">
                <div className="text-xs font-bold uppercase text-slate-500">Recent views</div>
                <ul className="mt-1 space-y-1 text-sm">{views.slice(0, 5).map((v) => <li key={v.id}>· {v.address}. {money(v.listPrice)}</li>)}</ul>
              </div>
            )}
          </div>
        )}

        {placesterActivity.length > 0 && (
          <div className="mt-6 rounded-3xl border border-[#1E1E1E] bg-[#141414] p-5 text-[#E0D2A8]" data-testid="placester-activity">
            <div className="flex items-center justify-between">
              <div className="font-bold text-[#C4A56B]">Placester activity</div>
              <span className="rounded-full bg-[#C4A56B] px-2 py-0.5 text-[10px] font-bold text-[#1E1E1E]">{placesterActivity.length} event{placesterActivity.length === 1 ? "" : "s"}</span>
            </div>
            <ul className="mt-3 space-y-2 text-sm">
              {placesterActivity.slice(0, 8).map((a) => (
                <li key={a.id} className="flex items-start gap-2">
                  <span className="mt-0.5 rounded-full bg-[#C4A56B]/20 px-2 py-0.5 text-[10px] font-bold uppercase text-[#C4A56B]">
                    {(a.activityType || "view").replace(/_/g, " ")}
                  </span>
                  <div className="flex-1">
                    <div>{a.listingAddress || a.listingUrl || "Property"}</div>
                    <div className="text-xs text-[#E0D2A8]/60">
                      {a.listingPrice ? `${a.listingPrice} · ` : ""}
                      {a.viewedAt ? new Date(a.viewedAt).toLocaleString() : ""}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-6 rounded-3xl bg-[#f6f2ea] p-5">
          <div className="font-bold">Recommended Next Action</div>
          <p className="mt-2 text-slate-700">{lead.recommendedNextAction}</p>
        </div>

        {lead.transactionId ? (
          <div className="mt-4 rounded-3xl border-2 border-[#C4A56B] bg-[#fff8e7] p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-bold text-[#1E1E1E]">This lead is under contract.</div>
                <div className="mt-1 text-xs text-slate-600">View the transaction concierge page to manage stages, checklists, and Jason's transaction-aware chat.</div>
              </div>
              <button data-testid="open-transaction-btn" onClick={() => openTransaction(lead.transactionId)} className="rounded-2xl bg-[#1E1E1E] px-4 py-2 text-sm font-bold text-white">Open Transaction →</button>
            </div>
          </div>
        ) : (
          <div className="mt-4 rounded-3xl border border-slate-200 p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-bold">Move to Under Contract</div>
                <div className="mt-1 text-xs text-slate-600">Start the transaction concierge: 8-stage tracker, auto checklists, Jason answers transaction-specific questions for the buyer.</div>
              </div>
              {!showContractForm && <button data-testid="move-under-contract-btn" onClick={() => setShowContractForm(true)} className="rounded-2xl bg-[#C4A56B] px-4 py-2 text-sm font-bold text-[#1E1E1E]">Move to Under Contract</button>}
            </div>
            {showContractForm && (
              <div className="mt-4 grid gap-2 md:grid-cols-2">
                <input data-testid="txn-form-address" value={contractData.propertyAddress} onChange={(e) => setContractData({ ...contractData, propertyAddress: e.target.value })} placeholder="Property address" className="md:col-span-2 rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                <input data-testid="txn-form-price" value={contractData.listPrice} onChange={(e) => setContractData({ ...contractData, listPrice: e.target.value })} placeholder="Sale price" type="number" className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                <input data-testid="txn-form-date" value={contractData.contractDate} onChange={(e) => setContractData({ ...contractData, contractDate: e.target.value })} type="date" className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                <input data-testid="txn-form-lender" value={contractData.lenderName} onChange={(e) => setContractData({ ...contractData, lenderName: e.target.value })} placeholder="Lender name" className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                <input data-testid="txn-form-title" value={contractData.titleCompany} onChange={(e) => setContractData({ ...contractData, titleCompany: e.target.value })} placeholder="Title company" className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                <div className="md:col-span-2 flex gap-2">
                  <button data-testid="txn-form-create-btn" onClick={() => createTransaction(lead, contractData)} disabled={!contractData.propertyAddress || !contractData.contractDate} className="flex-1 rounded-xl bg-[#1E1E1E] px-4 py-2 text-sm font-bold text-white disabled:opacity-50">Create Transaction</button>
                  <button onClick={() => setShowContractForm(false)} className="rounded-xl border border-slate-300 px-4 py-2 text-sm">Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <div className="rounded-3xl border border-slate-200 p-5">
            <div className="flex items-center justify-between"><div className="font-bold">Book Consultation</div><span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800">LIVE</span></div>
            <input data-testid="booking-date-input" type="date" value={bookingDate} onChange={(e) => setBookingDate(e.target.value)} className="mt-3 w-full rounded-xl border border-slate-300 px-3 py-2" />
            <input data-testid="booking-time-input" type="time" value={bookingTime} onChange={(e) => setBookingTime(e.target.value)} className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2" />
            <textarea data-testid="booking-notes-input" value={bookingNotes} onChange={(e) => setBookingNotes(e.target.value)} placeholder="Internal notes for the call" className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
            <button data-testid="book-consultation-btn" onClick={() => bookConsultation(lead, { preferredDate: bookingDate, preferredTime: bookingTime, notes: bookingNotes })} className="mt-3 w-full rounded-xl bg-[#1E1E1E] px-4 py-2 text-sm font-bold text-white">Book Consultation</button>
          </div>
          <div className="rounded-3xl border border-slate-200 p-5">
            <div className="flex items-center justify-between"><div className="font-bold">Email Follow-Up</div><span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800">LIVE</span></div>
            <input data-testid="email-subject-input" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} className="mt-3 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
            <textarea data-testid="email-body-input" value={emailBody} onChange={(e) => setEmailBody(e.target.value)} className="mt-2 min-h-24 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
            <div className="mt-2 flex items-center gap-2 rounded-lg bg-[#fff8e7] px-2 py-1.5 text-[10px] text-slate-600">
              <img src="/logo-new.png" alt="" className="h-5 w-5 rounded bg-black object-contain p-0.5" />
              Sends with branded IREG header, logo & signature
            </div>
            <button data-testid="send-email-btn" onClick={() => sendEmailFollowUp(lead, emailSubject, emailBody)} disabled={!lead.email} className="mt-3 w-full rounded-xl bg-[#1E1E1E] px-4 py-2 text-sm font-bold text-white">Send Email</button>
          </div>
          <div className="rounded-3xl border border-slate-200 p-5">
            <div className="flex items-center justify-between"><div className="font-bold">SMS Follow-Up</div><span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800">LIVE</span></div>
            <textarea data-testid="sms-body-input" value={smsBody} onChange={(e) => setSmsBody(e.target.value)} className="mt-3 min-h-36 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
            <button data-testid="send-sms-btn" onClick={() => sendSmsFollowUp(lead, smsBody)} disabled={!lead.phone} className="mt-3 w-full rounded-xl bg-[#1E1E1E] px-4 py-2 text-sm font-bold text-white">Send SMS</button>
          </div>
        </div>

        <div className="mt-6 rounded-3xl border border-slate-200 p-5">
          <div className="font-bold">Follow-Up Script</div>
          <p className="mt-2 leading-7 text-slate-700">{defaultScript}</p>
        </div>
        <div className="mt-6">
          <label className="font-bold">Notes</label>
          <textarea data-testid="lead-notes-input" value={lead.notes || ""} onChange={(e) => updateLeadNotes(lead.id, e.target.value)} className="mt-2 min-h-28 w-full rounded-2xl border border-slate-300 p-4 outline-none focus:border-[#C4A56B]" />
        </div>
      </div>
    </div>
  );
}

function flowFields(type) {
  if (type === "Buyer") return [
    { name: "name", label: "Full Name" }, { name: "phone", label: "Phone" }, { name: "email", label: "Email" },
    { name: "location", label: "Desired Area", placeholder: "Fort Worth, Arlington, Dallas, Irving..." },
    { name: "budget", label: "Price Range" },
    { name: "timeline", label: "Timeline", type: "select", options: ["0-90 days", "3-6 months", "6+ months", "Just exploring"] },
    { name: "preapprovalStatus", label: "Preapproval Status", type: "select", options: ["Preapproved", "Talking to a lender", "Not started", "Not sure"] },
    { name: "currentHousing", label: "Current Housing", type: "select", options: ["Renting", "Own and need to sell", "Living with family", "Relocating"] },
    { name: "mustHaves", label: "Must-Haves", type: "textarea", large: true },
    { name: "concern", label: "Biggest Concern", type: "textarea", large: true },
    { name: "consultationInterest", label: "Consultation Interest", type: "select", options: ["Yes", "Maybe", "Not yet"] },
  ];
  if (type === "Seller") return [
    { name: "name", label: "Full Name" }, { name: "phone", label: "Phone" }, { name: "email", label: "Email" },
    { name: "propertyAddress", label: "Property Address" },
    { name: "timeline", label: "Selling Timeline", type: "select", options: ["0-90 days", "3-6 months", "6+ months", "Just exploring"] },
    { name: "homeCondition", label: "Home Condition", type: "select", options: ["Excellent", "Good", "Needs minor updates", "Needs major updates", "Not sure"] },
    { name: "reason", label: "Reason for Selling", type: "textarea", large: true },
    { name: "desiredPrice", label: "Desired Price" },
    { name: "sellingBuyingNext", label: "Selling / Buying Next", type: "select", options: ["Selling only", "Selling then buying", "Buying then selling", "Not sure"] },
    { name: "consultationInterest", label: "Home Value Consultation Interest", type: "select", options: ["Yes", "Maybe", "Not yet"] },
  ];
  return [
    { name: "name", label: "Full Name" }, { name: "phone", label: "Phone" }, { name: "email", label: "Email" },
    { name: "monthlyRent", label: "Monthly Rent" }, { name: "leaseEndDate", label: "Lease End Date" },
    { name: "location", label: "Desired Area" },
    { name: "creditConfidence", label: "Credit Confidence", type: "select", options: ["Strong", "Okay", "Needs work", "Not sure"] },
    { name: "savingsRange", label: "Savings Range", type: "select", options: ["$0-$2,500", "$2,500-$7,500", "$7,500-$15,000", "$15,000+", "Prefer not to say"] },
    { name: "timeline", label: "Buying Timeline", type: "select", options: ["0-90 days", "3-6 months", "6+ months", "Just exploring"] },
    { name: "concern", label: "Biggest Concern", type: "textarea", large: true },
  ];
}

function scoreColor(score) {
  if (score === "Hot Lead") return "bg-red-100 text-red-800";
  if (score === "Warm Lead") return "bg-amber-100 text-amber-800";
  if (score === "Nurture Lead") return "bg-blue-100 text-blue-800";
  if (score === "Incomplete Lead") return "bg-slate-200 text-slate-700";
  return "bg-emerald-100 text-emerald-800";
}


function TodayPanel({ today, loading, refresh, openLead }) {
  if (loading && !today) {
    return <div data-testid="today-loading" className="rounded-2xl bg-white p-8 text-center text-slate-500">Loading today's workspace…</div>;
  }
  if (!today) {
    return (
      <div className="rounded-2xl bg-white p-8 text-center text-slate-500" data-testid="today-empty-load">
        <p>No today data yet.</p>
        <button data-testid="today-reload-btn" onClick={refresh} className="mt-3 rounded-full bg-[#1E1E1E] px-4 py-2 text-sm font-bold text-white">Load</button>
      </div>
    );
  }
  const c = today.counts || {};
  const nothingToDo = !c.newLeads && !c.hotAlerts && !c.goingCold && !c.overdue && !c.consultations && !c.celebrations;

  return (
    <div className="space-y-6" data-testid="today-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[#1E1E1E] p-5 text-white">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C4A56B]">Today · {new Date(today.asOf).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
          <div className="mt-1 text-xl font-bold">
            {nothingToDo
              ? "Nothing on the board. send a check-in to a past client."
              : `${(c.newLeads || 0) + (c.hotAlerts || 0) + (c.overdue || 0)} action${((c.newLeads || 0) + (c.hotAlerts || 0) + (c.overdue || 0)) === 1 ? "" : "s"} waiting on you.`}
          </div>
        </div>
        <button data-testid="today-refresh-btn" onClick={refresh} className="rounded-full bg-[#C4A56B] px-4 py-2 text-sm font-bold text-[#1E1E1E] hover:bg-[#f0cc5c]">
          Refresh
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
        <TodayStat label="New leads" value={c.newLeads || 0} tone="new" testId="today-count-new" />
        <TodayStat label="🔥 Hot alerts" value={c.hotAlerts || 0} tone="hot" testId="today-count-hot" />
        <TodayStat label="Going cold" value={c.goingCold || 0} tone="cold" testId="today-count-cold" />
        <TodayStat label="Overdue" value={c.overdue || 0} tone="overdue" testId="today-count-overdue" />
        <TodayStat label="Consultations" value={c.consultations || 0} tone="calm" testId="today-count-consults" />
        <TodayStat label="Celebrations" value={c.celebrations || 0} tone="celebrate" testId="today-count-cele" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <TodaySection
          title="🔥 Hot leads. call now"
          testId="today-section-hot"
          items={today.hotAlerts || []}
          empty="No unacknowledged hot leads. You're caught up."
          render={(a) => (
            <div key={a.alertId} data-testid="today-hot-item" className="rounded-2xl border border-red-200 bg-red-50/50 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <button onClick={() => openLead(a.leadId)} className="font-bold text-[#1E1E1E] hover:underline">{a.name}</button>
                  <div className="text-xs text-slate-600">{a.type} · {a.score}</div>
                </div>
                <div className="text-right text-xs text-slate-500">{a.phone || a.email}</div>
              </div>
              {a.callScript && (
                <div className="mt-2 rounded-xl border border-red-300 bg-white p-3 text-sm italic text-slate-700">
                  "{a.callScript}"
                </div>
              )}
              {!a.callScript && a.callScriptStatus === "generating" && (
                <div className="mt-2 text-xs text-slate-500">Generating call script…</div>
              )}
            </div>
          )}
        />

        <TodaySection
          title="⚡ New leads. first response wins"
          testId="today-section-new"
          items={today.newLeads || []}
          empty="No brand-new leads in the last 24 hours."
          render={(l) => (
            <div key={l.id} data-testid="today-new-item" className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <button onClick={() => openLead(l.id)} className="font-bold text-[#1E1E1E] hover:underline">{l.name}</button>
                  <div className="text-xs text-slate-600">{l.type} · {l.source || "unknown source"} · {l.minutesSinceCreated} min ago</div>
                </div>
                <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-600">{l.score || "New"}</span>
              </div>
              {l.recommendedNextAction && (
                <div className="mt-2 text-xs text-slate-700">→ {l.recommendedNextAction}</div>
              )}
            </div>
          )}
        />

        <TodaySection
          title="🧊 Going cold. reheat these"
          testId="today-section-cold"
          items={today.goingCold || []}
          empty="Nothing going cold. Good work."
          render={(l) => (
            <div key={l.id} data-testid="today-cold-item" className="rounded-2xl border border-sky-200 bg-sky-50/40 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <button onClick={() => openLead(l.id)} className="font-bold text-[#1E1E1E] hover:underline">{l.name}</button>
                  <div className="text-xs text-slate-600">{l.type} · {l.score} · silent {l.daysSinceTouch}d</div>
                </div>
              </div>
              {l.recommendedNextAction && (
                <div className="mt-2 text-xs text-slate-700">→ {l.recommendedNextAction}</div>
              )}
            </div>
          )}
        />

        <TodaySection
          title="⏰ Overdue follow-ups"
          testId="today-section-overdue"
          items={today.overdue || []}
          empty="Zero overdue. All caught up."
          render={(l) => (
            <div key={l.id} data-testid="today-overdue-item" className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <button onClick={() => openLead(l.id)} className="font-bold text-[#1E1E1E] hover:underline">{l.name}</button>
                  <div className="text-xs text-slate-600">{l.type} · {l.score} · SLA {l.slaDays}d, {l.daysOverdue}d past</div>
                </div>
              </div>
            </div>
          )}
        />

        <TodaySection
          title="📅 Consultations · today & tomorrow"
          testId="today-section-consults"
          items={today.consultations || []}
          empty="No consultations booked today or tomorrow."
          render={(c2) => (
            <div key={c2.id} data-testid="today-consult-item" className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <button onClick={() => openLead(c2.leadId)} className="font-bold text-[#1E1E1E] hover:underline">{c2.leadName}</button>
                  <div className="text-xs text-slate-600">{c2.type} · {c2.isToday ? "Today" : "Tomorrow"} {c2.preferredTime ? `at ${c2.preferredTime}` : ""}</div>
                </div>
                <div className="text-right text-xs text-slate-500">{c2.leadPhone || c2.leadEmail}</div>
              </div>
              {c2.notes && <div className="mt-2 text-xs text-slate-700">{c2.notes}</div>}
            </div>
          )}
        />

        <TodaySection
          title="🎉 Celebrations · next 7 days"
          testId="today-section-cele"
          items={today.celebrations || []}
          empty="No birthdays or anniversaries this week."
          render={(item) => (
            <div key={`${item.kind}-${item.subscriberId}`} data-testid="today-cele-item" className="rounded-2xl border border-purple-200 bg-purple-50/40 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-bold text-[#1E1E1E]">{item.name}</div>
                  <div className="text-xs text-slate-600">
                    {item.detail} · {item.daysUntil === 0 ? "Today!" : `in ${item.daysUntil}d (${item.date})`}
                  </div>
                </div>
              </div>
              {item.propertyAddress && (
                <div className="mt-1 text-xs text-slate-500">{item.propertyAddress}</div>
              )}
            </div>
          )}
        />
      </div>
    </div>
  );
}

function TodayStat({ label, value, tone, testId }) {
  const toneClasses = {
    new: "bg-emerald-50 text-emerald-900 ring-emerald-200",
    hot: "bg-red-50 text-red-900 ring-red-200",
    cold: "bg-sky-50 text-sky-900 ring-sky-200",
    overdue: "bg-amber-50 text-amber-900 ring-amber-200",
    calm: "bg-indigo-50 text-indigo-900 ring-indigo-200",
    celebrate: "bg-purple-50 text-purple-900 ring-purple-200",
  };
  return (
    <div data-testid={testId} className={`rounded-2xl p-4 ring-1 ${toneClasses[tone] || "bg-white ring-slate-200"}`}>
      <div className="text-3xl font-bold">{value}</div>
      <div className="mt-1 text-xs font-semibold uppercase tracking-wide">{label}</div>
    </div>
  );
}

function TodaySection({ title, items, empty, render, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl bg-white p-5 shadow-sm">
      <div className="mb-3 text-sm font-bold text-[#1E1E1E]">{title}</div>
      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 p-4 text-sm text-slate-500">{empty}</div>
      ) : (
        <div className="space-y-2">{items.map(render)}</div>
      )}
    </div>
  );
}


// Phase color tokens. shared between the broker Kanban and the agent portal.
const PHASE_TONE_CLASSES = {
  hot:       "bg-red-50 ring-red-200",
  warm:      "bg-orange-50 ring-orange-200",
  nurture:   "bg-emerald-50 ring-emerald-200",
  education: "bg-slate-50 ring-slate-200",
  incomplete:"bg-yellow-50 ring-yellow-200",
  action:    "bg-sky-50 ring-sky-200",
  closing:   "bg-indigo-50 ring-indigo-200",
  closed:    "bg-purple-50 ring-purple-200",
  past:      "bg-teal-50 ring-teal-200",
  lost:      "bg-slate-100 ring-slate-300",
};

function PipelinePanel({ openLead }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [expandedPhase, setExpandedPhase] = useState(null); // null = collapsed. one at a time.

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/leads/pipeline`);
      setData(r.data);
    } catch (err) {
      devError("pipeline fetch failed", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function moveTo(leadId, targetStage) {
    if (!leadId || !targetStage) return;
    setSavingId(leadId);
    setData((prev) => {
      if (!prev) return prev;
      const next = { ...prev, buckets: { ...prev.buckets } };
      let moved = null;
      for (const key of Object.keys(next.buckets)) {
        const idx = next.buckets[key].findIndex((l) => l.id === leadId);
        if (idx !== -1) {
          moved = { ...next.buckets[key][idx], pipelineStage: targetStage };
          next.buckets[key] = next.buckets[key].filter((_, i) => i !== idx);
          break;
        }
      }
      if (moved) {
        next.buckets[targetStage] = [moved, ...(next.buckets[targetStage] || [])];
      }
      return next;
    });
    try {
      await axios.patch(`${API}/leads/${leadId}/stage`, { pipelineStage: targetStage });
    } catch (err) {
      devError("stage update failed", err);
      await load();
    } finally {
      setSavingId(null);
    }
  }

  if (loading && !data) {
    return <div data-testid="pipeline-loading" className="rounded-2xl bg-white p-8 text-center text-slate-500">Loading pipeline…</div>;
  }
  if (!data) return null;

  const phases = data.phases || [];
  const buckets = data.buckets || {};

  return (
    <div className="space-y-4" data-testid="pipeline-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[#1E1E1E] p-5 text-white">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C4A56B]">Pipeline · Kanban view</div>
          <div className="mt-1 text-xl font-bold">{data.totalLeads} lead{data.totalLeads === 1 ? "" : "s"} across {phases.length} phases</div>
          <div className="mt-1 text-xs text-slate-300">Click a phase to expand its stages. Drag any card to move it between stages.</div>
        </div>
        <button onClick={load} className="rounded-full bg-[#C4A56B] px-4 py-2 text-sm font-bold text-[#1E1E1E] hover:bg-[#f0cc5c]" data-testid="pipeline-refresh-btn">
          Refresh
        </button>
      </div>

      {/* Phase-level grid. always visible, no horizontal scroll on desktop. */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {phases.map((phase) => {
          const total = phase.stages.reduce((sum, k) => sum + (buckets[k]?.length || 0), 0);
          const isOpen = expandedPhase === phase.key;
          return (
            <button
              key={phase.key}
              data-testid={`pipeline-phase-${phase.key}`}
              onClick={() => setExpandedPhase(isOpen ? null : phase.key)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const leadId = e.dataTransfer.getData("text/plain");
                if (leadId) moveTo(leadId, phase.stages[0]);
              }}
              className={`rounded-2xl p-4 text-left ring-1 transition ${PHASE_TONE_CLASSES[phase.tone] || "bg-white ring-slate-200"} ${isOpen ? "ring-2 ring-[#1E1E1E]" : "hover:ring-[#1E1E1E]/40"}`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-700">{phase.emoji} {phase.label}</div>
                <div className="rounded-full bg-white px-2 py-0.5 text-xs font-bold text-[#1E1E1E]">{total}</div>
              </div>
              <div className="mt-1 text-[10px] text-slate-500">{phase.stages.length} stage{phase.stages.length === 1 ? "" : "s"} · tap to {isOpen ? "collapse" : "expand"}</div>
            </button>
          );
        })}
      </div>

      {/* Expanded stage board for the selected phase. Renders only that phase's stages so no scroll. */}
      {expandedPhase && (() => {
        const phase = phases.find((p) => p.key === expandedPhase);
        if (!phase) return null;
        const stageDefs = data.stages.filter((s) => phase.stages.includes(s.key));
        // Sort in the phase's declared order.
        stageDefs.sort((a, b) => phase.stages.indexOf(a.key) - phase.stages.indexOf(b.key));
        // Use a responsive grid so it fits horizontally.
        const cols = { 1: "grid-cols-1", 2: "sm:grid-cols-2", 3: "sm:grid-cols-2 lg:grid-cols-3", 4: "sm:grid-cols-2 lg:grid-cols-4", 5: "sm:grid-cols-2 lg:grid-cols-5", 6: "sm:grid-cols-2 lg:grid-cols-6" }[Math.min(stageDefs.length, 6)] || "sm:grid-cols-2 lg:grid-cols-4";
        return (
          <div className={`grid gap-3 ${cols}`} data-testid={`pipeline-phase-expanded-${phase.key}`}>
            {stageDefs.map((stage) => {
              const cards = buckets[stage.key] || [];
              return (
                <div
                  key={stage.key}
                  data-testid={`pipeline-column-${stage.key.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`rounded-2xl p-3 ring-1 ${PHASE_TONE_CLASSES[stage.tone] || "bg-white ring-slate-200"}`}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const leadId = e.dataTransfer.getData("text/plain");
                    if (leadId) moveTo(leadId, stage.key);
                  }}
                >
                  <div className="mb-3 flex items-center justify-between">
                    <div className="text-xs font-bold text-[#1E1E1E]">{stage.label}</div>
                    <div className="rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-semibold text-slate-700">{cards.length}</div>
                  </div>
                  <div className="space-y-2">
                    {cards.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-slate-300 bg-white/60 p-3 text-center text-xs text-slate-500">
                        Drop leads here
                      </div>
                    ) : cards.map((lead) => (
                      <div
                        key={lead.id}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData("text/plain", lead.id);
                          e.dataTransfer.effectAllowed = "move";
                        }}
                        onClick={() => openLead(lead.id)}
                        data-testid={`pipeline-card-${lead.id}`}
                        className={`cursor-grab rounded-xl bg-white p-3 shadow-sm ring-1 ring-slate-200 hover:ring-[#1E1E1E] ${savingId === lead.id ? "opacity-60" : ""}`}
                      >
                        <div className="min-w-0">
                          <div className="truncate text-sm font-bold text-[#1E1E1E]">{lead.name || "Anonymous"}</div>
                          <div className="text-[11px] text-slate-500">{lead.type} · {lead.score || "-"}</div>
                        </div>
                        {(lead.budget || lead.location) && (
                          <div className="mt-1 text-[11px] text-slate-600">
                            {lead.location}{lead.budget ? ` · ${lead.budget}` : ""}
                          </div>
                        )}
                        {lead.recommendedNextAction && (
                          <div className="mt-2 truncate text-[11px] italic text-slate-500">→ {lead.recommendedNextAction}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}



function ContentStudioTab() {
  const [topics, setTopics] = useState([]);
  const [todaysTopic, setTodaysTopic] = useState("");
  const [activeTopic, setActiveTopic] = useState("");
  const [city, setCity] = useState("");
  const [cities, setCities] = useState([]);
  const [script, setScript] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [copyMsg, setCopyMsg] = useState("");

  const loadAll = useCallback(async () => {
    try {
      const [t, s, h, c] = await Promise.all([
        axios.get(`${API}/content/topics`),
        axios.get(`${API}/content/saved`),
        axios.get(`${API}/content/hooks`),
        axios.get(`${API}/market/cities`),
      ]);
      setTopics(t.data?.topics || []);
      setTodaysTopic(t.data?.todaysTopic || "");
      setActiveTopic(t.data?.todaysTopic || "");
      setSaved(s.data || []);
      setHooks(h.data?.hooks || []);
      setCities(c.data?.cities || []);
    } catch (err) {
      devError("Content studio load failed", err);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const generate = useCallback(async (topicKey) => {
    setBusy(true);
    setScript(null);
    try {
      const r = await axios.post(`${API}/content/generate`, {
        topic: topicKey || activeTopic || todaysTopic,
        city,
      });
      setScript(r.data);
    } catch (err) {
      devError("Generate failed", err);
    } finally {
      setBusy(false);
    }
  }, [activeTopic, todaysTopic, city]);

  useEffect(() => {
    if (todaysTopic && !script) generate(todaysTopic);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todaysTopic]);

  const saveScript = async () => {
    if (!script) return;
    try {
      await axios.post(`${API}/content/save`, script);
      setCopyMsg("Saved to your library");
      setTimeout(() => setCopyMsg(""), 2500);
      const r = await axios.get(`${API}/content/saved`);
      setSaved(r.data || []);
    } catch (err) {
      devError("Save failed", err);
    }
  };

  const deleteSaved = async (id) => {
    try {
      await axios.delete(`${API}/content/saved/${id}`);
      setSaved((cur) => cur.filter((s) => s.id !== id));
    } catch (err) { devError("Delete failed", err); }
  };

  const copy = async (text, label = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyMsg(label);
      setTimeout(() => setCopyMsg(""), 1500);
    } catch (err) { devError("Copy failed", err); }
  };

  return (
    <div className="space-y-6" data-testid="content-studio-tab">
      <div className="rounded-[2rem] bg-white p-6 shadow-sm">
        <div className="text-sm font-semibold text-[#1E1E1E]">Content Studio</div>
        <h2 className="text-xl font-bold">Today's video script</h2>
        <p className="mt-1 text-sm text-slate-600">Jason writes fresh Reels/TikTok/Shorts scripts using real DFW data. Rotates topics daily. override anytime.</p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {topics.map((t) => (
            <button
              key={t.key}
              data-testid={`content-topic-${t.key}`}
              onClick={() => { setActiveTopic(t.key); generate(t.key); }}
              className={`rounded-full px-3 py-1.5 text-xs font-bold ${activeTopic === t.key ? "bg-[#1E1E1E] text-white" : "bg-[#f6f2ea] text-slate-700 hover:bg-[#C4A56B]/20"}`}
            >
              {t.label}{t.key === todaysTopic ? " · Today" : ""}
            </button>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select data-testid="content-city-select" value={city} onChange={(e) => setCity(e.target.value)} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm">
            <option value="">DFW (any city)</option>
            {cities.map((c) => <option key={c}>{c}</option>)}
          </select>
          <button
            data-testid="content-regenerate-btn"
            onClick={() => generate(activeTopic)}
            disabled={busy}
            className="rounded-xl bg-[#C4A56B] px-4 py-2 text-sm font-bold text-[#1E1E1E] disabled:opacity-60"
          >
            {busy ? "Writing…" : "🔄 Regenerate"}
          </button>
          {script && (
            <button
              data-testid="content-save-btn"
              onClick={saveScript}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-700"
            >
              💾 Save to library
            </button>
          )}
          {copyMsg && <span className="text-xs font-bold text-emerald-700">{copyMsg}</span>}
        </div>

        {busy && (
          <div className="mt-6 rounded-2xl bg-[#f6f2ea] p-6 text-center text-sm italic text-slate-500" data-testid="content-loading">
            Jason is writing your script…
          </div>
        )}

        {script && !busy && (
          <div className="mt-6 space-y-4" data-testid="content-script-view">
            <ScriptBlock label="🎣 Hook (first 3 sec)" text={script.hook} testId="script-hook" onCopy={copy} accent />
            <ScriptBlock label="🎬 Script" text={script.script} testId="script-body" onCopy={copy} pre />
            <ScriptBlock label="📢 CTA" text={script.cta} testId="script-cta" onCopy={copy} />
            <ScriptBlock label="📝 Caption for the post" text={script.caption} testId="script-caption" onCopy={copy} />
            <div className="rounded-2xl bg-[#f6f2ea] p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-[#7a5a00]">#️⃣ Hashtags</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(script.hashtags || []).map((h) => (
                  <span key={h} className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-[#1E1E1E] ring-1 ring-slate-200">#{h.replace(/^#/, "")}</span>
                ))}
              </div>
              <button onClick={() => copy((script.hashtags || []).map((h) => `#${h.replace(/^#/, "")}`).join(" "), "Hashtags copied")} className="mt-3 text-xs font-bold text-[#1E1E1E] hover:underline">Copy all hashtags</button>
            </div>
            <div className="rounded-2xl bg-[#f6f2ea] p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-[#7a5a00]">🎥 B-Roll shot list</div>
              <ol className="mt-2 space-y-1 text-sm text-slate-700">
                {(script.bRoll || []).map((b, i) => (
                  <li key={b} className="flex gap-2"><span className="font-bold text-slate-400">{i + 1}.</span> {b}</li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-[2rem] bg-white p-6 shadow-sm">
        <div className="text-sm font-semibold text-[#1E1E1E]">Viral Hook Library</div>
        <h2 className="text-xl font-bold">20 scroll-stopping openers</h2>
        <p className="mt-1 text-sm text-slate-600">Steal any of these as the first line of a video. Tap to copy.</p>
        <ul className="mt-4 grid gap-2 md:grid-cols-2">
          {hooks.map((h, i) => (
            <li key={h} data-testid={`viral-hook-${i}`}>
              <button
                onClick={() => copy(h, "Hook copied")}
                className="flex w-full items-start gap-2 rounded-xl bg-[#f6f2ea] p-3 text-left text-sm text-slate-700 transition hover:bg-[#C4A56B]/20"
              >
                <span className="mt-0.5 shrink-0 rounded bg-white px-1.5 py-0.5 text-[10px] font-bold text-[#7a5a00]">{i + 1}</span>
                <span>{h}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {saved.length > 0 && (
        <div className="rounded-[2rem] bg-white p-6 shadow-sm" data-testid="content-saved-library">
          <div className="text-sm font-semibold text-[#1E1E1E]">Your Library</div>
          <h2 className="text-xl font-bold">Saved scripts ({saved.length})</h2>
          <div className="mt-4 space-y-3">
            {saved.map((s) => (
              <details key={s.id} data-testid={`saved-script-${s.id}`} className="rounded-2xl bg-[#f6f2ea] p-4">
                <summary className="cursor-pointer text-sm">
                  <span className="font-bold text-[#1E1E1E]">{s.topicLabel}</span>
                  <span className="ml-2 text-xs text-slate-500">· {s.city || "DFW"} · {(s.createdAt || "").slice(0, 10)}</span>
                </summary>
                <div className="mt-3 space-y-2 text-sm">
                  <div><strong className="text-[#7a5a00]">Hook:</strong> {s.hook}</div>
                  <div className="whitespace-pre-wrap"><strong className="text-[#7a5a00]">Script:</strong>{"\n"}{s.script}</div>
                  <div><strong className="text-[#7a5a00]">CTA:</strong> {s.cta}</div>
                  <div className="text-xs text-slate-500">{(s.hashtags || []).map((h) => `#${h.replace(/^#/, "")}`).join(" ")}</div>
                </div>
                <button onClick={() => deleteSaved(s.id)} data-testid={`saved-script-delete-${s.id}`} className="mt-3 text-xs font-bold text-red-600 hover:underline">Delete</button>
              </details>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ScriptBlock({ label, text, testId, onCopy, accent, pre }) {
  return (
    <div className={`rounded-2xl p-4 ${accent ? "bg-[#fff8e7] ring-1 ring-[#C4A56B]/40" : "bg-[#f6f2ea]"}`}>
      <div className="flex items-center justify-between">
        <div className={`text-xs font-bold uppercase tracking-wider ${accent ? "text-[#7a5a00]" : "text-slate-500"}`}>{label}</div>
        <button onClick={() => onCopy(text, "Copied")} className="text-xs font-bold text-[#1E1E1E] hover:underline">Copy</button>
      </div>
      <div data-testid={testId} className={`mt-2 text-sm text-slate-800 ${pre ? "whitespace-pre-wrap leading-6" : ""}`}>{text}</div>
    </div>
  );
}

function ComplianceBox() {
  return (
    <div className="mt-6 rounded-3xl border border-[#d9c7a3] bg-white p-5 text-sm leading-6 text-slate-700" data-testid="compliance-box">
      <div className="font-bold text-slate-950">Compliance Guardrail</div>
      <p className="mt-2">Jason provides general real estate and lending education only. For specific rates, qualification decisions, or formal Loan Estimates, your licensed lender is the source of truth. Jason does not provide legal, tax, appraisal, or fair-housing-sensitive advice.</p>
    </div>
  );
}

function SignInScreen({ signInWithGoogle, admin, setView }) {
  return (
    <main className="mx-auto max-w-2xl px-4 py-12 md:px-8" data-testid="signin-view">
      <div className="mb-6 text-center">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C4A56B]">Irving Real Estate Group</div>
        <h1 className="mt-2 font-serif text-3xl md:text-4xl">Sign in</h1>
        <p className="mt-2 text-sm text-slate-600">Broker + team access. Buyers and sellers, browse homes at <button onClick={() => window.open("https://irvingrealestategroup.com/listings", "_blank")} className="underline">our listings</button>.</p>
      </div>

      {/* Broker (Torey) - Google OAuth */}
      {admin ? (
        <div className="mb-4 rounded-[2rem] border border-[#C4A56B] bg-[#1E1E1E] p-6 text-white" data-testid="signin-broker-signed-in">
          <div className="flex items-center gap-4">
            {admin.picture && (
              <img src={admin.picture} alt={admin.name || admin.email} className="h-12 w-12 rounded-full border-2 border-[#C4A56B]" />
            )}
            <div className="flex-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#C4A56B]">Signed in as Broker</div>
              <div className="mt-0.5 font-bold">{admin.name || admin.email}</div>
            </div>
            <button
              data-testid="signin-go-dashboard-btn"
              onClick={() => setView?.("dashboard")}
              className="rounded-full bg-[#C4A56B] px-5 py-2 text-sm font-bold text-[#1E1E1E] hover:bg-[#f0cc5c]"
            >
              Open CRM →
            </button>
          </div>
        </div>
      ) : (
        <div className="mb-4 rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm" data-testid="signin-broker-block">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#C4A56B]">Broker</div>
          <div className="mt-1 text-lg font-bold text-[#1E1E1E]">Torey - CRM Dashboard</div>
          <p className="mt-1 text-sm text-slate-600">One tap with your allowlisted Google account. You will land directly in the Lead Command Center.</p>
          <button
            data-testid="signin-broker-google-btn"
            onClick={signInWithGoogle}
            className="mt-4 inline-flex w-full items-center justify-center gap-3 rounded-2xl border border-slate-300 bg-white px-6 py-3 font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
          >
            <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Sign in with Google
          </button>
        </div>
      )}

      {/* Agent portal - email + password (JWT) */}
      <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm" data-testid="signin-agent-block">
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#C4A56B]">Agent</div>
        <div className="mt-1 text-lg font-bold text-[#1E1E1E]">Team member portal</div>
        <p className="mt-1 text-sm text-slate-600">Access your assigned leads, pipeline, transactions, and Content Studio. Sign in with the email + password Torey set up for you.</p>
        <button
          data-testid="signin-agent-portal-btn"
          onClick={() => setView?.("agent-portal")}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#1E1E1E] px-6 py-3 font-semibold text-white hover:bg-black"
        >
          Open Agent Portal →
        </button>
        <p className="mt-3 text-center text-[11px] text-slate-500">Forgot your password? Ask Torey to reset it - torey@irvingrealestategroup.com.</p>
      </div>
    </main>
  );
}

function ProgressBar({ stage, stages }) {
  const total = stages?.length || 8;
  const pct = Math.min(100, Math.round(((stage - 1) / (total - 1)) * 100));
  return (
    <div className="rounded-2xl bg-slate-100 p-1">
      <div className="h-3 rounded-2xl bg-gradient-to-r from-[#C4A56B] to-[#f0cc5c]" style={{ width: `${pct}%` }} />
    </div>
  );
}

function StageDots({ stage, stages }) {
  if (!stages?.length) return null;
  return (
    <ol className="mt-4 grid grid-cols-4 gap-2 md:grid-cols-8">
      {stages.map((s) => {
        let toneClass;
        if (s.num < stage) {
          toneClass = "border-emerald-300 bg-emerald-50 text-emerald-700";
        } else if (s.num === stage) {
          toneClass = "border-[#C4A56B] bg-[#fff8e7] text-[#1E1E1E]";
        } else {
          toneClass = "border-slate-200 bg-white text-slate-400";
        }
        return (
          <li key={s.num} className={`rounded-xl border p-2 text-center text-[10px] font-bold uppercase tracking-wide ${toneClass}`}>
            <div className="text-base">{s.num < stage ? "✓" : s.num}</div>
            <div className="mt-1 truncate">{s.name}</div>
          </li>
        );
      })}
    </ol>
  );
}

function MyTransaction({ txn, toggleCheck, askJason, refresh }) {
  const stages = txn.stages || [];
  const stageItems = (txn.checklist || []).filter((i) => i.stage === txn.stage);
  const completed = (txn.checklist || []).filter((i) => i.done).length;
  return (
    <main className="mx-auto max-w-5xl px-4 py-8 md:px-8" data-testid="my-transaction-view">
      <div className="rounded-[2rem] bg-white p-6 shadow-sm md:p-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="text-sm font-semibold text-[#1E1E1E]">Your Transaction</div>
            <h1 className="mt-1 text-3xl font-bold">{txn.propertyAddress}</h1>
            <p className="mt-1 text-slate-600">Contract {txn.contractDate} · Target close {txn.targetCloseDate || "TBD"}</p>
          </div>
          <button data-testid="my-txn-ask-jason-btn" onClick={askJason} className="self-start rounded-2xl bg-[#1E1E1E] px-5 py-3 font-bold text-white">Ask Jason about this</button>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between text-sm font-semibold">
            <div>Stage {txn.stage} of {stages.length || 8} · {txn.stageName}</div>
            <div className="text-slate-500">{completed} task{completed === 1 ? "" : "s"} completed</div>
          </div>
          <div className="mt-2"><ProgressBar stage={txn.stage} stages={stages} /></div>
          <StageDots stage={txn.stage} stages={stages} />
        </div>

        <div className="mt-6 rounded-3xl bg-[#f6f2ea] p-5">
          <div className="text-sm font-semibold text-[#1E1E1E]">What this stage means</div>
          <div className="mt-2 leading-7 text-slate-700">{txn.stageMeta?.summary}</div>
        </div>

        <div className="mt-6 rounded-3xl border border-slate-200 p-5">
          <div className="font-bold">Your action items for this stage</div>
          <ul className="mt-3 space-y-2">
            {stageItems.map((item) => (
              <li key={item.key} className="flex items-start gap-3 rounded-xl bg-slate-50 p-3">
                <input
                  data-testid={`my-txn-check-${item.key}`}
                  type="checkbox"
                  checked={item.done}
                  onChange={(e) => toggleCheck(item.key, e.target.checked)}
                  className="mt-1 h-5 w-5 accent-[#C4A56B]"
                />
                <span className={`text-sm ${item.done ? "text-slate-400 line-through" : "text-slate-800"}`}>{item.text}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-6 grid gap-3 md:grid-cols-3">
          <Info label="Lender" value={txn.lenderName} />
          <Info label="Title Company" value={txn.titleCompany} />
          <Info label="Option Period Ends" value={txn.optionPeriodEndDate} />
        </div>

        <div className="mt-6 text-xs text-slate-500">Have a question? Tap <strong>Ask Jason about this</strong>. he sees your stage, dates, and lender on file.</div>
        <button onClick={refresh} className="mt-3 text-xs text-slate-500 underline">Refresh</button>
      </div>
    </main>
  );
}

function AgentTransaction({ txn, advance, toggleCheck, onDelete, back }) {
  const stages = txn.stages || [];
  const [conciergeLog, setConciergeLog] = useState([]);
  const isSellerTxn = String(txn.clientType || "").toLowerCase() === "seller";

  useEffect(() => {
    if (!txn?.id || !isSellerTxn) return;
    axios.get(`${API}/transactions/${txn.id}/concierge-emails`)
      .then((res) => setConciergeLog(res.data || []))
      .catch(() => {});
    // Re-fetch when the txn's stage changes (parent re-renders after advance)
  }, [txn?.id, txn?.stage, isSellerTxn]);

  return (
    <main className="mx-auto max-w-5xl px-4 py-8 md:px-8" data-testid="agent-transaction-view">
      <button data-testid="agent-txn-back-btn" onClick={back} className="mb-4 rounded-full bg-white px-4 py-2 text-sm font-semibold shadow-sm">← Back to dashboard</button>
      <div className="rounded-[2rem] bg-white p-6 shadow-sm md:p-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="text-sm font-semibold text-[#1E1E1E]">Active Transaction</div>
            <h1 className="mt-1 text-3xl font-bold">{txn.propertyAddress}</h1>
            <p className="mt-1 text-slate-600">Stage {txn.stage} of {stages.length || 8} · {txn.stageName}</p>
            {txn.atRisk && (
              <div data-testid="agent-txn-risk-banner" className="mt-3 rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-800">
                <strong>⚠ At risk:</strong> {txn.atRiskReason}
              </div>
            )}
          </div>
          {txn.stage < 8 && (
            <button data-testid="agent-txn-advance-btn" onClick={advance} className="self-start rounded-2xl bg-[#1E1E1E] px-5 py-3 font-bold text-white">
              Advance to next stage
            </button>
          )}
          {txn.stage === 8 && txn.approvalStatus === "pending_approval" && (
            <button
              data-testid="agent-txn-approve-btn"
              onClick={async () => {
                await axios.post(`${API}/transactions/${txn.id}/approve`);
                window.location.reload();
              }}
              className="self-start rounded-2xl bg-emerald-700 px-5 py-3 font-bold text-white"
            >
              Approve close (notify agent)
            </button>
          )}
          {txn.approvalStatus === "approved" && (
            <div className="self-start rounded-2xl border border-emerald-500 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700">
              ✓ Approved
            </div>
          )}
          {onDelete && (
            <button data-testid="agent-txn-delete-btn" onClick={onDelete} className="self-start rounded-2xl border border-red-300 bg-white px-4 py-3 text-sm font-bold text-red-700 hover:bg-red-50">
              Delete transaction
            </button>
          )}
        </div>

        <div className="mt-6"><ProgressBar stage={txn.stage} stages={stages} /><StageDots stage={txn.stage} stages={stages} /></div>

        <div className="mt-6 grid gap-3 md:grid-cols-4">
          <Info label="Contract" value={txn.contractDate} />
          <Info label="Target Close" value={txn.targetCloseDate} />
          <Info label="Lender" value={txn.lenderName} />
          <Info label="Title" value={txn.titleCompany} />
        </div>

        <div className="mt-6 rounded-3xl border border-slate-200 p-5">
          <div className="font-bold">Full checklist (across stages)</div>
          <ul className="mt-3 space-y-2">
            {(txn.checklist || []).map((item) => (
              <li key={item.key} className="flex items-start gap-3 rounded-xl bg-slate-50 p-3">
                <input data-testid={`agent-txn-check-${item.key}`} type="checkbox" checked={item.done} onChange={(e) => toggleCheck(item.key, e.target.checked)} className="mt-1 h-5 w-5 accent-[#C4A56B]" />
                <span className={`text-sm ${item.done ? "text-slate-400 line-through" : "text-slate-800"}`}>
                  <span className="mr-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold uppercase">S{item.stage}</span>
                  {item.text}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-6 text-xs text-slate-500">Notes: {txn.notes || "-"}</div>

        <div className="mt-6">
          <TransactionDocuments txnId={txn.id} theme="light" />
        </div>

        {isSellerTxn && (
          <div className="mt-6 rounded-3xl border border-[#C4A56B]/40 bg-[#1E1E1E] p-5 text-[#E0D2A8]" data-testid="concierge-delivery-log">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C4A56B]">Seller Concierge · Delivery log</div>
                <div className="mt-1 text-sm text-[#E0D2A8]/70">
                  {conciergeLog.length === 0
                    ? "No concierge emails delivered yet. Stage 1 fires on transaction create; subsequent stages fire on Advance."
                    : `${conciergeLog.length} email${conciergeLog.length === 1 ? "" : "s"} sent to ${txn.clientEmail || "the seller"} so far.`}
                </div>
              </div>
              <span className="rounded-full bg-[#C4A56B] px-2 py-0.5 text-[10px] font-bold text-[#1E1E1E]">{conciergeLog.length}/8</span>
            </div>
            {conciergeLog.length > 0 && (
              <ul className="mt-4 space-y-2 text-sm">
                {conciergeLog.map((f) => {
                  const status = String(f.status || "").toUpperCase();
                  const statusColor = status === "SENT" ? "text-green-300 border-green-500/40"
                    : status === "MOCKED" ? "text-amber-300 border-amber-500/40"
                    : "text-red-300 border-red-500/40";
                  return (
                    <li key={f.id} className="flex items-start gap-3 rounded-xl bg-[#0F0F0F] px-4 py-3" data-testid={`concierge-log-${f.id}`}>
                      <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${statusColor}`}>{status}</span>
                      <div className="flex-1">
                        <div className="text-[#E0D2A8]">{f.subject}</div>
                        <div className="mt-1 text-[11px] text-[#E0D2A8]/60">
                          {f.campaign} · {f.createdAt ? new Date(f.createdAt).toLocaleString() : ""}
                          {f.providerError ? ` · error: ${f.providerError}` : ""}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </main>
  );
}

function NavButton({ children, onClick, active, testId }) {
  return <button data-testid={testId} onClick={onClick} className={`rounded-full px-4 py-2 font-semibold ${active ? "bg-[#C4A56B] text-[#1E1E1E]" : "bg-white/10 text-white hover:bg-white/20"}`}>{children}</button>;
}

function NavDropdown({ label, testId, items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button
        data-testid={testId}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 rounded-full bg-white/10 px-4 py-2 font-semibold text-white hover:bg-white/20"
      >
        {label}
        <svg width="10" height="10" viewBox="0 0 20 20" className={`transition ${open ? "rotate-180" : ""}`} fill="currentColor"><path d="M5 7l5 6 5-6H5z"/></svg>
      </button>
      {open && (
        <div data-testid={`${testId}-menu`} className="absolute left-0 top-full z-40 mt-2 min-w-[220px] rounded-2xl border border-[#C4A56B]/30 bg-[#141414] p-2 shadow-2xl">
          {items.map((it) => (
            it.href ? (
              <a
                key={it.label}
                href={it.href}
                target={it.external ? "_blank" : undefined}
                rel={it.external ? "noopener noreferrer" : undefined}
                data-testid={it.testId}
                onClick={() => setOpen(false)}
                className="flex items-center justify-between rounded-xl px-3 py-2 text-sm text-[#E0D2A8] hover:bg-[#C4A56B]/15"
              >
                <span>{it.label}</span>
                {it.external && <span className="text-[#C4A56B]">↗</span>}
              </a>
            ) : (
              <button
                key={it.label}
                data-testid={it.testId}
                onClick={() => { setOpen(false); it.onClick(); }}
                className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm text-[#E0D2A8] hover:bg-[#C4A56B]/15"
              >
                <span>{it.label}</span>
                {it.badge && <span className="text-[10px] font-bold uppercase tracking-widest text-[#C4A56B]">{it.badge}</span>}
              </button>
            )
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Landing pages. minimal nav, single-CTA conversion-optimized ----

function LandingShell({ children }) {
  return (
    <main className="min-h-[calc(100vh-80px)] bg-[#f6f2ea]" data-testid="landing-shell">
      {children}
      <footer className="mx-auto max-w-5xl px-4 pb-12 pt-6 text-center text-xs text-slate-500">
        © Irving Real Estate Group · The Irving Experience. It Will Move You! · Licensed in Texas
      </footer>
    </main>
  );
}

function LandingHeroForm({ headline, sub, cta, fields, onSubmit, score, navigate }) {
  const [data, setData] = useState({});
  const [smsConsent, setSmsConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  function set(k, v) { setData((p) => ({ ...p, [k]: v })); }
  return (
    <div className="rounded-[2rem] bg-white p-6 shadow-xl md:p-8">
      <h1 className="text-3xl font-bold leading-tight md:text-4xl">{headline}</h1>
      <p className="mt-3 text-slate-600">{sub}</p>
      <div className="mt-6 grid gap-3">
        {fields.map((f) => (
          f.type === "select" ? (
            <select key={f.name} data-testid={`lp-field-${f.name}`} value={data[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} className="rounded-2xl border border-slate-300 bg-white px-4 py-3">
              <option value="">{f.placeholder}</option>
              {f.options.map((o) => <option key={o}>{o}</option>)}
            </select>
          ) : (
            <input key={f.name} data-testid={`lp-field-${f.name}`} value={data[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} placeholder={f.placeholder} type={f.inputType || "text"} className="rounded-2xl border border-slate-300 px-4 py-3" />
          )
        ))}
      </div>
      {score && <div className="mt-4 text-xs font-semibold text-slate-500">{score(data)}</div>}
      <SmsConsentCheckbox checked={smsConsent} onChange={setSmsConsent} navigate={navigate} />
      <button
        data-testid="lp-submit-btn"
        disabled={busy || fields.some((f) => f.required && !data[f.name])}
        onClick={async () => { setBusy(true); await onSubmit({ ...data, smsConsent, smsConsentText: smsConsent ? SMS_CONSENT_TEXT : "" }); setBusy(false); }}
        className="mt-5 w-full rounded-2xl bg-[#C4A56B] px-6 py-4 font-bold text-[#1E1E1E] shadow-lg hover:bg-[#f0cc5c] disabled:opacity-50"
      >
        {busy ? "Submitting…" : cta}
      </button>
      <div className="mt-3 text-center text-[11px] text-slate-500">Consent is not a condition of any purchase or service. Reply STOP to opt out at any time.</div>
    </div>
  );
}

function LandingHeader({ children }) {
  return (
    <section className="bg-[#1E1E1E] text-white">
      <div className="mx-auto grid max-w-6xl gap-8 px-4 py-12 md:px-8 lg:grid-cols-[1fr_1fr] lg:py-16">
        <div>
          <div className="mb-5 inline-block overflow-hidden rounded-2xl bg-black p-2 shadow-xl ring-1 ring-[#C4A56B]/30">
            <img src="/logo-new.png" alt="Irving Real Estate Group" className="h-20 w-20 object-contain md:h-24 md:w-24" />
          </div>
          {children}
        </div>
        <div className="rounded-[2rem] bg-white/10 p-1 shadow-2xl backdrop-blur" id="landing-form-anchor">
          {/* Form is injected by the parent below. kept here for layout shell */}
        </div>
      </div>
    </section>
  );
}

function LandingSell({ setView }) {
  const [submitted, setSubmitted] = useState(false);
  async function handleSubmit(data) {
    try {
      const utm = JSON.parse(localStorage.getItem("ireg-utm") || "null");
      await axios.post(`${API}/leads`, {
        type: "Seller",
        name: data.name || "",
        phone: data.phone || "",
        email: data.email || "",
        propertyAddress: data.address || "",
        timeline: data.timeline || "",
        consultationInterest: "Yes",
        source: utm ? utmSourceLabel(utm, "Seller") : "LP · Sell Fast DFW",
        utm,
        smsConsent: !!data.smsConsent,
        smsConsentText: data.smsConsentText || "",
        smsConsentSource: "lp_sell_fast",
      });
      setSubmitted(true);
    } catch (err) { devError(err); }
  }
  return (
    <LandingShell>
      <section className="bg-[#1E1E1E] text-white">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-12 md:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:py-16">
          <div>
            <div className="mb-5 inline-block overflow-hidden rounded-2xl bg-black p-2 shadow-xl ring-1 ring-[#C4A56B]/30">
              <img src="/logo-new.png" alt="Irving Real Estate Group" className="h-20 w-20 object-contain md:h-24 md:w-24" />
            </div>
            <h1 className="text-4xl font-bold leading-tight md:text-5xl">Sell your DFW home fast. without leaving money on the table.</h1>
            <p className="mt-4 text-lg text-slate-200">Most DFW homes we list close in <strong className="text-[#C4A56B]">30–45 days</strong>. Get a free home-value report + a no-pressure pricing strategy call.</p>
            <ul className="mt-6 space-y-2 text-slate-200">
              <li>✓ Free, no-obligation home value</li>
              <li>✓ Side-by-side comp analysis</li>
              <li>✓ Pricing strategy for your timeline</li>
              <li>✓ Real numbers, not Zestimate guesses</li>
            </ul>
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-slate-200">
              <strong className="text-[#C4A56B]">Why sellers pick Irving Real Estate Group:</strong> we combine local DFW expertise with Jason. our real estate concierge. so you get answers fast, day or night.
            </div>
          </div>
          <div>
            {submitted ? (
              <div data-testid="lp-sell-success" className="rounded-[2rem] bg-white p-8 text-center shadow-xl">
                <div className="text-5xl">🏡</div>
                <h2 className="mt-3 text-2xl font-bold">Got it. Torey will call you within 1 business hour.</h2>
                <p className="mt-2 text-slate-600">Want to keep exploring? Browse live DFW listings while you wait.</p>
                <button onClick={() => setView("listings")} className="mt-4 rounded-2xl bg-[#1E1E1E] px-5 py-3 font-bold text-white">See live DFW listings</button>
              </div>
            ) : (
              <LandingHeroForm
                headline="Get my free home value"
                sub="Takes 30 seconds. No obligation."
                cta="Get my free report"
                onSubmit={handleSubmit}
                fields={LP_VALUE_FIELDS}
                navigate={setView}
              />
            )}
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-5xl px-4 py-12 md:px-8">
        <div className="grid gap-6 md:grid-cols-3">
          <Feature title="Real value, not Zestimates" text="A licensed agent reviews comps within 5 miles, then walks you through pricing strategy live." />
          <Feature title="Net-proceeds clarity" text="See your expected net after commissions, payoff, and closing costs before you list." />
          <Feature title="Zero pressure" text="If now isn't the right time, we'll tell you. The Irving Experience is about clarity, not pressure." />
        </div>
      </section>
    </LandingShell>
  );
}

function LandingBuy({ setView, openListings }) {
  const [submitted, setSubmitted] = useState(false);
  async function handleSubmit(data) {
    try {
      const utm = JSON.parse(localStorage.getItem("ireg-utm") || "null");
      await axios.post(`${API}/leads`, {
        type: "Buyer",
        name: data.name || "",
        phone: data.phone || "",
        email: data.email || "",
        location: data.city || "",
        budget: data.budget || "",
        timeline: data.timeline || "",
        preapprovalStatus: data.preapproval || "",
        consultationInterest: "Yes",
        source: utm ? utmSourceLabel(utm, "Buyer") : "LP · Find Home DFW",
        utm,
        smsConsent: !!data.smsConsent,
        smsConsentText: data.smsConsentText || "",
        smsConsentSource: "lp_find_home",
      });
      setSubmitted(true);
    } catch (err) { devError(err); }
  }
  return (
    <LandingShell>
      <section className="bg-[#1E1E1E] text-white">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-12 md:px-8 lg:grid-cols-[1fr_1fr] lg:py-16">
          <div>
            <div className="mb-5 inline-block overflow-hidden rounded-2xl bg-black p-2 shadow-xl ring-1 ring-[#C4A56B]/30">
              <img src="/logo-new.png" alt="Irving Real Estate Group" className="h-20 w-20 object-contain md:h-24 md:w-24" />
            </div>
            <h1 className="text-4xl font-bold leading-tight md:text-5xl">Find your DFW home. with a concierge who never sleeps.</h1>
            <p className="mt-4 text-lg text-slate-200">Search <strong className="text-[#C4A56B]">live NTREIS listings</strong>, save favorites, and have <strong>Jason</strong> walk you through homes that fit your budget and timeline.</p>
            <ul className="mt-6 space-y-2 text-slate-200">
              <li>✓ Live MLS listings (not delayed Zillow data)</li>
              <li>✓ Personalized recommendations from Jason</li>
              <li>✓ Favorite homes + Torey follows up only when you say so</li>
              <li>✓ Free buyer consultation when you're ready</li>
            </ul>
            <button onClick={openListings} className="mt-6 rounded-2xl border border-[#C4A56B] bg-transparent px-5 py-3 font-bold text-[#C4A56B] hover:bg-[#C4A56B] hover:text-[#1E1E1E]">Browse live listings →</button>
          </div>
          <div>
            {submitted ? (
              <div data-testid="lp-buy-success" className="rounded-[2rem] bg-white p-8 text-center shadow-xl">
                <div className="text-5xl">🔑</div>
                <h2 className="mt-3 text-2xl font-bold">Welcome. Jason has your preferences.</h2>
                <p className="mt-2 text-slate-600">Jump into live DFW listings and start saving the homes you love.</p>
                <button onClick={openListings} className="mt-4 rounded-2xl bg-[#1E1E1E] px-5 py-3 font-bold text-white">See matching homes</button>
              </div>
            ) : (
              <LandingHeroForm
                headline="Get matched with DFW homes"
                sub="Takes 45 seconds. No spam. promise."
                cta="Get my matches"
                onSubmit={handleSubmit}
                fields={LP_BUY_FIELDS}
                navigate={setView}
              />
            )}
          </div>
        </div>
      </section>
    </LandingShell>
  );
}

function LandingValue({ setView }) {
  const [submitted, setSubmitted] = useState(false);
  async function handleSubmit(data) {
    try {
      const utm = JSON.parse(localStorage.getItem("ireg-utm") || "null");
      await axios.post(`${API}/leads`, {
        type: "Seller",
        name: data.name || "",
        phone: data.phone || "",
        email: data.email || "",
        propertyAddress: data.address || "",
        homeCondition: data.condition || "",
        reason: "Requested instant home value",
        consultationInterest: "Yes",
        source: utm ? utmSourceLabel(utm, "Seller") : "LP · Value My Home",
        utm,
        smsConsent: !!data.smsConsent,
        smsConsentText: data.smsConsentText || "",
        smsConsentSource: "lp_value_my_home",
      });
      setSubmitted(true);
    } catch (err) { devError(err); }
  }
  return (
    <LandingShell>
      <section className="bg-[#1E1E1E] text-white">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-12 md:px-8 lg:grid-cols-[1fr_1fr] lg:py-16">
          <div>
            <div className="mb-5 inline-block overflow-hidden rounded-2xl bg-black p-2 shadow-xl ring-1 ring-[#C4A56B]/30">
              <img src="/logo-new.png" alt="Irving Real Estate Group" className="h-20 w-20 object-contain md:h-24 md:w-24" />
            </div>
            <h1 className="text-4xl font-bold leading-tight md:text-5xl">What's your DFW home actually worth?</h1>
            <p className="mt-4 text-lg text-slate-200">Skip the Zestimate. Get a free, agent-prepared estimate with <strong className="text-[#C4A56B]">real comparable sales</strong> within a 5-mile radius.</p>
            <ul className="mt-6 space-y-2 text-slate-200">
              <li>✓ Free comp report in 24 hours</li>
              <li>✓ Pricing strategy call (optional)</li>
              <li>✓ Net-proceeds calculator included</li>
              <li>✓ Zero obligation to list</li>
            </ul>
          </div>
          <div>
            {submitted ? (
              <div data-testid="lp-value-success" className="rounded-[2rem] bg-white p-8 text-center shadow-xl">
                <div className="text-5xl">📊</div>
                <h2 className="mt-3 text-2xl font-bold">Your free value report is on the way.</h2>
                <p className="mt-2 text-slate-600">Torey will email comps + a written estimate within 24 hours. Want a quick call instead?</p>
                <button onClick={() => setView("assistant")} className="mt-4 rounded-2xl bg-[#1E1E1E] px-5 py-3 font-bold text-white">Ask Jason in the meantime</button>
              </div>
            ) : (
              <LandingHeroForm
                headline="Get my free home value"
                sub="Just the address + how to reach you."
                cta="Send my report"
                onSubmit={handleSubmit}
                fields={LP_HOME_VALUE_FIELDS}
                navigate={setView}
              />
            )}
          </div>
        </div>
      </section>
    </LandingShell>
  );
}

function HeroButton({ children, onClick, testId }) {
  return <button data-testid={testId} onClick={onClick} className="rounded-2xl border border-[#C4A56B]/50 bg-[#C4A56B] px-5 py-4 text-sm font-bold text-[#1E1E1E] shadow-lg hover:bg-[#f0cc5c]">{children}</button>;
}

function Stat({ label, value, testId }) {
  return <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testId}><div className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</div><div className="mt-2 text-3xl font-bold">{value}</div></div>;
}

function Feature({ title, text }) {
  return (
    <div className="rounded-[2rem] border border-[#C4A56B]/25 bg-[#141414] p-6 shadow-lg transition hover:border-[#C4A56B]/60">
      <div className="font-serif text-xl uppercase tracking-wide text-[#C4A56B]">{title}</div>
      <p className="mt-3 leading-7 text-[#E0D2A8]/80">{text}</p>
    </div>
  );
}

function Info({ label, value }) {
  return <div className="rounded-2xl bg-slate-50 p-4"><div className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</div><div className="mt-1 font-semibold">{value || "Not provided"}</div></div>;
}

function Pill({ children }) {
  return <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{children}</span>;
}
